import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import { apiFetchWithSession, loadStoredSession, refreshSession, saveStoredSession } from './services/api';
import { api, type TravessiaResumo, type AcessoApp } from './services/apiV2';
import { viewToUrl, parseUrlToView } from './routerHelpers';
import {
  AuthSession, AuthEnvelope,
  BookV2, BookStatusDesign, ProfileV2, ShelfV2,
  StreakData, HeatmapData, AnalyticsData,
  Achievement, InsightsResponse,
} from './types';

import { AuthFlow, AuthPayload, ResetPasswordScreen } from './v2/auth';
import { BaixarApp } from './v2/baixar-app';
import { TabBar } from './v2/common';
import { HomeScreen } from './v2/home';
import { NotifPopover, NotifItem } from './v2/notif-popover';
import { LibraryScreen } from './v2/library';
import { MentionNavProvider, MentionDataProvider } from './v2/mentions';
import { BookDetailScreen, LogReadingSheet } from './v2/book';
import { lerSessaoFoco, descartarSessaoFoco, segundosLidos, SessaoFoco } from './v2/focus-mode';
import { BookStudyScreen } from './v2/book-study';
import type { ContextoDaNota } from './v2/nota-composer';
import { EstudosGlobalScreen } from './v2/estudos-global';
import { CadernosScreen } from './v2/cadernos';
import { AddBookSheet } from './v2/add';
import { BookActionsSheet } from './v2/book-actions';
import { ClubBookActionsSheet } from './v2/club-book-actions';
import { unitMeta } from './v2/units';
import { StatsScreen } from './v2/stats';
import { ReportScreen } from './v2/report';
import { NotifScreen, ProfileScreen, DEFAULT_NOTIF_CONFIG } from './v2/profile';
import { TutoriaisScreen } from './v2/tutoriais';
import { ProfileSocialScreen } from './v2/profile-social';
import { HomeNova } from './v2/nova/home-nova';
import { ExplorarNova } from './v2/nova/explorar-nova';
import { PerfilNovo } from './v2/nova/perfil-novo';
import { ProVitrine } from './v2/nova/pro';
import { BloqueioPro, AvisoTeste } from './v2/nova/bloqueio-pro';
import { definirModelo } from './v2/nova/modelo';
import { PaginaAssinar } from './v2/nova/pagina-assinar';
import { CadernosNova } from './v2/nova/cadernos-nova';
import { MySubscriptionScreen } from './v2/subscription';
import { MyReadingsScreen } from './v2/readings';
import { LegalPage } from './v2/legal/legal-page';
import type { NotifConfig } from './v2/profile';
import { PaywallModal, PaywallScreen, UpgradeCelebration, LockedFeatureScreen } from './v2/paywall';
import { PlanosAposentados } from './v2/planos-aposentados';
import { SoMentorScreen } from './v2/clubs/so-mentor';
import { MentorPagina, AcaoAoEntrar } from './v2/circulo/mentor-pagina';
import { MentorEstudio } from './v2/circulo/estudio';
import { EditorDoMentor } from './v2/circulo/editor';
import { circuloAtivo, circuloAtivoAgora } from './v2/circulo/chave';
import { mentorLivroAtivo, mentorLivroAtivoAgora } from './v2/mentor-livro/chave';
import { EVENTO_TRILHA_VISTA } from './v2/mentor-livro/trilha';
import { AvisoTrilha } from './v2/mentor-livro/trilha';
import { TravessiaScreen } from './v2/mentor-livro/travessia';
import { LivroAbas, type FaceDoLivro } from './v2/nova/livro-abas';
import { lerOrigem } from './v2/origem';
import { PixCheckout } from './v2/pix-checkout';
import { shouldHidePaywall, isNativeApp, registerNativePush, setupDeepLinkListener, setupPushTapListener, openExternalUrl, setupBackButtonHandler, aoVoltarFechar, ligarVoltarDoNavegador, temFolhaAberta } from './v2/platform';
import { configureRevenueCat, loginRevenueCat, logoutRevenueCat } from './v2/revenuecat';
import { installGlobalErrorCapture, setTelemetryUser, tlog } from './v2/telemetry';
import { usoSessao, usoTela } from './v2/uso';
import { TutorialOverlay } from './v2/overlays';
import { OnboardingWizard } from './v2/onboarding-wizard';
import { EntradaCurta } from './v2/nova/entrada-curta';
import { habitoApi, EstadoHabito } from './v2/habito/api';
import { PreparoDoHabito } from './v2/habito/preparo';
import { SessaoSemDecisoes, CelebracaoDoDia } from './v2/habito/sessao';
import { TelaDeRetorno } from './v2/habito/retorno';
import { ProgramaScreen } from './v2/habito/programa';
import { EditarContrato } from './v2/habito/editar';
import { Formatura, MedicaoPeriodica } from './v2/habito/medida';
import { TrocarLivro } from './v2/habito/trocar';
import { ExploreTour } from './v2/slide-tour';
import { Toaster, toast } from './v2/toast';
import { AchievementModal, AchievementPayload } from './v2/overlays';
import { AdminConsoleScreen } from './v2/admin/console';

// Clubes/Grupos (novas telas — adicionadas em 2026-05-03)
import { ClubsHubScreen } from './v2/clubs/hub';
import { ClubsExploreScreen } from './v2/clubs/explore';
import { ExploreScreen } from './v2/clubs/explore-vitrine';
import { ClubInnerScreen } from './v2/clubs/inner';
import { ProposalPage } from './v2/clubs/proposal-page';
import { ClubAnalyticsScreen, ClubShareScreen } from './v2/clubs/other';
import { ClubCreateFlow, ClubEmptyScreen, ClubFormData } from './v2/clubs/create';
import { ProporClubeForm } from './v2/clubs/propor-form';
import { ClubQuestionBoxAdminScreen } from './v2/clubs/question-box';
import { EstudoScreen, InsightFinalDetailScreen } from './v2/clubs/estudo';
import { EstudoScreen2 } from './v2/clubs/estudo2/index2';
import { EstudoCaderno } from './v2/clubs/estudo-caderno';
import { StoriesProvider, StoriesTray, StoriesOverlays } from './v2/clubs/stories';
import { PokesSheet, PokesBell } from './v2/clubs/pokes-sheet';
import { SupportSheet } from './v2/support-sheet';
import { ReportSheet } from './v2/report-sheet';
import { Icon } from './v2/icons';
import { ActionSheet } from './v2/clubs/components';
import { InviteLandingScreen } from './v2/clubs/landing';
import { ProfileLandingScreen } from './v2/profile-landing';
import { ConviteLandingScreen } from './v2/convite-landing';
import { EditClubBookSheet } from './v2/clubs/edit-book';
import { EditClubSheet } from './v2/clubs/edit-club';
import { ComposeSheet } from './v2/clubs/compose';
import { MilestonesSheet } from './v2/clubs/milestones-sheet';
import { MilestoneInsightFinal } from './v2/clubs/data';
import type { Tab, View } from './routerHelpers';

const API_BASE = '/api';

const PLAN_LIMITS = {
  free: { maxLibrary: Infinity, maxReading: 2, hasStats: false, hasInsights: false },
  pro:  { maxLibrary: Infinity, maxReading: Infinity, hasStats: true, hasInsights: true },
};

// Tabs e Views vêm de routerHelpers (importados no topo).

function todayKeySP(): string {
  const d = new Date();
  const utc = d.getTime();
  const sp = new Date(utc + -180 * 60 * 1000);
  return sp.toISOString().slice(0, 10);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export type PushStatus = {
  supported: boolean;
  permission: 'default' | 'granted' | 'denied';
  subscribed: boolean;
  busy: boolean;
  error: string | null;
  isIOS: boolean;
  isStandalone: boolean;
};

// Círculo (P2): quem abre o link de um Mentor (/mentor/:slug) cai na página
// dele. As telas cheias da abertura — o preparo do hábito (conta nova), "O
// reencontro" e as outras do Protocolo — ficam para a próxima abertura normal
// do app: isto é lido uma vez, no carregamento, e não é guardado em lugar
// nenhum.
//
// P4: quem chega sem conta vê a página em modo vitrine; ao tocar em Assinar,
// Entrar no clube ou Criar caderno, o pedido fica em leitura.pendingMentor
// (como o convite de clube) e, com a conta pronta, a pessoa volta para a página
// com o passo aberto. Um pedido desses no carregamento também conta como
// "abriu pelo link" (a conta nova não cai no preparo do hábito antes de voltar).
// 19/10: só abrir a vitrine já guarda o pedido (acao 'pagina'), para o 1º login
// ou cadastro neste aparelho voltar ao Círculo de onde a pessoa veio. É lido e
// apagado no 1º carregamento com sessão: o login seguinte cai na Home normal.
// O app instalado tem o localStorage dele: o que a pessoa abriu no navegador
// não chega lá (o /baixar leva o Mentor à Play no `referrer`, para um build
// nativo que o leia).
const MENTOR_PENDENTE_KEY = 'leitura.pendingMentor';
const MENTOR_PENDENTE_TTL = 24 * 60 * 60 * 1000;
// nome: o do Mentor, para o cadastro falar do Círculo dele (QA #9).
type MentorPendente = { slug: string; acao: AcaoAoEntrar; t: number; nome?: string };
function lerMentorPendente(): MentorPendente | null {
  try {
    const p = JSON.parse(localStorage.getItem(MENTOR_PENDENTE_KEY) || 'null');
    if (!p || typeof p.slug !== 'string' || !p.slug || Date.now() - Number(p.t || 0) > MENTOR_PENDENTE_TTL) return null;
    return p;
  } catch { return null; }
}
const ABRIU_PELO_LINK_DO_MENTOR = typeof window !== 'undefined'
  && (/^\/mentor\//.test(window.location.pathname) || !!lerMentorPendente());

function App() {
  // ───── Router (URL é a fonte da verdade pra navigation) ─────
  const navigate = useNavigate();
  const location = useLocation();

  // ───── Rota standalone /planos (escapa do app inteiro pra fugir das taxas
  // do IAP da Apple — pagamento sempre via navegador externo) ─────
  // Detectamos antes do auth pra que qualquer um possa ver os preços, mesmo
  // sem conta. Quando clicar em "Ativar" o checkout exige login, aí redireciona.
  const isPlansRoute = typeof window !== 'undefined' && window.location.pathname === '/planos';

  // ───── Subdomínio admin.forjamente.com.br ─────
  // Quando user acessa via subdomínio admin, vai direto pro console admin
  // (escapando do app-shell normal). Continua exigindo login + role=admin.
  const isAdminSubdomain = typeof window !== 'undefined' && window.location.hostname.startsWith('admin.');

  // ───── Páginas legais públicas (sem auth) ─────
  // Apple e Google Play exigem URLs públicas pra Termos e Política de
  // Privacidade. Renderizamos antes de qualquer auth pra que sejam
  // acessíveis também pra quem não tem conta.
  const legalPathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isTermsRoute = legalPathname === '/termos' || legalPathname === '/terms';
  const isPrivacyRoute = legalPathname === '/privacidade' || legalPathname === '/privacy';

  // ───── Auth ─────
  // Lazy init: detecta tokens Supabase no #hash síncrono ANTES do primeiro
  // render. Crítico pra rotas standalone (/planos) que retornam direto sem
  // esperar useEffect — sem isso, magic link auto-login mostrava tela de
  // login no primeiro render e só atualizava depois.
  const [session, setSession] = useState<AuthSession | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      if (window.location.hash.length > 1) {
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const expiresIn = Number(hashParams.get('expires_in') || '3600');
        if (accessToken && refreshToken) {
          const sess: AuthSession = {
            accessToken, refreshToken,
            expiresAt: Math.floor(Date.now() / 1000) + (expiresIn || 3600),
          };
          saveStoredSession(sess, true);
          // Limpa hash, mantem path+search
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
          return sess;
        }
      }
    } catch {}
    return null;
  });
  // Rastro de uso (marco 2): a sessão e cada troca de tela — ver v2/uso.ts.
  useEffect(() => { usoSessao(session); }, [session]);
  useEffect(() => { usoTela(location.pathname, location.search); }, [location.pathname, location.search]);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  // Quando signup/login retorna needsVerification, guardamos o email pra mostrar
  // a tela de "Confirme seu email" com botão de reenviar.
  // A senha fica aqui só até o código ser confirmado: a pessoa acabou de
  // digitá-la no cadastro, e pedir de novo dois minutos depois é atrito puro.
  const [pendingVerify, setPendingVerify] = useState<{ email: string; password?: string } | null>(null);
  // CS-G: lembra o email do cadastro pra pré-preencher o login após "Já confirmei".
  const [verifyEmail, setVerifyEmail] = useState('');
  // UX: "Usar outro email" deve cair no CADASTRO (não no login); este estado controla isso.
  // ?cadastro=1 — o "usar no navegador" da landing e do /baixar abre direto o
  // cadastro web (os utm_ da mesma URL são guardados por v2/origem.ts).
  const [authInitialView, setAuthInitialView] = useState<'login' | 'register'>(() => {
    try { return new URLSearchParams(window.location.search).get('cadastro') === '1' ? 'register' : 'login'; } catch { return 'login'; }
  });
  // Círculo (P4): a vitrine do Mentor pediu o cadastro/login (mostra o AuthFlow
  // sem sair de /mentor/:slug) e, depois, o passo a abrir na volta.
  const [mentorPediuConta, setMentorPediuConta] = useState(false);
  // O APP SÓ PARA ASSINANTES (Wesley, 02/11/2026, noite): sem sessão, a 1ª vez
  // neste aparelho abre a PÁGINA DE ASSINAR (com o Login no topo) antes do
  // cadastro. Quem já entrou aqui alguma vez vai direto ao Login. A oferta vem
  // de /api/oferta (modelo 'assinatura' = TESTE_DIAS=0); `undefined` = carregando.
  const [oferta, setOferta] = useState<Awaited<ReturnType<typeof api.oferta>> | null | undefined>(undefined);
  const [mostrarAuth, setMostrarAuth] = useState(false);
  // Tocou "Assinar" na página: depois do cadastro, a compra da loja abre sozinha.
  const assinarAgoraRef = useRef(false);
  const jaEntrouNoAparelho = (() => { try { return localStorage.getItem('forjamente.jaEntrou') === '1'; } catch { return false; } })();
  const [mentorAoEntrar, setMentorAoEntrar] = useState<AcaoAoEntrar | null>(null);
  const limparMentorAoEntrar = useCallback(() => setMentorAoEntrar(null), []);
  // A chave do Círculo (v2/circulo/chave.ts): null enquanto o servidor não
  // responde. Só `true` mostra as entradas e as telas do Círculo.
  const [circuloLigado, setCirculoLigado] = useState<boolean | null>(circuloAtivoAgora);
  useEffect(() => { circuloAtivo().then(setCirculoLigado); }, []);
  const [resetToken, setResetToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('resetToken');
  });

  // ── INDICAÇÃO: captura o código de /r/:codigo ou ?ref= ────────────────
  // Roda ANTES de qualquer render e persiste em localStorage, porque entre
  // clicar no link e a conta existir passa a confirmação de email — que pode
  // acontecer em outra aba, outro navegador ou outro dia. Mesmo motivo do
  // pendingInviteToken dos clubes.
  useEffect(() => {
    try {
      const qp = new URLSearchParams(window.location.search);
      const doPath = window.location.pathname.match(/^\/r\/([A-Za-z0-9]{4,16})\/?$/);
      const bruto = (doPath && doPath[1]) || qp.get('ref') || '';
      const code = String(bruto).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (code) {
        localStorage.setItem('forjamente.pendingReferralCode', code);
        // Registra a ABERTURA do link. E o unico jeito de saber quantos
        // clicaram sem se cadastrar — o vinculo so nasce junto com a conta.
        // Dispara e esquece: telemetria jamais pode atrapalhar quem esta
        // entrando, entao erro aqui e engolido de proposito.
        if (doPath) {
          fetch('/api/public/referrals/' + encodeURIComponent(code) + '/abertura',
                { method: 'POST' }).catch(() => {});
        }
      }
    } catch {}
  }, []);

  // Assim que existe sessão, amarra a indicação. O backend é idempotente e
  // recusa auto-indicação, conta já vinculada e fundador — então pode chamar
  // sem medo; o pior caso é um 200 dizendo que não fez nada.
  useEffect(() => {
    if (!session) return;
    let code = null;
    try { code = localStorage.getItem('forjamente.pendingReferralCode'); } catch {}
    if (!code) return;
    (async () => {
      try {
        await api.attachReferral(session, code);
        try { localStorage.removeItem('forjamente.pendingReferralCode'); } catch {}
      } catch (e) {
        // Não limpa o código: pode ser falha de rede e a próxima sessão tenta.
        console.warn('[referral] attach falhou', e);
      }
    })();
  }, [session]);
  // Detecção de convite via path /c/:token (deep link de convite).
  // Na primeira renderização, parse o pathname; mantém em state pra controlar landing.
  const [inviteToken, setInviteToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    // Convite via query (?invite= ou ?c=) — chega no redirect de verificação de
    // email. Persiste em localStorage pra o auto-accept honrar mesmo após sair
    // do app e voltar (sobrevive a fluxo de email em outra aba/navegador no caso comum).
    try {
      const qp = new URLSearchParams(window.location.search);
      const qInvite = qp.get('invite') || qp.get('c');
      if (qInvite && /^[A-Za-z0-9_-]{8,}$/.test(qInvite)) {
        localStorage.setItem('leitura.pendingInviteToken', qInvite);
      }
    } catch {}
    const m = window.location.pathname.match(/^\/c\/([A-Za-z0-9_-]{8,})\/?$/);
    return m ? m[1] : null;
  });

  // Detecção do convite via path /r/:codigo. O código também é guardado em
  // localStorage pelo useEffect de indicação — aqui o estado serve só para
  // decidir se a landing aparece, e é limpo assim que a pessoa segue.
  const [conviteCode, setConviteCode] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const m = window.location.pathname.match(/^\/r\/([A-Za-z0-9]{4,16})\/?$/);
    return m ? m[1].toUpperCase() : null;
  });

  // Detecção de perfil via path /@username (deep link de perfil compartilhado).
  const [profileHandle, setProfileHandle] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const m = window.location.pathname.match(/^\/@([A-Za-z0-9_.]{2,40})\/?$/);
    return m ? m[1] : null;
  });

  // ───── Profile + Data ─────
  const [profile, setProfile] = useState<ProfileV2 | null>(null);
  const [books, setBooks] = useState<BookV2[]>([]);
  const [shelves, setShelves] = useState<ShelfV2[]>([]); // FP3 — estantes
  const [mentionPeople, setMentionPeople] = useState<any[]>([]); // FP4 — mútuos p/ autocomplete de @menção
  const [streak, setStreak] = useState<StreakData | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  // A primeira carga dos dados desta sessão ainda não terminou (UX-25).
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  // Nome digitado no cadastro, até o perfil chegar (UX-35).
  const [nomeDoCadastro, setNomeDoCadastro] = useState<string | null>(null);

  // ───── Navigation + UI ─────
  // tab/view são "espelhos" da URL atual. Wrappers setTab/setView abaixo navegam
  // (atualizam URL); o useEffect que escuta location.pathname garante sincronização
  // ao usuário usar back/forward do navegador, F5, ou colar uma URL.
  const [tab, setTabState] = useState<Tab>(() => {
    const stored = localStorage.getItem('leitura.tab');
    if (stored === 'home' || stored === 'clubs' || stored === 'stats' || stored === 'me') return stored;
    return 'home';
  });
  const [view, setViewState] = useState<View>({ kind: 'tab' });
  const [studyCompose, setStudyCompose] = useState<number | null>(null); // FP4: página pra auto-abrir o composer do caderno (one-shot)
  const [studyContexto, setStudyContexto] = useState<ContextoDaNota | null>(null); // a pergunta do insight que vai junto com essa página
  // Mentor do Livro: o registro liberou itens da trilha → aviso discreto que
  // leva ao caderno do livro, já na Trilha do Mentor (studyTrilha, one-shot).
  const [avisoTrilha, setAvisoTrilha] = useState<{ bookId: string; n: number } | null>(null);
  const [studyTrilha, setStudyTrilha] = useState(false);
  // Notas do Mentor liberadas e ainda não abertas, por livro: o número vermelho
  // no ícone do caderno (Home e Página do Livro). O servidor é a fonte; some
  // quando a trilha daquele livro é aberta (EVENTO_TRILHA_VISTA).
  const [mentorNovas, setMentorNovas] = useState<Record<string, number>>({});
  // A Travessia (o mapa da trilha): os livros desta pessoa que têm. Vazio para
  // quem não tem acesso — aí tudo segue pelo caderno, como antes.
  const [travessias, setTravessias] = useState<Record<string, TravessiaResumo>>({});
  // A INTERFACE NOVA (Home, Explorar e Perfil, 30/10/2026): liberada por conta
  // (forjamente_interface_nova). Sem a linha, tudo segue como sempre foi.
  const [interfaceNova, setInterfaceNova] = useState(false);
  const [perfilSocialAberto, setPerfilSocialAberto] = useState(false);
  // O PRO (refino de 30/10/2026): na interface nova o app é grátis e o que se
  // assina são as trilhas. acessoTrilhas = esta conta abre as travessias (pela
  // assinatura ou por acesso dado à mão); souPro = pela assinatura.
  const [acessoTrilhas, setAcessoTrilhas] = useState(false);
  const [souPro, setSouPro] = useState(false);
  // 'trilha': o pagamento aberto direto do convite da 1ª etapa grátis (na
  // Travessia) — fechar volta ao caminho, não à vitrine.
  const [proAberto, setProAberto] = useState<null | 'vitrine' | 'checkout' | 'trilha'>(null);
  // O APP PAGO COM 7 DIAS GRÁTIS (01/11/2026, server/acesso-app.js): o estado
  // desta conta ('teste' | 'bloqueado' | 'pro' | 'isento'). 'bloqueado' na
  // interface nova = o app inteiro dá lugar ao BloqueioPro.
  const [acessoApp, setAcessoApp] = useState<AcessoApp | null>(null);
  // A resposta de /api/interface também decide a PRIMEIRA abertura (a entrada
  // curta no lugar do questionário e do tutorial antigo): o loadAllData espera
  // esta mesma promessa em vez de pedir de novo.
  const interfaceNovaProm = useRef<Promise<any> | null>(null);
  const interfaceNovaRef = useRef(false);
  interfaceNovaRef.current = interfaceNova;
  // A página de assinar: a oferta é lida uma vez (sem sessão é o que decide a
  // 1ª tela) e a sessão marca o aparelho — da próxima vez, direto ao Login.
  useEffect(() => {
    let vivo = true;
    api.oferta().then((o) => { if (o?.modelo === 'assinatura') definirModelo(true, 0); if (vivo) setOferta(o || null); }).catch(() => { if (vivo) setOferta(null); });
    return () => { vivo = false; };
  }, []);
  useEffect(() => {
    if (session) { try { localStorage.setItem('forjamente.jaEntrou', '1'); } catch {} setMostrarAuth(false); }
  }, [session]);
  // A ENTRADA CURTA da conta nova da interface nova (31/10/2026): a abertura
  // "Bem-vindo" e a tela "Comece pela trilha do Munger", uma vez só.
  const [entradaAberta, setEntradaAberta] = useState(false);
  useEffect(() => {
    if (!session) { setInterfaceNova(false); setAcessoTrilhas(false); setSouPro(false); setAcessoApp(null); interfaceNovaProm.current = null; return; }
    let vivo = true;
    const prom = api.interfaceNova(session);
    interfaceNovaProm.current = prom.catch(() => null);
    prom.then((r) => {
      if (!vivo) return;
      definirModelo(!!r?.paywall, r?.acesso?.teste_dias);
      setInterfaceNova(!!r?.nova); setAcessoTrilhas(!!r?.trilhas); setSouPro(!!r?.pro);
      setAcessoApp(r?.nova && r?.paywall ? (r.acesso || null) : null);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [session, profile?.plan, (profile as any)?.plan_status, (profile as any)?.plan_expires_at]);
  // Lê o acesso de novo: depois de assinar/restaurar, ou quando uma API deu
  // 402 { paywall: true } (o teste venceu com o app aberto — api.ts avisa).
  // Saiu do bloqueio? Recarrega os dados que o 402 deixou de fora.
  const loadAllDataRef = useRef<(() => any) | null>(null);
  const conferirAcesso = useCallback(async () => {
    if (!session) return;
    const r = await api.interfaceNova(session).catch(() => null);
    if (!r) return;
    definirModelo(!!r.paywall, r.acesso?.teste_dias);
    setInterfaceNova(!!r.nova); setAcessoTrilhas(!!r.trilhas); setSouPro(!!r.pro);
    const novo = r.nova && r.paywall ? (r.acesso || null) : null;
    setAcessoApp((antes) => {
      if (antes?.estado === 'bloqueado' && novo?.estado !== 'bloqueado') setTimeout(() => loadAllDataRef.current?.(), 0);
      return novo;
    });
  }, [session]);
  useEffect(() => {
    const aoBloquear = () => { if (interfaceNovaRef.current) conferirAcesso(); };
    window.addEventListener('forjamente:paywall', aoBloquear);
    return () => window.removeEventListener('forjamente:paywall', aoBloquear);
  }, [conferirAcesso]);
  // Marca no body para o CSS da interface nova (nova.css) valer só para quem a tem.
  useEffect(() => {
    document.body.classList.toggle('nv-interface', interfaceNova);
    return () => document.body.classList.remove('nv-interface');
  }, [interfaceNova]);
  // O perfil social (o antigo) abre por cima do Perfil novo; sair da aba fecha.
  useEffect(() => { if (tab !== 'me') setPerfilSocialAberto(false); }, [tab]);
  const atualizarMentorNovas = useCallback(() => {
    if (!session) { setMentorNovas({}); setTravessias({}); return; }
    mentorLivroAtivo().then((ligado) => {
      if (!ligado) return;
      api.trilhaNovas(session).then((r) => setMentorNovas(r?.porLivro || {})).catch(() => {});
      api.travessiaLivros(session).then((r) => setTravessias(r?.livros || {})).catch(() => {});
    });
  }, [session]);
  // Ao entrar, ao voltar ao app e quando uma trilha é aberta.
  useEffect(() => {
    atualizarMentorNovas();
    const aoVoltar = () => { if (document.visibilityState === 'visible') atualizarMentorNovas(); };
    window.addEventListener(EVENTO_TRILHA_VISTA, atualizarMentorNovas);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      window.removeEventListener(EVENTO_TRILHA_VISTA, atualizarMentorNovas);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [atualizarMentorNovas]);
  // Chegou ao caderno daquele livro por qualquer caminho: o aviso já cumpriu.
  useEffect(() => {
    if (avisoTrilha && (view.kind === 'book-study' || view.kind === 'travessia') && (view as any).book?.id === avisoTrilha.bookId) setAvisoTrilha(null);
    if (studyTrilha && view.kind !== 'book-study') setStudyTrilha(false);
  }, [view, avisoTrilha, studyTrilha]);
  // Stack de navegacao interna pra que onBack volte pra de onde veio (e nao
  // pra um destino fixo). Mantida em ref pra evitar re-renders desnecessarios.
  //
  // Cada entrada guarda tambem `idx`: a posicao, no historico do NAVEGADOR, da
  // tela que ela representa (o react-router grava esse numero em
  // history.state.idx). E isso que mantem o "<" da tela e o voltar do
  // navegador/Android como o MESMO gesto (UX-08):
  //   · o "<" volta no historico ate aquela posicao, em vez de empilhar a URL
  //     de novo — empilhar fazia o voltar do navegador levar de volta a tela de
  //     onde a pessoa tinha acabado de sair (livro -> caderno -> "<" -> voltar
  //     = caderno de novo);
  //   · o voltar do navegador apara a pilha (efeito logo abaixo) — sem isso o
  //     proximo "<" voltava para a tela em que a pessoa ja estava, e o toque
  //     parecia nao fazer nada.
  const viewHistoryRef = useRef<{ view: View; tab: Tab; idx: number | null }[]>([]);
  const idxNoHistorico = (): number | null => {
    try {
      const st: any = window.history.state;
      return st && typeof st.idx === 'number' ? st.idx : null;
    } catch { return null; }
  };

  // Wrapper de setView: navega pra URL canônica e o useEffect atualiza state.
  // `trocar: true` SUBSTITUI a tela atual em vez de empilhar: o assistente de
  // criar clube vira a tela do clube, e o voltar dela vai para Meus clubes —
  // não de volta ao assistente, que convidava a criar o clube de novo
  // (pente fino, QA-04).
  const setView = useCallback((next: View, opts?: { trocar?: boolean }) => {
    // Lida ANTES de navegar: e a posicao da tela que esta sendo deixada.
    const idx = idxNoHistorico();
    if (opts?.trocar) {
      setViewState(next);
      const url = viewToUrl(next as any, tab);
      if (location.pathname !== url) navigate(url, { replace: true });
      return;
    }
    setViewState(prev => {
      // Empilha apenas se a view atual e diferente da proxima (evita lixo).
      const prevKind = (prev as any).kind;
      const nextKind = (next as any).kind;
      const sameKindSameIds = prevKind === nextKind
        && (prev as any).clubId === (next as any).clubId
        && ((prev as any).bookId === (next as any).bookId)
        && ((prev as any).book?.id === (next as any).book?.id);
      if (!sameKindSameIds) {
        // Guarda a view E o tab atual — pra o goBack restaurar a aba de origem.
        viewHistoryRef.current.push({ view: prev, tab, idx });
        // Evita historico crescer sem fim — limita ultimas 30 entradas.
        if (viewHistoryRef.current.length > 30) viewHistoryRef.current.shift();
      }
      return next;
    });
    const targetUrl = viewToUrl(next as any, tab);
    if (location.pathname !== targetUrl) navigate(targetUrl);
  }, [navigate, location.pathname, tab]);

  // Volta pra view anterior na stack interna. Se a stack estiver vazia,
  // cai pra raiz da aba (e, ja na raiz de outra aba, pra Hoje).
  const goBack = useCallback(() => {
    const prev = viewHistoryRef.current.pop();
    // Restaura view E tab de onde o usuário veio (tab capturado no push). Sem
    // restaurar o tab, voltar de um perfil (que troca o tab pra 'me') cairia na
    // aba errada em vez da de origem (ex: Hoje).
    const naRaiz = !prev && view.kind === 'tab';
    const target: View = prev ? prev.view : ({ kind: 'tab' } as any);
    const targetTab: Tab = prev ? prev.tab : (naRaiz ? 'home' : tab);
    setViewState(target);
    setTabState(targetTab);
    const targetUrl = viewToUrl(target as any, targetTab);
    const atual = idxNoHistorico();
    if (prev && prev.idx !== null && atual !== null && prev.idx < atual) {
      // A tela anterior esta no historico: volta ate ela. O efeito que escuta
      // a URL encontra o estado ja certo e nao mexe em nada.
      navigate(prev.idx - atual);
    } else if (location.pathname !== targetUrl) {
      // Nada atras no historico (chegou por link, F5): troca a entrada atual
      // em vez de empilhar — empilhar criava um vai-e-volta sem fim.
      navigate(targetUrl, { replace: true });
    }
  }, [navigate, location.pathname, tab, view.kind]);

  // O voltar/avancar do navegador (e o gesto do iPhone) mexe no historico sem
  // passar pelo goBack: apara da pilha interna o que ficou "a frente" da
  // posicao atual, para o proximo "<" voltar para onde o navegador voltaria.
  useEffect(() => {
    const atual = idxNoHistorico();
    if (atual === null) return;
    viewHistoryRef.current = viewHistoryRef.current.filter((e) => e.idx === null || e.idx < atual);
  }, [location.key]);

  // Wrapper de setTab: idem
  const setTab = useCallback((next: Tab) => {
    setTabState(next);
    const targetUrl = viewToUrl({ kind: 'tab' }, next);
    if (location.pathname !== targetUrl) navigate(targetUrl);
  }, [navigate, location.pathname]);
  // QUEM ACABOU DE CHEGAR começa pelas trilhas (refino de 30/10/2026): na
  // interface nova, estante vazia e a Home aberta → o Explorar, uma vez só por
  // sessão. Quem já tem livro, ou abriu outro endereço, fica onde está.
  const novoFoiAoExplorar = useRef(false);
  useEffect(() => {
    if (novoFoiAoExplorar.current || !interfaceNova || primeiraCarga) return;
    novoFoiAoExplorar.current = true;
    if (books.length === 0 && tab === 'home' && view.kind === 'tab') setTab('explore');
  }, [interfaceNova, primeiraCarga, books.length, tab, view.kind, setTab]);
  // Termo configurável (clube vs grupo) — default a partir de profile.palette/preferência futura.
  const [clubsTerm, setClubsTerm] = useState<'clube' | 'grupo'>(
    () => (localStorage.getItem('leitura.clubsTerm') as 'clube' | 'grupo') || 'clube'
  );
  // Modais de clube
  const [clubMenuOpen, setClubMenuOpen] = useState(false);
  const [editClubOpen, setEditClubOpen] = useState(false); // tela editar dados do grupo
  const [clubShareOpen, setClubShareOpen] = useState(false);
  const [clubShareTargetId, setClubShareTargetId] = useState<string | null>(null);
  // Modal de "adicionar livro do clube" (admin) — reusa o AddBookSheet do app.
  const [addClubBookOpen, setAddClubBookOpen] = useState(false);
  const [addClubBookFor, setAddClubBookFor] = useState<string | null>(null);
  // Modal de "editar livro do clube" (admin) — sheet customizada
  const [editClubBookOpen, setEditClubBookOpen] = useState(false);
  // Quando admin tap num card do coverflow de series, guarda o book pra abrir
  // o EditClubBookSheet com aquele livro especifico (nao so o atual).
  const [editClubBookTarget, setEditClubBookTarget] = useState<any | null>(null);
  // Sheet de compor post (Estudo do Livro)
  const [composeOpen, setComposeOpen] = useState(false);
  // Quando user clica "Compartilhar como você leu" num question post, guarda
  // o contexto da pergunta pra mostrar no ComposeSheet (modo reply) e injetar
  // parentPostId no payload de createPost.
  const [composeReplyTo, setComposeReplyTo] = useState<{ postId: string; questionText: string; authorName: string } | null>(null);

  // Pokes inbox (cutucadas recebidas pelo viewer em qualquer clube)
  const [pokesOpen, setPokesOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  // Modal de denuncia UGC — recebe target { kind: 'club'|'post', id, label }
  const [reportTarget, setReportTarget] = useState<{ kind: 'club' | 'post'; id: string; label: string } | null>(null);
  const [pokesUnread, setPokesUnread] = useState(0);

  // Quando user clica "Ver feed deste marco" no Insight Final detail, marca aqui
  // o id do marco a expandir. EstudoScreen consome via prop initialExpandedMilestoneId.
  const [expandMilestoneOnEstudo, setExpandMilestoneOnEstudo] = useState<string | null>(null);

  // Fechar sheets/menus ao mudar de view (proteção contra estado grudado quando
  // user navega via URL/back-button — sem o useEffect, AddBookSheet aberto numa
  // tela permanecia aberto na próxima e renderizava in-line no scroll).
  useEffect(() => {
    // Fecha qualquer sheet/menu que possa ter ficado aberto na view anterior
    setAddOpen(false);
    setAddClubBookOpen(false);
    setLogOpen(false);
    setComposeOpen(false);
    setComposeReplyTo(null);
    setClubMenuOpen(false);
    setClubShareOpen(false);
    setEditClubBookOpen(false);
    setMilestonesSheetOpen(false);
    setPokesOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.kind, (view as any).clubId]);

  // Total de perguntas anônimas aguardando resposta no clube ativo (admin only).
  // Usado pra mostrar badge no item "Caixa de perguntas" do ActionSheet do clube.
  // Recarrega quando entra/sai de view.clubId ou abre o menu.
  const [pendingQboxCount, setPendingQboxCount] = useState(0);

  // Carrega contagem de pokes não-lidos quando logado / volta pra app
  useEffect(() => {
    if (!session) { setPokesUnread(0); return; }
    let cancelled = false;
    const carregar = async () => {
      try {
        const r = await api.listPokesInbox(session, { onlyUnread: true });
        if (!cancelled) setPokesUnread((r.pokes || []).length);
      } catch (e) {
        // silencioso (badge só some — não quebra a app)
      }
    };
    carregar();
    // Só no boot, a cutucada recebida com o app aberto nunca aparecia no 👋
    // até fechar e abrir de novo. Mesmo ritmo do sino (60 s) + ao voltar ao app.
    const id = setInterval(() => { if (document.visibilityState === 'visible') carregar(); }, 60000);
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [session]);

  // Carrega contagem de perguntas anônimas aguardando resposta no clube atual.
  // Roda quando view tem clubId (club-inner/estudo/club-question-box). Soma
  // openQuestions de todas as Caixas de Perguntas do clube. Mostra badge no
  // ActionSheet pra admin saber que tem coisa esperando dele.
  useEffect(() => {
    if (!session) { setPendingQboxCount(0); return; }
    const clubIdNow = (view as any).clubId;
    if (!clubIdNow) { setPendingQboxCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.listQuestionBoxes(session, clubIdNow);
        if (cancelled) return;
        const total = (r.qboxes || []).reduce((acc: number, qb: any) => acc + (qb.openQuestions || 0), 0);
        setPendingQboxCount(total);
      } catch (e) {
        // silencioso (badge só some — não quebra a app)
      }
    })();
    return () => { cancelled = true; };
  }, [session, (view as any).clubId, view.kind]);
  // Sheet de marcos (admin)
  const [milestonesSheetOpen, setMilestonesSheetOpen] = useState(false);

  // ───── Dados de Clubes/Grupos (vindos do backend) ─────
  const [clubs, setClubs] = useState<any[]>([]);
  const [myProposals, setMyProposals] = useState<any[]>([]);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [exploreClubs, setExploreClubs] = useState<any[]>([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  // ───── Sync URL → state quando usuário navega externamente (back/F5/colar URL) ─────
  // Roda toda vez que location.pathname muda. Compara com state atual e só atualiza
  // se for diferente, evitando loop com setView (que faz navigate e dispara este).
  useEffect(() => {
    // Não sincroniza nas rotas especiais de auth (/c/:token e ?resetToken=) —
    // essas têm tratamento próprio acima do roteador interno.
    const isInvitePath = /^\/c\/[A-Za-z0-9_-]{8,}\/?$/.test(location.pathname);
    const isProfilePath = /^\/@[A-Za-z0-9_.]{2,40}\/?$/.test(location.pathname);
    if (isInvitePath || isProfilePath) return;

    const parsed = parseUrlToView(location.pathname, {
      findBookById: (id) => books.find(b => b.id === id),
      search: location.search,
    });
    // Endereço que não existe: mostrava a Home com a URL errada na barra
    // (pente fino, QA-15). Logado, troca a URL pela do início e avisa.
    if ((parsed as any).desconhecida && session && location.pathname !== '/planos'
        && !/^\/(r|c|u)\//.test(location.pathname) && !/^\/@/.test(location.pathname)
        // Termos e Privacidade são renderizados pelo App (antes do roteador):
        // logado, também têm de abrir (link de Ajustes, lojas).
        && !/^\/(termos|terms|privacidade|privacy)\/?$/.test(location.pathname)) {
      navigate('/', { replace: true });
      toast.info('Essa página não existe — você está no início.');
      return;
    }
    setViewState(prev => {
      const prevId = (prev as any).clubId;
      const parsedId = (parsed.view as any).clubId;
      const prevBook = (prev as any).bookId;
      const parsedBook = (parsed.view as any).bookId;

      // Se URL nao carrega o clubId/bookId mas o state tem (caso club-empty
      // que vai pra /grupos/recem-criado sem id), preserva o ID em memoria.
      // Caso contrario o useEffect ao re-rodar (ex: books mudou) sobrescreveria
      // com null e quebraria fluxos como invite.
      const same = prev.kind === parsed.view.kind &&
        (prevId === parsedId || (prevId && !parsedId)) &&
        (prevBook === parsedBook || (prevBook && !parsedBook)) &&
        (prev as any).slug === (parsed.view as any).slug;
      if (same) return prev;
      // Mesmo kind mas alguma diferenca: merge preservando IDs em memoria
      // que a URL nao carrega
      if (prev.kind === parsed.view.kind) {
        const merged: any = { ...parsed.view };
        if (prevId && !parsedId) merged.clubId = prevId;
        if (prevBook && !parsedBook) merged.bookId = prevBook;
        if (prev.kind === 'club-empty' && (prev as any).clubData && Object.keys((prev as any).clubData).length > 0 &&
            (!merged.clubData || Object.keys(merged.clubData).length === 0)) {
          merged.clubData = (prev as any).clubData;
        }
        return merged;
      }
      return parsed.view as any;
    });
    setTabState(prev => prev !== parsed.tab ? parsed.tab : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, books]);

  // Clube ativo (quando dentro de club-inner / estudo)
  const [activeClub, setActiveClub] = useState<any | null>(null);
  const [activeClubLoading, setActiveClubLoading] = useState(false);
  // Status HTTP da última falha ao abrir um clube (404 sumiu, 403 é fechado).
  const [activeClubError, setActiveClubError] = useState<number | null>(null);
  const [clubViewingUserId, setClubViewingUserId] = useState<string | null>(null);
  // Estudo do livro — feed por marco
  const [studyMilestones, setStudyMilestones] = useState<any[]>([]);
  const [studyPosts, setStudyPosts] = useState<any[]>([]);
  // Qual tela de Estudo está no ar. O EstudoCaderno busca os PRÓPRIOS dados;
  // quando ele está ativo, `studyPosts` não é lido por ninguém e carregá-lo é
  // uma requisição jogada fora — duas, na prática, porque o efeito roda de novo
  // quando o livro do clube chega. Esta função existe pra que a decisão de
  // RENDERIZAR e a de BUSCAR nunca discordem.
  const usandoCaderno = () => {
    try { return (typeof window !== 'undefined') && window.localStorage.getItem('estudoCaderno') !== '0'; }
    catch { return true; }
  };
  const [studyFilter, setStudyFilter] = useState('all');
  const [studyLoading, setStudyLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Pediu para adicionar um livro nesta abertura? (UX-18 a — ver o preparo.)
  const pediuLivroRef = useRef(false);
  useEffect(() => { if (addOpen) pediuLivroRef.current = true; }, [addOpen]);
  // Sobe a cada leitura salva, por qualquer caminho (sessao do protocolo,
  // sheet da estante, clube). E o que faz o card dos 7 dias perceber a
  // primeira leitura sem depender de recarregar a pagina.
  const [leiturasSalvas, setLeiturasSalvas] = useState(0);
  const [logOpen, setLogOpen] = useState(false);
  // livro alvo do menu "..." (ações rápidas a partir do card, sem abrir a página)
  const [bookActions, setBookActions] = useState<any | null>(null);
  const [clubActions, setClubActions] = useState<{ club: any; mirror: any } | null>(null);
  const [logBook, setLogBook] = useState<BookV2 | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  // ── A VOLTA DO MERCADO PAGO ────────────────────────────────────────
  // Com ?mp=success a pessoa acabou de pagar, mas quem libera o plano é o
  // aviso do MP — que pode chegar segundos depois. Antes o app lia o perfil
  // uma vez, e quem tinha pago via o paywall até recarregar. Agora relê a
  // cada 3 s por até 1 min ('conferindo'); passado isso, diz com franqueza que
  // o MP ainda não confirmou e oferece conferir de novo ('demorou').
  const [voltaDoMp, setVoltaDoMp] = useState<null | 'conferindo' | 'demorou'>(() => {
    try { return new URL(window.location.href).searchParams.get('mp') === 'success' ? 'conferindo' : null; } catch { return null; }
  });
  // A tela de planos aberta pela barra do teste grátis: a mesma do paywall,
  // mas com X — a pessoa ainda está no teste e pode voltar ao app.
  const [planosDoTeste, setPlanosDoTeste] = useState(false);
  // PIX checkout overlay — quando user escolhe Pix anual, abrimos tela custom
  // (paleta Forjamente) com QR + copia-e-cola + auto-poll.
  const [pixCheckoutPayload, setPixCheckoutPayload] = useState<any>(null);
  // Prompt amigável "ativar lembretes" — disparado uma vez após primeira meta diária
  // batida, se push ainda não está ativo. Persiste em localStorage pra não repetir.
  const [pushPromptOpen, setPushPromptOpen] = useState(false);
  // Contexto do paywall: qual plano foi gatilho + label da feature que tentou usar.
  // Quando user clica em algo bloqueado, openPaywall(suggested, label) registra o
  // contexto e o PaywallScreen mostra o plano certo destacado.
  const [paywallSuggested, setPaywallSuggested] = useState<any>(undefined);
  const [paywallFeatureLabel, setPaywallFeatureLabel] = useState<string | undefined>(undefined);
  const [upgradeCelebOpen, setUpgradeCelebOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // Protocolo do Hábito — o estado do dia governa a Home inteira.
  const [habito, setHabito] = useState<EstadoHabito | null>(null);
  const [preparoOpen, setPreparoOpen] = useState(false);
  // Tutorial de perfil adiado: espera a pessoa chegar numa superfície social.
  const [perfilPendente, setPerfilPendente] = useState(false);
  // UX-28: o tutorial de perfil foi pedido e espera a tela ficar livre.
  const [tutorialPerfilPedido, setTutorialPerfilPedido] = useState(false);
  const tutorialJaMostradoRef = useRef(false);
  const [exploreTourOpen, setExploreTourOpen] = useState(false);
  useEffect(() => {
    const onProf = (e: any) => { const d = e?.detail; if (d) setProfile((p: any) => p ? { ...p, ...d } : p); };
    window.addEventListener('forjamente:profile-updated', onProf);
    return () => window.removeEventListener('forjamente:profile-updated', onProf);
  }, []);
  // Tutorial da Explorar: 1ª visita (ou quando reativado em Ajustes → Tutoriais).
  // Na interface nova ele não abre: fala de "leitores e clubes", do Explorar antigo.
  useEffect(() => {
    if (interfaceNova) { setExploreTourOpen(false); return; }
    if (tab !== 'explore' || !profile || onboardingOpen) return;
    const st = (profile as any).tutorials_state?.explore;
    if (st && st.seen && st.enabled !== true) return;
    const t = setTimeout(() => setExploreTourOpen(true), 350);
    return () => clearTimeout(t);
  }, [tab, profile, onboardingOpen, interfaceNova]);
  const [achievement, setAchievement] = useState<AchievementPayload | null>(null);
  const [dailyDoneFor, setDailyDoneFor] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('leitura.dailyDone') || '{}'); } catch { return {}; }
  });

  // ───── Insights ─────
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  // ───── Daily quote ─────
  const [dailyQuote, setDailyQuote] = useState<{ text: string; author: string; book?: { title: string } | null } | null>(null);

  // ───── Push notifications status ─────
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  // Capacitor native app conta como standalone (app já instalado de verdade,
  // mais que PWA). Sem isso, NotifScreen mostrava "instale primeiro via Safari"
  // mesmo dentro do app nativo do iOS. PWA detection (matchMedia/navigator.standalone)
  // não funciona dentro de Capacitor WebView — daí o bypass explícito.
  const isStandalone =
    isNativeApp() ||
    (typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)')?.matches ||
      (window.navigator as any).standalone === true)) || false;
  const [pushStatus, setPushStatus] = useState<PushStatus>({
    supported: typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window,
    permission: (typeof Notification !== 'undefined' ? Notification.permission : 'default') as PushStatus['permission'],
    subscribed: false,
    busy: false,
    error: null,
    isIOS, isStandalone,
  });

  const refreshPushStatus = useCallback(async () => {
    if (typeof window === 'undefined') return;
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    // Em app nativo (Capacitor), reporta como sempre-suportado. Status real
    // (subscribed) vem do backend — checa se há FCM token ativo pra esse user.
    if (isNativeApp()) {
      let subscribed = false;
      try {
        const r = await apiFetchWithSession(session, `${API_BASE}/push/status`);
        if (r.ok) {
          const data = await r.json();
          subscribed = !!data?.subscribed;
        }
      } catch {}
      // NÃO limpa error aqui — o erro só some quando user clica "Atualizar".
      // Sem isso, refreshPushStatus disparado por re-render limparia o erro
      // imediatamente após activatePush setá-lo, deixando o usuário sem ver
      // a mensagem.
      setPushStatus(prev => ({ ...prev, supported: true, permission: subscribed ? 'granted' : 'default', subscribed }));
      return;
    }
    const permission = (supported ? Notification.permission : 'default') as PushStatus['permission'];
    let subscribed = false;
    if (supported) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        subscribed = !!sub;
        // Auto-sync: se o browser já tem subscription mas o backend pode não ter,
        // re-envia silenciosamente ao backend para manter sincronizado.
        if (sub && session && permission === 'granted') {
          try { await api.subscribePush(session, sub); } catch {}
        }
      } catch {}
    }
    // Idem ao caminho native: NÃO limpa error automaticamente.
    setPushStatus(prev => ({ ...prev, supported, permission, subscribed }));
  }, [session]);

  useEffect(() => { refreshPushStatus(); }, [refreshPushStatus, session]);

  // Deep links nativos — registra listener uma vez no boot do app.
  // App Links (Android) / Universal Links (iOS) entregam URL via Capacitor App.
  useEffect(() => {
    setupDeepLinkListener();
    setupPushTapListener();   // tap de notificação registrado no boot (mata o "tap morto")
    installGlobalErrorCapture();
    tlog('boot', { platform: (window as any).Capacitor?.getPlatform?.() || 'web' });
  }, []);

  // Tap de notificação → navegação SPA suave (sem recarregar o app inteiro).
  // platform.ts dispara 'forjamente:navigate' (cancelable); aqui confirmamos que
  // tratamos via react-router chamando preventDefault, e navegamos.
  useEffect(() => {
    const onNav = (e: any) => {
      const path = e?.detail?.path;
      if (!path) return;
      // /e/* são rotas do SERVIDOR (o clique rastreado do teste, server/email/cobaia.js):
      // sem preventDefault, platform.ts abre com carga completa e o servidor
      // grava o toque antes de redirecionar.
      if (/^\/e\//.test(path)) return;
      try { e.preventDefault(); } catch {}
      try { navigate(path); } catch {}
    };
    window.addEventListener('forjamente:navigate', onNav as any);
    return () => window.removeEventListener('forjamente:navigate', onNav as any);
  }, [navigate]);

  // RevenueCat — configure SDK com appUserID = profile.id pra evitar compras
  // sairem como ":..." e nao virem com o user real no webhook.
  // Espera profile.id existir antes de configurar.
  // IDENTIFICAR A CONTA ATUAL, e nao so a primeira do processo.
  //
  // `configureRevenueCat` tem uma trava interna: depois da primeira vez, ela
  // retorna na hora. Isso basta para quem abre o app e usa uma conta so — e
  // falha calado quando duas contas compartilham o celular, porque a segunda
  // herdava a identidade de compra da primeira.
  //
  // `loginRevenueCat` troca a identidade no SDK e e barato de chamar de novo
  // (o proprio SDK ignora quando ja e o mesmo usuario). Por isso ele roda
  // SEMPRE que uma conta entra — inclusive a primeira e inclusive a MESMA
  // conta depois de sair: o logout deixa o RC anonimo (logOut), e so trocar
  // quando a conta muda deixaria quem saiu e voltou comprando como anonimo,
  // sem a compra ser creditada. Tambem cobre o configure que rodou sem conta
  // (o paywall pode buscar os pacotes antes do perfil chegar).
  const rcUltimo = useRef<string | null>(null);
  useEffect(() => {
    if (profile?.id) {
      configureRevenueCat(profile.id, (profile as any)?.email || (session as any)?.user?.email);
      if (rcUltimo.current !== profile.id) {
        loginRevenueCat(profile.id).catch(() => {});
      }
      rcUltimo.current = profile.id;
      setTelemetryUser(profile.id);
    } else {
      rcUltimo.current = null;
      setTelemetryUser(null);
    }
  }, [profile?.id]);

  // RevenueCat — AUTO-CURA do plano. Sincroniza profiles.plan a partir dos direitos
  // ATIVOS no RevenueCat (o backend consulta o RC com a chave secreta). Cobre o caso
  // em que a compra completou mas o webhook/callback do cliente falhou (timeout de 30s,
  // compra num RC anonimo depois fundido, etc). Roda no boot (nativo) e ao voltar o foco.
  const lastRcSyncRef = useRef(0);
  useEffect(() => {
    if (!session || !profile?.id || !isNativeApp()) return;
    let cancelled = false;
    const sync = async () => {
      // Cooldown: no maximo 1 sync a cada 5min — nao martelar a API do RC a cada foco.
      const nowMs = Date.now();
      if (nowMs - lastRcSyncRef.current < 5 * 60 * 1000) return;
      lastRcSyncRef.current = nowMs;
      try {
        const res: any = await api.syncRcPlan(session);
        if (!cancelled && res && res.changed && res.plan) {
          setProfile((p: any) => (p ? { ...p, plan: res.plan } : p));
        }
      } catch {}
    };
    sync();
    const onVis = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(session as any)?.access_token, profile?.id]);

  // ─────────────────────────────────────────────────────────────
  // Feed de notificações (sino do header)
  // ─────────────────────────────────────────────────────────────
  // Estado:
  //  - notifOpen: popover aberto/fechado
  //  - notifItems: últimas 20 atividades (posts + pokes) dos clubes do user
  //  - notifUnread: contagem total de não-lidas (pra badge)
  //  - notifLoading: durante fetch inicial
  // Polling: a cada 60s no foreground pra atualizar badge sem clicar.
  const [notifOpen, setNotifOpen] = useState(false);

  // Declaradas aqui (e não junto do protocolo) porque a pilha do voltar logo
  // abaixo precisa delas (pente fino, UX-06).
  const [pausaFolha, setPausaFolha] = useState(false);
  const [celebracao, setCelebracao] = useState<null | { dia: number | null; paginas: number; primeiroDia: boolean; recorde?: boolean; retorno?: { dias: number; total?: number } | null }>(null);

  // OS MODAIS SE EMPILHAM PARA O VOLTAR.
  //
  // Cada overlay aberto registra o proprio fechador; ao fechar, desregistra. O
  // voltar do Android fecha o do topo. Ficar num efeito so, aqui, tem um
  // motivo: a lista de overlays cresce com o app, e espalhar o registro por
  // dez componentes garante que o proximo overlay nasca esquecido — foi
  // exatamente assim que o `useEffect` de limpeza logo acima ficou cobrindo
  // metade dos sheets.
  useEffect(() => {
    const abertos: Array<[boolean, () => void]> = [
      [notifOpen, () => setNotifOpen(false)],
      [!!achievement, () => setAchievement(null)],
      [!!celebracao, () => setCelebracao(null)],
      [paywallOpen, () => setPaywallOpen(false)],
      [planosDoTeste, () => setPlanosDoTeste(false)],
      // O Pro: do checkout, o voltar devolve à vitrine; da vitrine, fecha.
      [!!proAberto, () => setProAberto((p) => (p === 'checkout' ? 'vitrine' : null))],
      [!!bookActions, () => setBookActions(null)],
      [!!clubActions, () => setClubActions(null)],
      [supportOpen, () => setSupportOpen(false)],
      [!!reportTarget, () => setReportTarget(null)],
      [logOpen, () => setLogOpen(false)],
      [addOpen, () => setAddOpen(false)],
      [tutorialOpen, () => setTutorialOpen(false)],
      // As folhas do clube. Fora daqui, o voltar do Android saia da tela do
      // clube com a folha aberta — e o texto que estava sendo escrito ia junto.
      [clubMenuOpen, () => setClubMenuOpen(false)],
      [clubShareOpen, () => setClubShareOpen(false)],
      [editClubOpen, () => setEditClubOpen(false)],
      [addClubBookOpen, () => setAddClubBookOpen(false)],
      [editClubBookOpen, () => setEditClubBookOpen(false)],
      [milestonesSheetOpen, () => setMilestonesSheetOpen(false)],
      [composeOpen, () => { setComposeOpen(false); setComposeReplyTo(null); }],
      [pokesOpen, () => setPokesOpen(false)],
    ];
    const desfazer = abertos.filter(([v]) => v).map(([, fechar]) => aoVoltarFechar(fechar));
    return () => desfazer.forEach((d) => d());
  }, [notifOpen, achievement, celebracao, paywallOpen, planosDoTeste, proAberto, bookActions, clubActions, supportOpen, reportTarget, logOpen, addOpen, tutorialOpen,
      clubMenuOpen, clubShareOpen, editClubOpen, addClubBookOpen, editClubBookOpen, milestonesSheetOpen, composeOpen, pokesOpen]);

  // ── O VOLTAR DO ANDROID ──────────────────────────────────────────────
  //
  // Registrado UMA vez. A ordem e: fecha o que estiver por cima (a pilha de
  // fechadores em platform.ts), senao volta uma tela do app, e so na raiz
  // deixa o app sair.
  //
  // `goBackRef` existe porque o ouvinte e registrado uma vez so e `goBack`
  // muda de identidade a cada render — sem o ref, o ouvinte ficaria preso a
  // versao do primeiro render e voltaria sempre para o mesmo lugar.
  const goBackRef = useRef<() => void>(() => {});
  useEffect(() => { goBackRef.current = goBack; }, [goBack]);
  // `view` e `tab` tambem por ref: lidos direto no ouvinte, ficavam presos ao
  // primeiro render — na aba Clubes, com a pilha vazia, o voltar podia achar
  // que estava em Hoje e fechar o app em vez de voltar para Hoje.
  const raizRef = useRef({ view, tab });
  useEffect(() => { raizRef.current = { view, tab }; }, [view, tab]);
  // Na web, o voltar do navegador fecha a folha do topo (pente fino, QA-03).
  useEffect(() => { ligarVoltarDoNavegador(); }, []);
  useEffect(() => {
    setupBackButtonHandler(() => {
      // Na raiz nao ha para onde voltar: devolve false e o app sai.
      const { view: v, tab: t } = raizRef.current;
      if (!viewHistoryRef.current.length && v.kind === 'tab' && t === 'home') return false;
      goBackRef.current();
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [notifItems, setNotifItems] = useState<NotifItem[]>([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  // Não deixa um fetch concorrente "ressuscitar" o badge com unreadCount stale do
  // servidor (cenário do WKWebView: abre o sino, app vai/volta de background antes
  // do /read materializar → badge piscava 0→N→0). Boolean cobre a janela do POST
  // em voo (duração arbitrária); o timestamp estende um cooldown pós-/read pra
  // absorver o read-after-write lag do Supabase (mesmo padrão do poll social).
  const notifReadInFlightRef = useRef(false);
  const notifReadMuteUntilRef = useRef(0);
  const fetchNotifFeed = useCallback(async () => {
    if (!session) return;
    try {
      const r = await apiFetchWithSession(session, `${API_BASE}/notifications/feed?limit=20`);
      if (!r.ok) return;
      const data = await r.json();
      setNotifItems(Array.isArray(data?.items) ? data.items : []);
      if (!notifReadInFlightRef.current && Date.now() >= notifReadMuteUntilRef.current) {
        setNotifUnread(typeof data?.unreadCount === 'number' ? data.unreadCount : 0);
      }
    } catch {}
  }, [session]);

  // Fetch inicial + polling 60s + refresh imediato ao voltar o foco (badge ao vivo)
  useEffect(() => {
    if (!session) return;
    fetchNotifFeed();
    const interval = setInterval(fetchNotifFeed, 60_000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchNotifFeed(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, [session, fetchNotifFeed]);

  // Abre popover: refresh + marca como lido (otimista local + chamada server)
  const handleOpenNotif = useCallback(async () => {
    setNotifOpen(true);
    setNotifLoading(true);
    await fetchNotifFeed();
    setNotifLoading(false);
    // Marca como lido em background (não bloqueia UI)
    if (session) {
      notifReadInFlightRef.current = true; // segura o badge até o servidor confirmar
      try {
        await apiFetchWithSession(session, `${API_BASE}/notifications/read`, { method: 'POST' });
      } catch {}
      finally { notifReadInFlightRef.current = false; notifReadMuteUntilRef.current = Date.now() + 8000; }
      // Otimista: zera badge na hora; itens permanecem visíveis sem o estado unread
      setNotifUnread(0);
      setNotifItems(prev => prev.map(it => ({ ...it, unread: false })));
    }
  }, [session, fetchNotifFeed]);

  const handleNotifItemClick = useCallback((item: NotifItem) => {
    setNotifOpen(false);
    if (item.clubId) {
      setView({ kind: 'club-inner', clubId: item.clubId });
    }
  }, [setView]);

  const activatePush = useCallback(async () => {
    if (!session) return;
    setPushStatus(prev => ({ ...prev, busy: true, error: null }));
    try {
      // Caminho nativo (Capacitor Android/iOS): usa FCM via plugin oficial.
      // Web Push (VAPID) NÃO funciona em WebView — registramos via FCM direto.
      if (isNativeApp()) {
        const r = await registerNativePush(API_BASE, session.accessToken);
        if (!r.ok) {
          if (r.error === 'permission_denied') throw new Error('Você não permitiu notificações. Vá em Ajustes → Notificações e habilite o Forjamente.');
          throw new Error('Não foi possível ativar notificações nativas: ' + (r.error || 'erro'));
        }
        setPushStatus(prev => ({ ...prev, busy: false, subscribed: true, permission: 'granted' }));
        return;
      }

      // Caminho web (PWA / browser): mantém Web Push VAPID.
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Seu navegador não suporta notificações push.');
      }
      if (isIOS && !isStandalone) {
        throw new Error('No iPhone, primeiro instale o app na tela de início (Safari → Compartilhar → Adicionar à tela de início).');
      }
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }
      if (perm === 'denied') {
        throw new Error('Você bloqueou as notificações. Abra as configurações do navegador para este site e permita notificações.');
      }
      if (perm !== 'granted') {
        throw new Error('Permissão não concedida.');
      }

      const { publicKey } = await api.getPushPublicKey(session);
      if (!publicKey) throw new Error('Chave VAPID ausente no servidor.');

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      await api.subscribePush(session, sub);
      await refreshPushStatus();
      setPushStatus(prev => ({ ...prev, busy: false, subscribed: true }));
    } catch (e: any) {
      // NÃO chama refreshPushStatus aqui — refresh sobrescreveria o erro
      // imediatamente, fazendo a mensagem sumir antes do user ler. Erro
      // persiste até user clicar "Atualizar" (que chama refreshPushStatus
      // explicitamente via wrapper que limpa error antes).
      setPushStatus(prev => ({ ...prev, busy: false, error: e.message || 'Erro ao ativar' }));
    }
  }, [session, refreshPushStatus, isIOS, isStandalone]);

  const testPush = useCallback(async () => {
    if (!session) return;
    setPushStatus(prev => ({ ...prev, busy: true, error: null }));
    try {
      await api.testPush(session);
    } catch (e: any) {
      setPushStatus(prev => ({ ...prev, error: e.message || 'Erro' }));
    } finally {
      setPushStatus(prev => ({ ...prev, busy: false }));
    }
  }, [session]);

  // ───── Notifications settings ─────
  const [notifConfig, setNotifConfig] = useState<NotifConfig>(DEFAULT_NOTIF_CONFIG);

  // Load notif settings when session ready
  useEffect(() => {
    if (!session) return;
    (async () => {
      try {
        const s = await api.getNotifSettings(session);
        setNotifConfig({
          zeroReading: {
            enabled:        s?.zeroReading?.enabled ?? DEFAULT_NOTIF_CONFIG.zeroReading.enabled,
            startTime:      s?.zeroReading?.startTime ?? DEFAULT_NOTIF_CONFIG.zeroReading.startTime,
            endTime:        s?.zeroReading?.endTime ?? DEFAULT_NOTIF_CONFIG.zeroReading.endTime,
            frequencyValue: s?.zeroReading?.frequencyValue ?? DEFAULT_NOTIF_CONFIG.zeroReading.frequencyValue,
            frequencyUnit:  s?.zeroReading?.frequencyUnit ?? DEFAULT_NOTIF_CONFIG.zeroReading.frequencyUnit,
          },
          belowGoal: {
            enabled:        s?.belowGoal?.enabled ?? DEFAULT_NOTIF_CONFIG.belowGoal.enabled,
            startTime:      s?.belowGoal?.startTime ?? DEFAULT_NOTIF_CONFIG.belowGoal.startTime,
            endTime:        s?.belowGoal?.endTime ?? DEFAULT_NOTIF_CONFIG.belowGoal.endTime,
            frequencyValue: s?.belowGoal?.frequencyValue ?? DEFAULT_NOTIF_CONFIG.belowGoal.frequencyValue,
            frequencyUnit:  s?.belowGoal?.frequencyUnit ?? DEFAULT_NOTIF_CONFIG.belowGoal.frequencyUnit,
          },
          bookGoalHit:  { enabled: s?.bookGoalHit?.enabled ?? true },
          dailyGoalHit: { enabled: s?.dailyGoalHit?.enabled ?? true },
        });
      } catch {}
    })();
  }, [session]);

  const saveNotifConfig = async (next: NotifConfig) => {
    if (!session) return;
    const payload = {
      zeroReading: {
        enabled:        next.zeroReading.enabled,
        startTime:      next.zeroReading.startTime,
        endTime:        next.zeroReading.endTime,
        frequencyValue: next.zeroReading.frequencyValue,
        frequencyUnit:  next.zeroReading.frequencyUnit,
        recurrence:     'everyday',
        days:           [true, true, true, true, true, true, true],
      },
      belowGoal: {
        enabled:        next.belowGoal.enabled,
        startTime:      next.belowGoal.startTime,
        endTime:        next.belowGoal.endTime,
        frequencyValue: next.belowGoal.frequencyValue,
        frequencyUnit:  next.belowGoal.frequencyUnit,
        recurrence:     'everyday',
        days:           [true, true, true, true, true, true, true],
      },
      bookGoalHit:  { enabled: next.bookGoalHit.enabled },
      dailyGoalHit: { enabled: next.dailyGoalHit.enabled },
    };
    try {
      await api.putNotifSettings(session, payload);
      toast.success('Preferências salvas');
    } catch (e: any) {
      console.error('[notif] save', e);
      toast.error('Não consegui salvar — tenta de novo');
    }
  };

  const today = todayKeySP();

  // Após migração 2026-05-03 dos planos: qualquer plano pago habilita features premium.
  // ACESSO AO APP — plano pago nao e mais a unica porta.
  //   fundador : existia antes do lancamento (acesso vitalicio)
  //   promo    : ganhou 1 ano por 3 indicacoes
  //   trial    : 7 dias de acesso completo desde o cadastro
  // O backend e a fonte de verdade (GET /api/me/access); estes campos do
  // profile sao o mesmo dado, lidos aqui pra evitar round-trip no boot.
  const agoraMs = Date.now();
  const ehFundador = !!(profile as any)?.is_founder;
  const temPromo = !!(profile as any)?.promo_access_until
    && new Date((profile as any).promo_access_until).getTime() > agoraMs;
  const temAcessoPorTrial = !!(profile as any)?.trial_ends_at
    && new Date((profile as any).trial_ends_at).getTime() > agoraMs;
  const planoPago = !!profile?.plan && profile.plan !== 'free';
  // O APP É GRÁTIS (decisão do Wesley, 14/10/2026): todo mundo tem o app
  // inteiro. O que se paga é assinar um Mentor (Círculo do Mentor). As
  // variáveis acima ficam só para as telas que mostram de onde vem o acesso.
  void (planoPago || ehFundador || temPromo || temAcessoPorTrial);
  const isPro = true;
  const limits = isPro ? PLAN_LIMITS.pro : PLAN_LIMITS.free;

  // Apply palette to <html>
  useEffect(() => {
    const pal = profile?.palette || 'midnight';
    document.documentElement.setAttribute('data-palette', pal);
    document.body.setAttribute('data-palette', pal);
  }, [profile?.palette]);

  // Subdomínio admin.forjamente.com.br: navega pra /admin assim que o user
  // estiver logado e identificado como admin. Senão, renderiza tela de login
  // normal (sem flash da home) — usuários comuns que caem aqui por engano são
  // redirecionados pro domínio principal pelo handler de view.kind === 'admin'.
  useEffect(() => {
    if (isAdminSubdomain && session && profile && view.kind !== 'admin') {
      setView({ kind: 'admin' });
    }
  }, [isAdminSubdomain, session, profile?.id]); // eslint-disable-line

  // Persist tab + dailyDone + clubsTerm
  useEffect(() => { localStorage.setItem('leitura.tab', tab); }, [tab]);
  useEffect(() => { localStorage.setItem('leitura.dailyDone', JSON.stringify(dailyDoneFor)); }, [dailyDoneFor]);
  useEffect(() => { localStorage.setItem('leitura.clubsTerm', clubsTerm); }, [clubsTerm]);

  // ───────────────────────────────────────────────
  // Bootstrap session
  // ───────────────────────────────────────────────
  useEffect(() => {
    // Hash auth ja foi detectado no useState lazy init (sincrono). Aqui so
    // mostra mensagem contextual + dispara welcome email se foi signup verify.
    // Detect baseado em ?verified=1 OU em hash residual (raro, ja foi limpo).
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('verified') === '1' && session) {
        setAuthLoading(false);
        setTimeout(() => toast.success('Email confirmado. Bem-vindo ao Forjamente.'), 50);
        apiFetchWithSession(session, `${API_BASE}/auth/welcome-email`, { method: 'POST' }).catch(() => {});
        // Limpa o ?verified=1 da URL
        window.history.replaceState({}, '', '/');
        return;
      }
    } catch {}

    // 2) Detecta retorno do checkout Mercado Pago.
    //    MP redireciona pra /?mp=success | failure | pending após o user autorizar.
    //    Mostra toast e limpa URL — webhook do backend já atualizou plan_status
    //    no DB; loadAllData() vai pegar o plano novo na próxima request.
    try {
      const url = new URL(window.location.href);
      const mpStatus = url.searchParams.get('mp');
      if (mpStatus) {
        if (mpStatus === 'success') {
          // Sem toast: a tela de "confirmando" (voltaDoMp) diz o que está
          // acontecendo e some sozinha quando o plano ativa.
        } else if (mpStatus === 'failure') {
          setTimeout(() => toast.error('Pagamento não autorizado. Tente novamente.'), 50);
        } else if (mpStatus === 'pending') {
          setTimeout(() => toast.info('Pagamento pendente de confirmação. Avisamos por email assim que aprovar.'), 50);
        }
        window.history.replaceState({}, '', '/');
      }
    } catch {}

    (async () => {
      const stored = loadStoredSession();
      if (!stored) { setAuthLoading(false); return; }
      try {
        // Try /auth/me
        const res = await apiFetchWithSession(stored.session, `${API_BASE}/auth/me`);
        if (res.ok) {
          setSession(stored.session);
          setAuthLoading(false);
          return;
        }
        // try refresh
        if (stored.session.refreshToken) {
          const envelope = await refreshSession(stored.session.refreshToken);
          saveStoredSession(envelope.session, stored.persistent);
          setSession(envelope.session);
        }
      } catch (e) {
        saveStoredSession(null);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, []);

  // ───────────────────────────────────────────────
  // Auto-refresh do JWT — renova proativamente antes do access token expirar
  // pra que o user NUNCA seja deslogado por timeout enquanto tem refresh
  // token valido. Sem isso, o JWT (default ~1h no Supabase) expira e o user
  // ve "sessao expirada" mesmo com refresh_token ainda valido por semanas.
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (!session?.refreshToken) return;
    let stopped = false;

    const runRefresh = async () => {
      try {
        const envelope = await refreshSession(session.refreshToken);
        if (stopped) return;
        saveStoredSession(envelope.session, true);
        setSession(envelope.session);
      } catch (e) {
        // Falha de refresh: nao desloga aqui — o proximo fetch autenticado
        // vai bater no servidor e (se 401) o fluxo de logout natural cuida.
        console.warn('[auth] auto-refresh falhou:', (e as any)?.message);
      }
    };

    const scheduleNext = () => {
      const now = Math.floor(Date.now() / 1000);
      const exp = session.expiresAt || (now + 3600);
      // Renova 5 min antes de expirar; minimo de 30s pra evitar loop tight
      // caso o token venha ja muito perto do limite.
      const secsUntilRefresh = Math.max(30, exp - now - 5 * 60);
      return window.setTimeout(() => {
        if (stopped) return;
        runRefresh();
      }, secsUntilRefresh * 1000);
    };

    const timer = scheduleNext();

    // Ao voltar do background (iOS suspende setTimeout), checa se token ja
    // expirou (ou esta perto) e refaz refresh imediatamente.
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Math.floor(Date.now() / 1000);
      const exp = session.expiresAt || 0;
      if (exp - now <= 5 * 60) runRefresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [session?.refreshToken, session?.expiresAt]);

  // ───────────────────────────────────────────────
  // Load user data when session ready
  // ───────────────────────────────────────────────
  const loadAllData = useCallback(async () => {
    if (!session) return;
    setDataLoading(true);
    try {
      const [prof, lib, s, h, an, ach, sh] = await Promise.all([
        api.getProfile(session).catch(() => null),
        api.getLibrary(session).catch(() => []),
        api.getStreak(session).catch(() => null),
        api.getHeatmap(session, 26).catch(() => null),
        api.getAnalytics(session).catch(() => null),
        api.getAchievements(session).catch(() => ({ achievements: [], newlyUnlocked: [] })),
        api.listShelves(session).catch(() => []),
      ]);
      setProfile(prof as ProfileV2 | null);
      setBooks(lib);
      setShelves(sh as ShelfV2[]);
      // FP4 — mútuos (seguidores ∪ seguindo) p/ autocomplete de @menção (fire-and-forget)
      const myId = (prof as any)?.id;
      if (myId) {
        Promise.all([
          api.listFollowers(session, myId).catch(() => []),
          api.listFollowing(session, myId).catch(() => []),
        ]).then(([fr, fg]: any[]) => {
          const map: any = {};
          // listFollowers/Following retornam { people: [...] } (não array direto)
          const arr = (x: any) => Array.isArray(x) ? x : ((x && x.people) || []);
          [...arr(fr), ...arr(fg)].forEach((u: any) => {
            if (u?.id && u.id !== myId) map[u.id] = { id: u.id, name: u.display_name || u.name || u.username, username: u.username, avatarUrl: u.avatar_url || u.avatarUrl || null };
          });
          setMentionPeople(Object.values(map));
        }).catch(() => {});
      }
      setStreak(s);
      setHeatmap(h);
      setAnalytics(an);
      setAchievements(ach.achievements);
      api.getDailyQuote(session).then(q => setDailyQuote({ text: q.text, author: q.author, book: q.book })).catch(() => {});
      // Protocolo do Hábito — quem manda na primeira abertura é o estado do
      // protocolo, não o tutorial de perfil. Pedir avatar, @username e bio antes
      // da primeira página lida é identidade antes de execução, que é exatamente
      // a inversão que o protocolo combate. O tutorial de perfil passa a ser
      // disparado só quando a pessoa chega numa superfície social.
      let precisaPreparar = false;
      let temProtocolo = false;
      try {
        const h = await habitoApi.hoje(session);
        setHabito(h);
        // DISPENSADO nunca reabre o fluxo: a promessa de não perguntar de novo
        // só vale se ela for cumprida em toda abertura do app.
        precisaPreparar = h.estado === 'PRECISA_QUALIFICAR' || h.estado === 'PRECISA_PREPARAR';
        temProtocolo = !!h.inscricao && h.estado !== 'DISPENSADO';
      } catch { /* protocolo indisponível não pode travar o app */ }

      // Nao dispara nada quando ha convite pendente — o novo membro deve cair
      // direto no clube (o boas-vindas do clube cuida do onboarding).
      const hasPendingInvite = (() => { try { return !!localStorage.getItem('leitura.pendingInviteToken'); } catch { return false; } })();
      const querTutorialDePerfil = !!prof && (!prof.tutorial_seen
        || (prof.tutorials_state && prof.tutorials_state.onboarding && prof.tutorials_state.onboarding.enabled === true));

      // Interface nova: nem o questionário do protocolo nem o tutorial antigo
      // abrem sozinhos. A conta nova recebe a entrada curta, uma vez só.
      const iface = await (interfaceNovaProm.current || Promise.resolve(null));
      const entradaVista = !!(prof as any)?.tutorials_state?.entrada?.seen
        || (() => { try { return localStorage.getItem('forjamente.entrada-curta') === '1'; } catch { return false; } })();

      if (ABRIU_PELO_LINK_DO_MENTOR && await circuloAtivo()) {
        // Nada por cima da página do Mentor (ver ABRIU_PELO_LINK_DO_MENTOR).
      } else if (iface?.nova) {
        if (precisaPreparar && !hasPendingInvite && !entradaVista) setEntradaAberta(true);
      } else if (precisaPreparar && !hasPendingInvite) {
        // Quem já tocou em "Adicionar livro" recebe a folha de inclusão, não o
        // questionário por cima dela (pente fino, UX-18 a). O questionário
        // segue na entrada das próximas aberturas — isso é o funil (UX-18 b).
        setTimeout(() => { if (!pediuLivroRef.current) setPreparoOpen(true); }, 350);
      } else if (querTutorialDePerfil && !hasPendingInvite) {
        // Quem está no protocolo NÃO recebe o tutorial de perfil na Home. Avatar,
        // @username e bio são identidade, e identidade antes de execução reduz a
        // execução. Ele fica pendente e só abre quando a pessoa chega numa
        // superfície social — que é onde ele passa a fazer sentido.
        if (temProtocolo) setPerfilPendente(true);
        else if (!tutorialJaMostradoRef.current) setTutorialPerfilPedido(true);
      }
    } finally {
      setDataLoading(false);
      setPrimeiraCarga(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) loadAllData();
  }, [session, loadAllData]);
  loadAllDataRef.current = loadAllData;

  // ───── Carrega clubes ─────
  const loadClubs = useCallback(async () => {
    if (!session) return;
    setClubsLoading(true);
    try {
      const r = await api.getClubs(session);
      setClubs(r.clubs || []);
      setPendingInvites(r.pendingInvites || []);
      // Clube Social: propostas/lançamentos que me convidaram ou que propus
      try { const pr = await api.listMyProposals(session); setMyProposals(pr.proposals || []); } catch { setMyProposals([]); }
      // Tenta carregar explore (silencia se plano free, retorna 402)
      try {
        const ex = await api.exploreClubs(session, { limit: 6 });
        setExploreClubs(ex.clubs || []);
      } catch {
        setExploreClubs([]);
      }
    } catch (e) {
      console.error('[clubs] load', e);
    } finally {
      setClubsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) loadClubs();
  }, [session, loadClubs]);

  // Aceita convite pendente após login/signup. Se tem `pendingInviteToken` no
  // localStorage e session existe, aceita e navega pro clube.
  useEffect(() => {
    if (!session) return;
    let pending: string | null = null;
    try { pending = localStorage.getItem('leitura.pendingInviteToken'); } catch {}
    // Fonte de servidor: convite guardado no signup (sobrevive entre devices).
    if (!pending) pending = (profile as any)?.pending_invite_token || null;
    if (!pending) return;
    (async () => {
      try {
        const r = await api.acceptInvite(session, pending!);
        try { localStorage.removeItem('leitura.pendingInviteToken'); } catch {}
        const inherited = (() => { try { return localStorage.getItem('leitura.inheritedTerm'); } catch { return null; } })();
        if (inherited === 'clube' || inherited === 'grupo') {
          await api.setPreferredTerm(session, inherited);
          setClubsTerm(inherited);
          try { localStorage.removeItem('leitura.inheritedTerm'); } catch {}
        }
        await loadClubs();
        if (r.clubId) setView({ kind: 'club-inner', clubId: r.clubId });
      } catch (e: any) {
        console.error('[invite/auto-accept]', e);
        try { localStorage.removeItem('leitura.pendingInviteToken'); } catch {}
        // Falha visivel: explica o motivo em vez de cair no app vazio em silencio.
        const m = (e?.message || '').toLowerCase();
        let friendly = 'Não foi possível entrar no clube pelo convite. Peça um novo link a quem te convidou.';
        if (m.includes('expir')) friendly = 'Esse convite expirou. Peça um novo link a quem te convidou.';
        else if (m.includes('cheio') || m.includes('lotad') || m.includes('limite') || m.includes('máx') || m.includes('max')) friendly = 'Esse clube está cheio no momento.';
        else if (m.includes('outro email') || m.includes('e-mail') || m.includes('destinad')) friendly = 'Esse convite era para outro email.';
        else if (m.includes('já') && (m.includes('membro') || m.includes('member'))) friendly = '';
        if (friendly) setTimeout(() => toast.error(friendly), 500);
      }
    })();
  }, [session, loadClubs, (profile as any)?.pending_invite_token]);

  // Após login, se havia um perfil pendente (/@handle aberto deslogado), resolve
  // o handle e abre o perfil dentro do app. Espelha o fluxo de pendingInviteToken.
  useEffect(() => {
    if (!session) return;
    let pending: string | null = null;
    try { pending = localStorage.getItem('leitura.pendingProfileHandle'); } catch {}
    if (!pending) return;
    (async () => {
      try {
        const r = await api.getProfilePreview(pending!);
        try { localStorage.removeItem('leitura.pendingProfileHandle'); } catch {}
        if (r?.id) openProfile(r.id);
      } catch {
        try { localStorage.removeItem('leitura.pendingProfileHandle'); } catch {}
      }
    })();
  }, [session]);

  // Círculo (P4): a conta ficou pronta depois de um pedido na vitrine do Mentor
  // — volta para /mentor/:slug com o passo pedido (assinar, clube, caderno).
  // Espera a chave do Círculo: desligado, o pedido é só apagado.
  useEffect(() => {
    if (!session || circuloLigado === null) return;
    const p = circuloLigado ? lerMentorPendente() : null;
    try { localStorage.removeItem(MENTOR_PENDENTE_KEY); } catch {}
    setMentorPediuConta(false);
    if (!p) return;
    setMentorAoEntrar(p.acao);
    if (!(view.kind === 'mentor' && (view as any).slug === p.slug)) setView({ kind: 'mentor', slug: p.slug } as any);
  }, [session, circuloLigado]); // eslint-disable-line react-hooks/exhaustive-deps

  // Círculo desligado (CIRCULO_ATIVO): o pedido guardado neste aparelho é
  // limpo, e /mentor/:slug, o estúdio e o editor (aberto por link no app, ou
  // pelo voltar do navegador) caem na Home, sem aviso.
  const telaDoCirculo = view.kind === 'mentor' || view.kind === 'mentorEstudio' || view.kind === 'circuloEditor';
  useEffect(() => {
    if (circuloLigado !== false) return;
    try { localStorage.removeItem(MENTOR_PENDENTE_KEY); } catch {}
    if (ABRIU_PELO_LINK_DO_MENTOR) {
      // As telas cheias da abertura voltam ao normal (ver ABRIU_PELO_LINK_DO_MENTOR).
      setRetornoAdiado(false);
      setMedicaoAdiada(adiadoAte('leitura.srbaiAdiado'));
      setFormaturaAdiada(adiadoAte('leitura.formaturaAdiada'));
    }
  }, [circuloLigado]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (circuloLigado !== false || !telaDoCirculo) return;
    setMentorPediuConta(false);
    setTabState('home');
    setViewState({ kind: 'tab' } as any);
    navigate(viewToUrl({ kind: 'tab' }, 'home'), { replace: true });
  }, [circuloLigado, telaDoCirculo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincroniza preferred_term com profile do backend ao carregar
  useEffect(() => {
    if (profile?.preferred_term && profile.preferred_term !== clubsTerm) {
      setClubsTerm(profile.preferred_term as 'clube' | 'grupo');
    }
  }, [profile?.preferred_term]); // eslint-disable-line

  // Persiste mudança de termo no backend
  const changeClubsTerm = useCallback(async (next: 'clube' | 'grupo') => {
    setClubsTerm(next);
    if (!session) return;
    try { await api.setPreferredTerm(session, next); } catch (e) { console.error('[term]', e); }
  }, [session]);

  // ───── Handlers de clubes ─────
  const handleCreateClub = useCallback(async (form: any) => {
    if (!session) throw new Error('Sessão expirada');
    // Multi-livro: converte unidade pt-BR pra schema do backend e monta lista
    // de livros que vai pro POST /api/clubs. Se startEmpty=true, manda books
    // vazio e o admin define depois.
    const unitMap: Record<string, string> = { semanas: 'weeks', meses: 'months', ano: 'year' };
    const seqBooks = Array.isArray(form.sequenceBooks)
      ? form.sequenceBooks.filter((b: any) => b && b.title && b.author && b.totalPages)
      : [];
    const booksPayload = form.startEmpty
      ? []
      : (seqBooks.length > 0
          ? seqBooks
          : (form.book && form.book.title && form.book.author && form.book.totalPages ? [form.book] : []));
    const payload = {
      name: form.name,
      description: form.description,
      mode: form.mode,
      privacy: form.privacy,
      coverTemplate: form.coverTemplate,
      storiesPostBy: form.storiesPostBy || 'all',
      customCoverUrl: form.customCover || null,
      customBannerUrl: form.customBanner || null,
      coverHue: form.coverHue ?? 22,
      postBy: form.postBy,
      openWhen: form.openWhen,
      openDays: form.openDays || [],
      allowPoke: form.allowPoke,
      publicRanking: form.publicRanking,
      allowMemberQuiz: form.allowMemberQuiz,
      // Cronograma multi-livro (so usado em group-read).
      totalBooksPlanned: form.totalBooksPlanned || null,
      durationValue: form.noDeadline ? null : (form.durationCount || null),
      durationUnit: form.noDeadline ? null : (unitMap[form.durationUnit] || null),
      // Lista de livros (substitui `book` singular quando presente).
      books: booksPayload,
      // Clube Social: quando é PROPOSTA, manda status + objetivo/regras/data + convidar mútuos.
      ...(form.propose ? {
        status: 'proposta',
        objective: form.objective || null,
        rulesText: form.rulesText || null,
        // datetime-local é "naive" (sem fuso) → converte pro instante absoluto
        // (new Date aplica o offset local do browser) pra não deslocar 3h no VPS (UTC).
        proposedStartAt: form.proposedStartAt ? (() => { try { return new Date(form.proposedStartAt).toISOString(); } catch { return null; } })() : null,
        // convidar: todos os mútuos OU só os selecionados
        ...(form.inviteMode === 'select' ? { inviteeIds: form.selectedIds || [] } : { inviteAll: true }),
      } : {}),
    };
    try {
      const created = await api.createClub(session, payload);
      fetch('/api/debug-log', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ tag: 'club-create-result', data: 'id=' + (created?.id || 'NULL') + ' keys=' + Object.keys(created || {}).join(',') + ' raw=' + JSON.stringify(created)?.slice(0,200) }) }).catch(()=>{});
      loadClubs();
      return created;
    } catch (e: any) {
      // Criar clube é só para Mentor (403 mentor_required). Marca o perfil para
      // a tela trocar pelo aviso "para Mentores" — sem paywall.
      if (e?.message === 'mentor_required') {
        setProfile((p: any) => p ? { ...p, is_mentor: false } : p);
      }
      throw e;
    }
  }, [session, loadClubs]);

  // Sair de um grupo. Backend desvincula livros pessoais antes de remover
  // a membership (livro espelhado vira pessoal puro). Owner não pode usar
  // — owner tem que deletar o grupo.
  const handleLeaveClub = useCallback(async (clubId: string, clubName: string) => {
    if (!session) return;
    const term = clubsTerm === 'grupo' ? 'grupo' : 'clube';
    if (!window.confirm(`Sair do ${term} "${clubName}"?\n\nVocê continua com seus livros pessoais. Pode entrar de novo se receber um novo convite.`)) return;
    try {
      await api.leaveClub(session, clubId);
      await loadClubs();
      setTab('clubs');
      toast.success(`Você saiu do ${term} "${clubName}".`);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao sair do ' + term);
    }
  }, [session, loadClubs, clubsTerm]);

  // Excluir grupo (só owner). Backend faz soft-delete, desvincula livros
  // pessoais de TODOS os membros, e limpa memberships pra liberar slots.
  const handleDeleteClub = useCallback(async (clubId: string, clubName: string) => {
    if (!session) return;
    const term = clubsTerm === 'grupo' ? 'grupo' : 'clube';
    if (!window.confirm(`Excluir o ${term} "${clubName}"?\n\nIsso vai remover todos os membros (eles ficam com os livros pessoais deles intactos). Esta ação é IRREVERSÍVEL.`)) return;
    if (!window.confirm(`Tem certeza absoluta? Digite mentalmente "${clubName}" e clique OK pra confirmar.`)) return;
    try {
      await api.deleteClub(session, clubId);
      await loadClubs();
      setTab('clubs');
      toast.success(`${term[0].toUpperCase() + term.slice(1)} "${clubName}" excluído.`);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao excluir ' + term);
    }
  }, [session, loadClubs, clubsTerm]);

  const handleAcceptInvite = useCallback(async (token: string) => {
    if (!session) return;
    try {
      const r = await api.acceptInvite(session, token);
      await loadClubs();
      if (r.clubId) setView({ kind: 'club-inner', clubId: r.clubId });
    } catch (e: any) {
      // Limite de grupos atingido (free entra em até 2)
      if (e?.message?.includes('plan_required') || e?.status === 402 || e?.message?.includes('plan_limit_reached')) {
        openPaywall('reader', 'Entrar em mais grupos');
        return;
      }
      if (e?.message?.includes('club_member_limit_reached')) {
        toast.error('Esse grupo atingiu o máximo de membros.');
        return;
      }
      toast.error(e?.message || 'Erro ao aceitar convite');
    }
  }, [session, loadClubs]);

  const handleRejectInvite = useCallback(async (token: string) => {
    if (!session) return;
    try {
      await api.rejectInvite(session, token);
      await loadClubs();
    } catch (e: any) {
      console.error('[invite/reject]', e);
    }
  }, [session, loadClubs]);

  // Carrega o detalhe completo de um clube quando o usuário entra em qualquer
  // view interna do clube. O state PERMANECE entre navegações dentro do mesmo
  // clube — sem isso, `activeClub?.yourRole` ficava null momentaneamente após
  // mudanças de view e ActionSheet/ComposeSheet caíam pra modo member-only.
  // Cleanup só roda quando user sai do clube completamente (sem clubId).
  useEffect(() => {
    const inClubScope = ['club-inner', 'estudo', 'insight-detail', 'club-question-box', 'club-analytics']
      .includes(view.kind);
    const clubId = inClubScope ? (view as any).clubId : null;
    if (!session || !clubId) {
      setActiveClub(null);
      setClubViewingUserId(null);
      return;
    }
    // Se ja temos o clube certo carregado: NAO mostra loading (evita flash),
    // mas dispara refresh silencioso em background pra atualizar ranking +
    // metricas em tempo real (paginas lidas hoje, etc).
    const haveCached = activeClub?.id === clubId;
    let cancelled = false;
    if (!haveCached) setActiveClubLoading(true);
    setActiveClubError(null);
    (async () => {
      try {
        const c = await api.getClub(session, clubId);
        if (!cancelled) setActiveClub(c);
      } catch (e: any) {
        console.error('[club/get]', e);
        // Sem isto a tela do clube ficava em "Carregando…" pra sempre: o
        // loading acabava, mas activeClub seguia null e o render não sabia
        // diferenciar "ainda vem" de "não vem nunca".
        if (!cancelled) setActiveClubError(e?.status || 500);
      } finally {
        if (!cancelled && !haveCached) setActiveClubLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, (view as any).clubId, view.kind]);

  // A TELA DO CLUBE AO VIVO (pente fino, MOB-07). Mural e Estudo já eram ao
  // vivo, mas membros e progresso só mudavam recarregando: alguém entrava pelo
  // convite e a líder seguia vendo "1 membros"; alguém lia 20 páginas e o
  // clube seguia em "0 / 200". Enquanto a tela do clube está aberta e visível,
  // o detalhe é relido em silêncio a cada 30 s (e ao voltar para o app).
  useEffect(() => {
    if (!session || view.kind !== 'club-inner') return;
    const clubId = (view as any).clubId;
    if (!clubId) return;
    let vivo = true;
    const reler = async () => {
      if (document.visibilityState !== 'visible') return;
      try { const c = await api.getClub(session, clubId); if (vivo && c && c.id === clubId) setActiveClub(c); } catch { /* segue com o que tem */ }
    };
    const t = setInterval(reler, 30000);
    const aoVoltar = () => { if (document.visibilityState === 'visible') reler(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { vivo = false; clearInterval(t); document.removeEventListener('visibilitychange', aoVoltar); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, view.kind, (view as any).clubId]);

  // Carrega marcos enquanto user navega DENTRO do escopo de um clube (qualquer view
  // de detalhe do clube). Posts do feed só carregam no estudo (custoso).
  // O cleanup só roda quando user sai do clube completamente — assim, ao navegar
  // de estudo pra insight-detail, milestones permanecem em memória pra o detail
  // poder mostrar Marco N de M / navegação cronológica entre insights.
  useEffect(() => {
    const inClubScope = ['club-inner', 'estudo', 'insight-detail', 'club-question-box', 'club-analytics']
      .includes(view.kind);
    if (!session || !inClubScope) {
      if (!inClubScope) {
        setStudyMilestones([]);
        setStudyPosts([]);
        setStudyFilter('all');
      }
      return;
    }
    const clubId = (view as any).clubId;
    if (!clubId) return;
    // bookId vem da view (quando user abre estudo de livro especifico do
    // historico); senao default = livro atual do clube.
    const viewBookId = (view as any).bookId || null;
    const effectiveBookId = viewBookId || activeClub?.book?.id || null;
    let cancelled = false;
    setStudyLoading(true);
    (async () => {
      try {
        const promises: any[] = [api.listMilestones(session, clubId, { bookId: effectiveBookId }).catch(() => ({ milestones: [] }))];
        // Só busca posts se a tela que os consome estiver no ar.
        const precisaDosPosts = view.kind === 'estudo' && !usandoCaderno();
        if (precisaDosPosts) {
          promises.push(api.listPosts(session, clubId, { filter: studyFilter, limit: 30, bookId: effectiveBookId }).catch(() => ({ posts: [], nextCursor: null })));
        }
        const results = await Promise.all(promises);
        if (!cancelled) {
          setStudyMilestones(results[0].milestones || []);
          if (precisaDosPosts) setStudyPosts(results[1]?.posts || []);
          // Em outras views de clube, NÃO mexe em studyPosts (preserva pra voltar pro estudo)
        }
      } catch (e) {
        console.error('[study/load]', e);
      } finally {
        if (!cancelled) setStudyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session, (view as any).clubId, (view as any).bookId, activeClub?.book?.id, view.kind, studyFilter]);

  // Recarregar só os marcos (após criar/fechar)
  const reloadMilestones = useCallback(async () => {
    const clubId = (view.kind === 'club-inner' || view.kind === 'estudo') ? (view as any).clubId : null;
    if (!session || !clubId) return;
    const viewBookId = (view as any).bookId || null;
    const effectiveBookId = viewBookId || activeClub?.book?.id || null;
    try {
      const r = await api.listMilestones(session, clubId, { bookId: effectiveBookId });
      setStudyMilestones(r.milestones || []);
    } catch (e) { console.error('[milestones/reload]', e); }
  }, [session, view, activeClub?.book?.id]);

  // Toggle de reação num post — otimistico no server (count vem certo), mas
  // já atualizamos o post localmente pra UX instantânea.
  const handleReactPost = useCallback(async (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => {
    if (!session || view.kind !== 'estudo') return;
    try {
      await api.reactToPost(session, view.clubId, postId, kind);
      // Recarrega o post específico (o jeito mais simples é recarregar o feed; pra escala futura, atualizar in-place)
      const ps = await api.listPosts(session, view.clubId, { filter: studyFilter, limit: 30 });
      setStudyPosts(ps.posts || []);
    } catch (e: any) {
      console.error('[react]', e);
    }
  }, [session, view, studyFilter]);

  // Editar post: chama PATCH e atualiza o post in-place no studyPosts.
  // Throws repassam pro PostCard mostrar window.alert.
  const handleEditPost = useCallback(async (postId: string, patch: any) => {
    if (!session || view.kind !== 'estudo') return;
    const r = await api.updatePost(session, view.clubId, postId, patch);
    setStudyPosts(prev => prev.map(p => p.id === postId ? { ...p, ...r.post } : p));
  }, [session, view]);

  // Excluir post: soft-delete no backend + remove localmente.
  const handleDeletePost = useCallback(async (postId: string) => {
    if (!session || view.kind !== 'estudo') return;
    await api.deletePost(session, view.clubId, postId);
    setStudyPosts(prev => prev.filter(p => p.id !== postId));
  }, [session, view]);

  // ───────────────────────────────────────────────
  // Auth handlers
  // ───────────────────────────────────────────────
  const handleLogin = async (p: AuthPayload) => {
    setAuthBusy(true); setAuthError(null);
    tlog('auth:login_start');
    try {
      const r = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: p.email, password: p.password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if ((data as any)?.needsVerification && (data as any)?.email) {
          setVerifyEmail((data as any).email || '');
          setPendingVerify({ email: (data as any).email, password: p.password });
          return;
        }
        throw new Error((data as any).error || 'Falha no login');
      }
      const env = data as AuthEnvelope;
      saveStoredSession(env.session, true);
      setSession(env.session);
      tlog('auth:login_ok');
    } catch (e: any) {
      setAuthError(e.message || 'Erro de login');
      tlog('auth:login_err', { msg: (e?.message || '').slice(0, 160) });
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRegister = async (p: AuthPayload) => {
    setAuthBusy(true); setAuthError(null);
    tlog('auth:register_start');
    try {
      // Gera username válido (3-20 chars, [a-z0-9_]). Se ficar curto/inválido,
      // adiciona sufixo aleatório pra atingir mínimo do backend.
      // Acentos viram a letra simples ("João" → "joao"; antes virava "joo").
      let baseUser = (p.name || p.email.split('@')[0]).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
      if (baseUser.length < 3) {
        baseUser = (baseUser + Math.random().toString(36).slice(2, 7)).slice(0, 20);
      }
      const r = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: p.email,
          password: p.password,
          displayName: p.name || p.email.split('@')[0],
          username: baseUser,
          // O @ sai do nome e a tela não tem campo para ele: se já existir, o
          // servidor escolhe um livre em vez de travar o cadastro (UX-27).
          usernameAuto: true,
          phone: p.wpp,
          // Convite pendente — vai junto pro servidor anexar ao redirect de verificação.
          inviteToken: (() => { try { return localStorage.getItem('leitura.pendingInviteToken') || undefined; } catch { return undefined; } })(),
          // Indicação — o código viaja no próprio cadastro. Esperar uma sessão
          // pra vincular não funciona: quem confirma o e-mail aqui costuma
          // logar dentro do app, que tem localStorage próprio e vazio.
          refCode: (() => { try { return localStorage.getItem('forjamente.pendingReferralCode') || undefined; } catch { return undefined; } })(),
          // De onde a pessoa veio (utm_ e referrer da primeira visita). Ver v2/origem.ts.
          origem: lerOrigem(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        // Mensagens amigáveis pros casos comuns
        if (r.status === 429) throw new Error(data.error || 'Muitas tentativas. Espere alguns minutos.');
        if (r.status === 409) throw new Error(data.error || 'Email ou usuário já em uso.');
        throw new Error(data.error || 'Falha no cadastro');
      }
      // Quem manda é o servidor (EXIGIR_VERIFICACAO_EMAIL). Hoje ele devolve a
      // sessão e a pessoa entra direto; se a exigência for religada, devolve
      // needsVerification e a tela de código aparece. As duas portas ficam
      // abertas de propósito — a decisão é de negócio e muda sem deploy.
      if ((data as any).needsVerification && (data as any).user?.email) {
        setVerifyEmail((data as any).user.email || '');
        setPendingVerify({ email: (data as any).user.email, password: p.password });
      } else if ((data as any).session) {
        // O nome digitado vale para a saudação desde o primeiro render — o
        // perfil só chega na carga seguinte (pente fino, UX-35).
        setNomeDoCadastro(String(p.name || '').trim() || null);
        saveStoredSession((data as any).session, true);
        setSession((data as any).session);
        // O "?cadastro=1" só servia para abrir o cadastro: dentro da conta ele
        // ficava na barra de endereço (pente fino, QA-15).
        try { if (/[?&]cadastro=1/.test(window.location.search)) window.history.replaceState(window.history.state, '', window.location.pathname); } catch {}
      } else {
        // Conta criada, mas a sessão automática não veio (rede, timing). Não é
        // erro do cadastro — a conta existe e a senha vale.
        setAuthError('Conta criada! Entre com seu e-mail e senha.');
      }
    } catch (e: any) {
      setAuthError(e.message || 'Erro no cadastro');
    } finally {
      setAuthBusy(false);
    }
  };

  const handleResendVerification = async (email: string): Promise<void> => {
    const r = await fetch(`${API_BASE}/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      throw new Error((data as any)?.error || 'Não foi possível reenviar.');
    }
  };

  const handleForgot = async (email: string) => {
    const r = await fetch(`${API_BASE}/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier: email }),
    });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      throw new Error((data as any).error || 'Falha ao solicitar recuperação');
    }
  };

  const handleLogout = async (opts: { semDesregistrar?: boolean } = {}) => {
    tlog('auth:logout');
    // SAIR DA CONTA TEM DE SOLTAR O APARELHO, nao so limpar a tela.
    //
    // Duas amarras nativas sobreviviam ao logout, e as duas mordem quando duas
    // contas usam o MESMO celular — o caso do teste, do casal, da familia:
    //
    //  · O RevenueCat continuava identificado como a conta anterior. A trava
    //    `configured` em revenuecat.ts faz `configureRevenueCat` virar no-op
    //    depois da primeira vez, e `loginRevenueCat`/`logoutRevenueCat` estavam
    //    IMPORTADOS e nunca chamados. Resultado: a segunda conta assinava pela
    //    loja e a compra era creditada na PRIMEIRA. Quem pagou continuava vendo
    //    o paywall; quem nao pagou ganhava o plano.
    //
    //  · O token de push (FCM) e do APARELHO, nao da conta. Ficando preso a
    //    conta anterior, a rota de registro respondia 409 para a segunda conta
    //    — para sempre. A tela dizia "tentar de novo", e tentar de novo dava o
    //    mesmo 409.
    //
    // Nenhuma das duas pode travar a saida: quem pediu para sair, sai. Por isso
    // as duas sao best-effort e o logout segue de qualquer jeito.
    try { await logoutRevenueCat(); } catch (e) { /* sair nao pode falhar */ }
    if (session && !opts.semDesregistrar) {
      try {
        await apiFetchWithSession(session, `${API_BASE}/push/fcm-deregister`, { method: 'POST' });
      } catch (e) { /* idem */ }
    }
    saveStoredSession(null);
    setSession(null);
    setProfile(null);
    setBooks([]);
    setStreak(null); setHeatmap(null); setAnalytics(null);
    setAchievements([]);
    setTab('home');
  };

  // Apple Guideline 5.1.1.v + LGPD — exclusão definitiva.
  // Chama DELETE /api/auth/me, e em sucesso limpa toda a sessão local + toast.
  const handleDeleteAccount = async () => {
    tlog('account:delete_start');
    if (!session) throw new Error('Sessão expirada — faça login de novo.');
    // O token do aparelho sai ANTES de a conta sumir: depois, a sessão já não
    // vale e o pedido levava 401 (pente fino, QA-13). Melhor esforço — o
    // servidor também apaga os tokens junto com a conta.
    try { await apiFetchWithSession(session, `${API_BASE}/push/fcm-deregister`, { method: 'POST' }); } catch (e) { /* segue */ }
    const res = await apiFetchWithSession(session, `${API_BASE}/auth/me`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error((data as any)?.error || 'Não foi possível excluir a conta.');
    }
    handleLogout({ semDesregistrar: true });
    toast.success('Conta excluída. Até qualquer dia.');
  };

  // ───────────────────────────────────────────────
  // Book actions
  // ───────────────────────────────────────────────
  const openBook = (b: BookV2) => setView({ kind: 'book', book: b });
  // composePage NÃO vai no View (o sync URL→view o apagaria); vive num estado dedicado,
  // consumido pelo BookStudyScreen no mount. Sempre seteado (page ou null) a cada abertura.
  const openBookStudy = (b: BookV2, page: number | null = null, naTrilha = false, contexto: ContextoDaNota | null = null) => { setStudyCompose(page); setStudyContexto(page != null ? contexto : null); setStudyTrilha(naTrilha); setView({ kind: 'book-study', book: b }); };
  // Já no mapa deste livro: fica (o registro novo recarrega a tela e a névoa abre ali mesmo).
  // Interface nova: Travessia e Caderno são as duas faces do livro. Trocar de
  // face SUBSTITUI a tela — o voltar sai do livro, para onde a pessoa estava.
  const trocarFace = (b: BookV2, face: FaceDoLivro, pagina: number | null = null, contexto: ContextoDaNota | null = null) => {
    if (face === 'caderno') { setStudyCompose(pagina); setStudyContexto(pagina != null ? contexto : null); setStudyTrilha(false); setView({ kind: 'book-study', book: b }, { trocar: true }); }
    else setView({ kind: 'travessia', book: b }, { trocar: true });
  };
  const openTravessia = (b: BookV2) => { if (view.kind === 'travessia' && (view as any).book?.id === b.id) return; setView({ kind: 'travessia', book: b }); };
  // A Home: "Ontem você levou" abre o mapa já com a folha daquele insight/desafio.
  // (com o bookId: a sincronia da URL reconhece a mesma view e não descarta o item)
  const abrirItemDaTravessia = (b: BookV2, item: string) => setView({ kind: 'travessia', book: b, bookId: b.id, item });
  // O convite "nota nova" leva ao mapa quando o livro tem Travessia; senão, ao caderno.
  const abrirTrilha = (b: BookV2) => (travessias[b.id] ? openTravessia(b) : openBookStudy(b, null, true));
  // A ficha do Explorar novo: "Começar a travessia". O servidor põe o livro da
  // estante em leitura (ou diz o que criar); daí a estante recarrega e abre o mapa.
  const comecarTravessiaDaFicha = async (slug: string, acesso: boolean) => {
    if (!session) return;
    const r = await api.comecarTravessia(session, slug);
    let id = r.book_id || null;
    if (r.criar) {
      const novo = await api.createBook(session, {
        ...r.criar, status: 'reading', startedAt: new Date().toISOString().slice(0, 10),
        coverSourceUrl: r.criar.coverSourceUrl ? new URL(r.criar.coverSourceUrl, window.location.origin).href : null,
      });
      id = novo.id;
    }
    const lib: BookV2[] = await api.getLibrary(session);
    setBooks(lib);
    atualizarMentorNovas();
    const b = lib.find((x) => x.id === id);
    if (!b) return;
    if (acesso) setView({ kind: 'travessia', book: b }); else openBook(b);
  };
  const fecharEntrada = () => {
    setEntradaAberta(false);
    tutorialJaMostradoRef.current = true;
    try { localStorage.setItem('forjamente.entrada-curta', '1'); } catch {}
    // Em sequência: o servidor lê e regrava o tutorials_state inteiro.
    (async () => {
      try { await api.setTutorialState(session, 'entrada', { seen: true, enabled: false }); } catch {}
      try { await api.setTutorialState(session, 'onboarding', { seen: true, enabled: false }); } catch {}
      try { await api.markTutorialSeen(session, true); } catch {}
    })();
    setProfile((p: any) => p ? { ...p, tutorial_seen: true, tutorials_state: { ...(p.tutorials_state || {}), entrada: { seen: true, enabled: false }, onboarding: { seen: true, enabled: false } } } : p);
  };
  const convidarAmigos = async () => {
    const handle = (profile as any)?.username;
    const url = handle ? `https://forjamente.com.br/@${handle}` : 'https://forjamente.com.br';
    const text = 'Estou lendo no Forjamente. Bora ler junto?';
    try {
      const mod: any = await import('@capacitor/share').catch(() => null);
      if (mod?.Share?.share) { await mod.Share.share({ title: 'Forjamente', text, url, dialogTitle: 'Convidar amigos' }); return; }
    } catch {}
    try { if ((navigator as any).share) { await (navigator as any).share({ title: 'Forjamente', text, url }); return; } } catch {}
    openExternalUrl(url);
  };
  // Soma enquanto for o mesmo livro: o ponto de partida e o registro logo
  // depois, na mesma folha, viram um convite só.
  const avisarTrilha = (bookId: string, n: any) => {
    if (!(Number(n) > 0) || mentorLivroAtivoAgora() !== true) return;
    setAvisoTrilha(a => ({ bookId, n: (a && a.bookId === bookId ? a.n : 0) + Number(n) }));
    atualizarMentorNovas();
  };
  // Abre a Página do Livro por id: se for meu livro (na lista) abre com os dados
  // completos (tracker do dono na hora); senão abre mínimo {id} e a própria
  // BookDetailScreen busca os dados e renderiza o estado visitante.
  // ABRIR UM LIVRO PELO ID, inclusive um que o acervo ainda nao conhece.
  //
  // O BUG DO WILMER (25/09/2026): adicionou um livro pelo Explorar, foi ao
  // perfil, tocou no livro — e a tela ficou "Carregando..." para sempre.
  //
  // O caminho inteiro: `querLer` gravava o livro no servidor mas nao mexia no
  // acervo do cliente; aqui o `find` nao achava e a tela era aberta com um
  // livro DE MENTIRA, so com o id; e a Pagina do Livro assume, escrito no
  // proprio comentario dela, que "para o DONO o livro vem da prop (lista
  // completa)". Sem titulo, ela caia num "Carregando..." que nada resolvia,
  // porque nao havia mais nada para carregar.
  //
  // Agora, quando o acervo nao conhece o livro, ele e BUSCADO antes de abrir.
  // A tela abre com o livro inteiro, na mesma forma dos outros.
  const openBookById = async (bookId: string) => {
    const full = books.find(b => b.id === bookId);
    if (full) { setView({ kind: 'book', book: full }); return; }
    try {
      const lib = await api.getLibrary(session);
      setBooks(lib);
      const achado = (lib || []).find((b: any) => b.id === bookId);
      if (achado) { setView({ kind: 'book', book: achado }); return; }
    } catch (e) { /* sem rede: a tela abaixo ainda tenta pelo /page */ }
    // Nao e do acervo (livro de outra pessoa, link recebido): a propria tela
    // busca pelo `/page` e sabe desenhar o visitante.
    setView({ kind: 'book', book: { id: bookId } as any });
  };
  // O caderno de um livro pelo id (aba Cadernos do Círculo): o livro pode ter
  // acabado de entrar na estante, então o acervo é relido se não o conhece.
  const openBookStudyById = async (bookId: string) => {
    let full = books.find(b => b.id === bookId);
    if (!full) {
      try {
        const lib = await api.getLibrary(session);
        setBooks(lib);
        full = (lib || []).find((b: any) => b.id === bookId);
      } catch (e) { /* sem rede: fica na página do Mentor */ }
    }
    if (full) openBookStudy(full);
  };
  const openProfile = (uid: string) => { if (uid && uid !== profile?.id) setView({ kind: 'user', userId: uid } as any); };
  const refreshProfile = React.useCallback(async () => { try { const p = await api.getProfile(session); setProfile(p as any); } catch (e) {} }, [session]);

  // O LAÇO DA VOLTA DO MERCADO PAGO (ver `voltaDoMp`): a cada 3 s, por até
  // 1 min, relê o perfil; para assim que o plano pago aparece.
  useEffect(() => {
    if (voltaDoMp !== 'conferindo' || !session) return undefined;
    let vivo = true;
    let tentativas = 0;
    const conferir = async () => {
      tentativas++;
      try {
        const p: any = await api.getProfile(session);
        if (!vivo) return;
        const pago = !!p?.plan && p.plan !== 'free'
          && (!p.plan_expires_at || new Date(p.plan_expires_at).getTime() > Date.now());
        if (pago) {
          setProfile(p);
          setVoltaDoMp(null);
          toast.success('Pagamento confirmado. Boa leitura!');
          return;
        }
      } catch (e) { /* rede: tenta no próximo giro */ }
      if (!vivo) return;
      if (tentativas >= 20) { setVoltaDoMp('demorou'); return; }
      relogio = window.setTimeout(conferir, 3000);
    };
    let relogio = window.setTimeout(conferir, 1500);
    return () => { vivo = false; window.clearTimeout(relogio); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voltaDoMp, session]);
  const closeBook = () => goBack();
  const [logModo, setLogModo] = useState<'linear' | 'foco'>('linear');
  const startLog = (b: BookV2, modo: 'linear' | 'foco' = 'linear') => {
    // O livro de quem chegou por link (/livro/:id, notificação, F5) é só { id }
    // até a estante carregar. A folha de registro precisa do livro inteiro
    // (título, páginas, onde parou): sem ele, a capa derrubava o app (tela
    // branca) e a conta das páginas partia do nada (pente fino, MOB-19).
    const inteiro = books.find((x) => x.id === b.id) || b;
    if (!(inteiro as any).title && session) {
      api.getLibrary(session).then((lib: any) => {
        setBooks(lib);
        const achado = (lib || []).find((x: any) => x.id === b.id);
        if (achado) { setLogBook(achado); setLogModo(modo); setLogOpen(true); }
      }).catch(() => {});
      return;
    }
    setLogBook(inteiro); setLogModo(modo); setLogOpen(true);
  };

  // QA-20: sessão de foco que ficou em andamento quando o app fechou. Lida UMA
  // vez, quando a estante chega (precisa do livro). Some sozinha depois de 12 h.
  const [sessaoFocoPendente, setSessaoFocoPendente] = useState<SessaoFoco | null>(null);
  const [registrarSessaoSalva, setRegistrarSessaoSalva] = useState(false);
  const focoConferido = useRef(false);
  useEffect(() => {
    if (focoConferido.current || !books.length) return;
    focoConferido.current = true;
    const s = lerSessaoFoco();
    if (!s) return;
    if (!books.some((b) => b.id === s.bookId)) { descartarSessaoFoco(); return; }
    setSessaoFocoPendente(s);
  }, [books]);

  // Protocolo do Hábito — recarrega o estado do dia. Toda ação que possa mudar o
  // estágio (ler, selar contrato, responder o SRBAI) chama isto depois.
  const recarregarHabito = React.useCallback(async () => {
    try { const h = await habitoApi.hoje(session); setHabito(h); return h; }
    catch { return null; }
  }, [session]);

  // A sessão sem decisões: um toque na Home abre o livro na página certa, sem
  // nenhuma tela de escolha no meio (invariante 3).
  const [sessaoAberta, setSessaoAberta] = useState(false);
  // O retorno pode ser adiado ("agora não dá"), mas só até a próxima abertura —
  // o reconhecimento não pode ser perdido por um fechamento de aba.
  const [retornoAdiado, setRetornoAdiado] = useState(ABRIU_PELO_LINK_DO_MENTOR);
  // "Depois" tem que persistir. Como estado de sessão, estas duas telas cheias
  // reapareciam a cada abertura do app, para sempre, para quem nunca respondia.
  // O app já sabe fazer isso: leitura.trocaOferecida.<id> segue o mesmo padrão.
  const adiadoAte = (chave: string) => {
    try {
      const v = localStorage.getItem(chave);
      return !!v && Date.now() < Number(v);
    } catch { return false; }
  };
  const adiarPor = (chave: string, horas: number) => {
    try { localStorage.setItem(chave, String(Date.now() + horas * 3600000)); } catch {}
  };
  const [medicaoAdiada, setMedicaoAdiada] = useState(() => ABRIU_PELO_LINK_DO_MENTOR || adiadoAte('leitura.srbaiAdiado'));
  const [formaturaAdiada, setFormaturaAdiada] = useState(() => ABRIU_PELO_LINK_DO_MENTOR || adiadoAte('leitura.formaturaAdiada'));
  const [trocarAberto, setTrocarAberto] = useState(false);
  const [trocandoModo, setTrocandoModo] = useState(false);
  const [editarContratoAberto, setEditarContratoAberto] = useState(false);

  // A chave dos dois mundos. Quem ainda não tem programa (dispensou ou nunca
  // começou) cai no fluxo de preparo em vez de só trocar um campo — não existe
  // "entrar no protocolo" sem escolher livro e selar contrato.
  // As acoes de estante, num pacote so — a biblioteca do perfil usa todas, e
  // manter cada uma como prop solta faria a assinatura crescer sem parar.
  const estantesDoPerfil = React.useMemo(() => ({
    criar: async (patch: any) => {
      if (!session) return;
      const nova = await api.createShelf(session, patch.name, patch.emoji || null);
      setShelves(prev => [...prev, nova]);
    },
    renomear: async (id: string, patch: any) => {
      if (!session) return;
      const sh = await api.updateShelf(session, id, patch);
      setShelves(prev => prev.map(x => x.id === id ? { ...x, ...sh } : x));
    },
    excluir: async (id: string) => {
      if (!session) return;
      await api.deleteShelf(session, id);
      setShelves(prev => prev.filter(x => x.id !== id));
      // Os livros continuam existindo — some so o vinculo.
      setBooks(prev => prev.map(b => ({ ...b, shelfIds: (b.shelfIds || []).filter((sid: string) => sid !== id) })));
    },
    reordenar: async (ids: string[]) => {
      if (!session) return;
      // Otimista: a lista ja foi reordenada na mao de quem arrastou.
      setShelves(prev => ids.map(id => prev.find(x => x.id === id)!).filter(Boolean));
      try { const r = await api.reorderShelves(session, ids); if (r?.shelves) setShelves(r.shelves); } catch {}
    },
    vincular: async (shelfId: string, entrar: string[], sair: string[]) => {
      if (!session) return;
      await Promise.all([
        ...entrar.map(bid => api.addBookToShelf(session, shelfId, bid)),
        ...sair.map(bid => api.removeBookFromShelf(session, shelfId, bid)),
      ]);
      setBooks(prev => prev.map(b => {
        if (entrar.includes(b.id)) return { ...b, shelfIds: Array.from(new Set([...(b.shelfIds || []), shelfId])) };
        if (sair.includes(b.id)) return { ...b, shelfIds: (b.shelfIds || []).filter((sid: string) => sid !== shelfId) };
        return b;
      }));
      setShelves(prev => prev.map(sh => sh.id === shelfId
        ? { ...sh, count: Math.max(0, (sh.count || 0) + entrar.length - sair.length) } : sh));
    },
  }), [session]);

  // PARKED (12/09/2026) — a chave Leitor/Protocolo saiu da Home na fusão das
  // duas abas. A troca de modo continua existindo no servidor; esta função
  // fica aqui para quando ela voltar por outra porta (tela do Programa).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const trocarModoDoHabito = React.useCallback(async (modo: 'leitor' | 'protocolo') => {
    if (modo === 'protocolo' && (!habito?.inscricao || habito.estado === 'DISPENSADO'
        || !habito.inscricao.seladoEm)) {
      setPreparoOpen(true);
      return;
    }
    setTrocandoModo(true);
    try {
      await habitoApi.trocarModo(session, modo);
      await recarregarHabito();
      await loadAllData();
    } catch (e) {
      toast.error('Não consegui trocar de modo. Tente de novo.');
    } finally { setTrocandoModo(false); }
  }, [habito, session, recarregarHabito, loadAllData]);
  const abrirSessaoDoProtocolo = React.useCallback(() => {
    if (habito?.livro) setSessaoAberta(true);
  }, [habito]);

  // A permissão de abandonar é oferecida ATIVAMENTE por volta da página 50 — e
  // some depois que a pessoa recusa uma vez, para não virar cutucada.
  const ofereceTrocaDeLivro = React.useMemo(() => {
    const l = habito?.livro;
    if (!l || l.paginaAtual < 50) return false;
    if (l.totalPaginas && l.paginaAtual > l.totalPaginas * 0.7) return false; // quase no fim, não faz sentido
    try { return localStorage.getItem(`leitura.trocaOferecida.${l.id}`) !== '1'; } catch { return true; }
  }, [habito]);

  // O tutorial de perfil só aparece quando a pessoa vai para uma superfície
  // social — é lá que ter nome, foto e @ passa a servir para alguma coisa.
  useEffect(() => {
    if (!perfilPendente) return;
    if (!['explore', 'me', 'clubs'].includes(tab)) return;
    if (preparoOpen || sessaoAberta || celebracao) return;
    setPerfilPendente(false);
    if (interfaceNovaRef.current) return; // a interface nova não tem o tutorial antigo
    if (!tutorialJaMostradoRef.current) setTutorialPerfilPedido(true);
  }, [perfilPendente, tab, preparoOpen, sessaoAberta, celebracao]);

  // O TUTORIAL ESPERA A TELA FICAR LIVRE (pente fino, UX-28). Ele abria por
  // cima do que a pessoa estava fazendo — depois da 1ª leitura, cobriu o
  // caderno e engoliu o toque em "Guardar". Agora o pedido fica guardado e o
  // tutorial só abre numa aba principal, sem folha, editor, sessão, festa ou
  // preparo abertos (conferido duas vezes seguidas). Fechado, não volta na
  // mesma sessão, mesmo que a marca de "visto" ainda não tenha chegado.
  const telaLivreRef = useRef(false);
  telaLivreRef.current = view.kind === 'tab' && !preparoOpen && !entradaAberta && !sessaoAberta && !celebracao
    && !achievement && !onboardingOpen && !logOpen && !addOpen;
  useEffect(() => {
    if (!tutorialPerfilPedido) return;
    let livreAntes = false;
    const t = setInterval(() => {
      const livre = telaLivreRef.current && !temFolhaAberta()
        && (typeof document === 'undefined' || document.visibilityState === 'visible');
      if (livre && livreAntes) {
        clearInterval(t);
        setTutorialPerfilPedido(false);
        if (interfaceNovaRef.current) return; // idem: só a interface de sempre
        if (!tutorialJaMostradoRef.current) { tutorialJaMostradoRef.current = true; setOnboardingOpen(true); }
      }
      livreAntes = livre;
    }, 600);
    return () => clearInterval(t);
  }, [tutorialPerfilPedido]);

  // Quando o preparo termina com "abrir o livro agora", o livro acabou de ser
  // criado — esperamos ele aparecer na estante antes de abrir a sessão.
  const [livroPendente, setLivroPendente] = useState<string | null>(null);
  useEffect(() => {
    if (!livroPendente) return;
    const b = books.find((x: any) => x.id === livroPendente);
    if (b) { setLivroPendente(null); setSessaoAberta(true); }
  }, [livroPendente, books]);

  const handleAddBook = async (b: any) => {
    if (!session) return;
    // Respeita o status escolhido no formulário (Ler agora / Ler depois / Já li).
    // 'Ler agora' (reading) é o único que conta no cap de leituras simultâneas.
    const created = await api.createBook(session, { ...b, status: b.status || 'reading' });
    // FP3 — atribui o livro recém-criado às estantes escolhidas no form
    const shelfIds: string[] = Array.isArray(b.shelfIds) ? b.shelfIds : [];
    let createdFinal: any = created;
    if (shelfIds.length && (created as any)?.id) {
      // SO CONTA A ESTANTE QUE DE FATO PEGOU.
      //
      // Antes: `Promise.all(... .catch(() => {}))` — o erro de cada vinculo era
      // engolido, e em seguida TODAS as estantes escolhidas eram marcadas como
      // aplicadas. O livro aparecia na estante e, ao abri-la, nao estava la: o
      // vinculo nunca existiu no banco, e a marcacao sumia na proxima recarga
      // sem explicacao nenhuma.
      //
      // A tela de EDICAO ja fazia isso direito (book-actions.tsx desfaz e avisa
      // quando falha). So o caminho de CRIACAO nao tinha a rede.
      const res = await Promise.allSettled(
        shelfIds.map((sid) => api.addBookToShelf(session, sid, (created as any).id)),
      );
      const entraram = shelfIds.filter((_, i) => res[i].status === 'fulfilled');
      createdFinal = { ...created, shelfIds: entraram };
      if (entraram.length) {
        setShelves(prev => prev.map(s => entraram.includes(s.id) ? { ...s, count: (s.count || 0) + 1 } : s));
      }
      if (entraram.length < shelfIds.length) {
        window.alert(entraram.length
          ? 'O livro entrou, mas nao consegui coloca-lo em todas as estantes escolhidas.'
          : 'O livro entrou, mas nao consegui coloca-lo na estante escolhida.');
      }
    }
    setBooks(prev => [createdFinal, ...prev]);
  };

  // FP5: registrar um TRECHO (da/até). Não move o cursor linear; soma páginas
  // (entram no esforço/streak via sessão) e atualiza o mapa de leitura.
  const handleSaveSegment = async (bookId: string, from: number, to: number, minutes?: number | null, label?: string | null) => {
    if (!session) return;
    setLeiturasSalvas((n) => n + 1);
    const res = await api.logSegment(session, bookId, from, to, minutes, label ?? null);
    const updated = res.book; // current_page inalterado; sessions já traz o trecho novo
    setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...updated } : b));
    if (view.kind === 'book' && view.book.id === bookId) {
      setView({ kind: 'book', book: { ...view.book, ...updated } });
    }
  };

  // LIVRO APAGADO EM OUTRO APARELHO (pente fino, QA-30): sai da lista, as
  // folhas dele fecham e a pessoa volta à estante, com o motivo. Antes o
  // registro dava "Livro não encontrado" com o livro ainda na tela, e reabrir
  // falava em "perfil privado".
  const livroSumiu = (bookId: string) => {
    setBooks(prev => prev.filter(b => b.id !== bookId));
    setLogOpen(false);
    setBookActions((b: any) => (b && b.id === bookId ? null : b));
    const v: any = view;
    if ((v.kind === 'book' && v.book?.id === bookId) || ((v.kind === 'book-study' || v.kind === 'travessia') && v.book?.id === bookId)) {
      setView({ kind: 'library' } as any, { trocar: true });
    }
    toast.info('Esse livro foi removido em outro aparelho.');
  };

  const handleSaveLog = async (bookId: string, newPage: number, minutes?: number | null, opts: { semCelebracao?: boolean } = {}) => {
    if (!session) return;
    let res: any;
    try { res = await api.logReadingToPage(session, bookId, newPage, minutes); }
    catch (e: any) { if (e?.status === 404) livroSumiu(bookId); throw e; }
    // O servidor reconheceu o mesmo envio (outra aba, reenvio da rede): a
    // leitura já está gravada uma vez. Só alinha o livro, sem somar a sessão
    // nem comemorar de novo.
    if (res?.repetido) {
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...res.book } : b));
      if (view.kind === 'book' && view.book.id === bookId) setView({ kind: 'book', book: { ...view.book, ...res.book } });
      return;
    }
    setLeiturasSalvas((n) => n + 1);
    avisarTrilha(bookId, res.trilhaNovas);
    const updated = res.book;
    const prevBook = books.find(b => b.id === bookId);
    setBooks(prev => prev.map(b => b.id === bookId ? { ...updated, sessions: [...(b.sessions || []), { date: today, pages: newPage - (prevBook?.read || 0) }] } : b));

    if (view.kind === 'book' && view.book.id === bookId) {
      setView({ kind: 'book', book: { ...view.book, read: updated.read, currentPage: updated.currentPage, status: updated.status } });
    }

    // Se o livro lido e vinculado a um clube ativo na tela, refaz o getClub
    // pra atualizar yourPage/collectivePct/ranking no card e na pagina.
    const fromClubId = (updated as any).fromClubId || prevBook?.fromClubId;
    if (fromClubId && activeClub?.id === fromClubId) {
      api.getClub(session, fromClubId).then(setActiveClub).catch(() => {});
    }

    // Achievement triggers — usar SEMPRE o livro fresco retornado pelo backend
    // como fonte de verdade da dailyGoal (evita state stale do cliente).
    const delta = newPage - (prevBook?.read || 0);

    // Livros com o livro atualizado já aplicado (fresh snapshot)
    const freshBooks = books.map(b => b.id === bookId ? { ...b, ...updated } : b);

    // Meta do livro individual (do backend)
    const bookGoal = Number(updated?.dailyGoal) || 0;
    const pagesTodayForBook = (prevBook?.sessions || []).filter(ss => ss.date === today).reduce((t, ss) => t + (ss.pages || 0), 0) + delta;

    // Unidade do livro recém-registrado. A "meta do dia" é calculada DENTRO da
    // mesma unidade — nunca somamos páginas + capítulos + posições (seria 10+3=13
    // sem sentido). Mantém a celebração consistente com o anel do hero, que também
    // é filtrado por unidade.
    const loggedUnit = (prevBook as any)?.progressUnit || (updated as any)?.progressUnit || 'pages';

    // Meta total do dia = soma das daily_goal dos livros em leitura NA MESMA UNIDADE
    const dayTotalGoalLocal = freshBooks
      .filter(b => b.status === 'reading' && (((b as any).progressUnit) || 'pages') === loggedUnit)
      .reduce((s, b) => s + (Number(b.dailyGoal) || 0), 0);

    const pagesTodayBefore = books
      .filter(bk => (((bk as any).progressUnit) || 'pages') === loggedUnit)
      .reduce((s, bk) => s + (bk.sessions || []).filter(ss => ss.date === today).reduce((t, ss) => t + (ss.pages || 0), 0), 0);
    const newDayTotal = pagesTodayBefore + delta;

    // Dispara apenas se a meta > 0 e foi efetivamente atingida pela 1ª vez hoje
    const hitBookGoal = bookGoal > 0 && pagesTodayForBook >= bookGoal && !dailyDoneFor[`${today}:${bookId}`];
    const dayKey = `day:${today}`;
    const hitDayGoal = dayTotalGoalLocal > 0 && newDayTotal >= dayTotalGoalLocal && !dailyDoneFor[dayKey];
    const isFinished = res.finished;
    console.log('[achievement] bookGoal=', bookGoal, 'pagesTodayForBook=', pagesTodayForBook, 'dayTotalGoal=', dayTotalGoalLocal, 'newDayTotal=', newDayTotal, 'hits:', { hitBookGoal, hitDayGoal, isFinished });

    // Protocolo do Hábito: quem está no programa recebe a celebração do
    // protocolo, não a das metas antigas. Duas comemorações empilhadas se
    // anulam — e o que vence o dia aqui é o mínimo ridículo, não a soma das
    // metas por livro. Terminar um livro continua valendo festa própria.
    // 'formado' entra junto: quem terminou o programa não volta a receber o
    // confete da meta antiga, que o próprio protocolo criou por compatibilidade.
    const noProtocolo = ['ativo', 'formado'].includes(habito?.inscricao?.status || '');
    // Qualquer leitura registrada muda o estado do dia, inclusive as que entram
    // pelos caminhos antigos (estante, card do clube, sheet de registro).
    if (habito) recarregarHabito();

    // UMA comemoração por salvamento (auditoria de 31/10, achados 2 e 5): a
    // mais forte vence — livro terminado > protocolo > meta do livro/dia — e a
    // da própria folha ("+N páginas") só aparece quando nenhuma delas vem. A
    // folha recebe a decisão na resposta (festa) e não festeja por cima.
    const festa = !!isFinished || noProtocolo || hitBookGoal || hitDayGoal;
    // A folha do registro espera a festa do App (pente fino, UX-06): enquanto
    // o App mostra a festa, a folha some (sem perder o estado) e volta com o
    // convite "Anote sobre o que você leu" depois. A festa entra logo (o
    // tempo da folha sair), não depois de 1,2 s de tela parada.
    if (festa) setPausaFolha(true);
    setTimeout(async () => {
      try {
      if (isFinished) {
        setAchievement({ kind: 'finished', book: updated });
      } else if (noProtocolo) {
        // A celebração do dia é do protocolo — mas ela SÓ disparava pelo sheet
        // dele. Quem registrava pela estante ou pelo card do clube perdia a
        // antiga e não ganhava a nova: silêncio absoluto. Agora o caminho
        // antigo também celebra, a não ser que o próprio sheet já vá fazê-lo.
        if (!opts.semCelebracao) {
          const h = await recarregarHabito();
          if (h && (h.estado === 'DIA_VENCIDO' || h.leuHoje)) {
            const ret = h.retorno || null;
            if (ret) { try { await habitoApi.marcarLapsoVisto(session, ret.id, 'celebracao'); } catch {} }
            setCelebracao({
              dia: h.dia ?? null,
              paginas: Math.max(1, delta),
              primeiroDia: (h.dia ?? 0) === 1 && !ret,
              recorde: h.sequencia.atual > 1 && h.sequencia.atual >= h.sequencia.melhor,
              retorno: ret ? { dias: ret.dias, total: h.retornos?.total } : null,
            });
          }
        }
      } else if (hitBookGoal) {
        // O número é o que ela leu HOJE neste livro, não a meta.
        setAchievement({ kind: 'book', book: updated, data: { pagesToday: pagesTodayForBook } });
        // A meta do dia batida no MESMO salvamento fica comemorada por esta:
        // não sobe sozinha no próximo registro.
        setDailyDoneFor(prev => ({ ...prev, [`${today}:${bookId}`]: true, ...(hitDayGoal ? { [dayKey]: true } : {}) }));
      } else if (hitDayGoal) {
        const booksRead = new Set(books
          .filter(bk => (((bk as any).progressUnit) || 'pages') === loggedUnit)
          .flatMap(bk => (bk.sessions || []).filter(ss => ss.date === today).map(() => bk.id))).size || 1;
        setAchievement({ kind: 'day', data: { pagesToday: newDayTotal, books: Math.max(1, booksRead), unit: loggedUnit } });
        setDailyDoneFor(prev => ({ ...prev, [dayKey]: true }));

        // Primeira meta batida + push ainda inativo + ainda não pediu antes:
        // dispara prompt amigável sugerindo ativar lembretes.
        const promptKey = 'leitura.pushPromptShownV1';
        if (!pushStatus.subscribed && !localStorage.getItem(promptKey)) {
          localStorage.setItem(promptKey, '1');
          // Espera o achievement modal fechar (confetti) antes de mostrar.
          setTimeout(() => setPushPromptOpen(true), 4500);
        }
      }
      // Reload stats + library + clubes + clube ativo (sincroniza tudo após registrar
      // leitura — yourPage no marco, collectivePct no clube, sessions no Home).
      if (session) {
        const [s, h, an, ach, lib] = await Promise.all([
          api.getStreak(session).catch(() => null),
          api.getHeatmap(session, 26).catch(() => null),
          api.getAnalytics(session).catch(() => null),
          api.getAchievements(session).catch(() => ({ achievements: [], newlyUnlocked: [] })),
          api.getLibrary(session).catch(() => null),
        ]);
        setStreak(s); setHeatmap(h); setAnalytics(an); setAchievements(ach.achievements);
        if (lib) setBooks(lib);
        loadClubs();
        // Se estamos olhando um clube/estudo, recarrega o detalhe pra atualizar
        // yourPage e collectivePct.
        const activeClubId = (view.kind === 'club-inner' || view.kind === 'estudo') ? (view as any).clubId : null;
        if (activeClubId) {
          api.getClub(session, activeClubId).then(setActiveClub).catch(() => {});
        }
      }
      } finally { setPausaFolha(false); }
    }, festa ? 350 : 1200);
    return { festa };
  };

  const handleStatusChange = async (id: string, status: BookStatusDesign) => {
    if (!session) return;
    const dbStatus = status === 'reading' ? 'lendo' : status === 'finished' ? 'lido' : status === 'paused' ? 'parado' : 'proximo';
    // Roteia pelo PATCH v2 (api.updateBook): é o único caminho que (a) aceita o
    // status 'parado' e (b) aplica o cap de 2 'lendo' ao mover PRA lendo. O legado
    // api.setBookStatus NÃO tinha gate (dava pra furar o cap pelo menu) e rejeitava
    // 'parado'. Sem cap ao sair de 'lendo' / mover pra proximo/parado/lido.
    try {
      await api.updateBook(session, id, { status });
    } catch (e: any) {
      const msg = (e?.message || '') + '';
      if (msg.includes('plan_limit') || msg.includes('plan_required')) {
        try { toast.error('Você já lê 2 livros ao mesmo tempo. Pause ou termine um antes de começar outro.'); } catch {}
      } else {
        try { toast.error('Não foi possível mudar o status agora.'); } catch {}
      }
      return;
    }
    // UX: marcar "Lido" deve COMPLETAR o progresso — antes um livro "Terminado"
    // seguia mostrando "50/300 · 17%". Sincroniza a posição pro total (sem somar
    // como leitura de hoje). Só quando ainda não está completo.
    let finishedPatch: any = {};
    if (status === 'finished') {
      const bk = books.find(b => b.id === id);
      if (bk && (bk.pages || 0) > 0 && (bk.read || 0) < bk.pages) {
        try { await api.syncBookPage(session, id, bk.pages); finishedPatch = { read: bk.pages }; } catch {}
      }
    }
    setBooks(prev => prev.map(b => b.id === id ? { ...b, status, rawStatus: dbStatus, ...finishedPatch } : b));
    if (view.kind === 'book' && view.book.id === id) {
      setView({ kind: 'book', book: { ...view.book, status, rawStatus: dbStatus, ...finishedPatch } });
    }
  };

  const handleDeleteBook = async (id: string) => {
    if (!session) return;
    if (!window.confirm('Remover este livro permanentemente?')) return;
    // SO SOME DA LISTA SE O SERVIDOR TIVER APAGADO.
    //
    // O `catch { /* fallback */ }` engolia a falha e o livro era removido da
    // tela de qualquer jeito. Num celular — 4G que oscila, app suspenso no meio
    // do pedido, token vencido — isso vira o pior tipo de defeito: a pessoa ve
    // "removido", confia, e minutos depois o livro REAPARECE sozinho, porque a
    // proxima leitura da biblioteca traz do banco o que nunca saiu de la.
    //
    // Fingir sucesso nao poupa ninguem do erro: so adia o erro para um momento
    // em que ele nao faz mais sentido nenhum.
    try {
      await api.deleteBook(session, id);
    } catch (e: any) {
      window.alert(e?.message || 'Nao consegui remover o livro. Tente de novo.');
      return;
    }
    setBooks(prev => prev.filter(b => b.id !== id));
    closeBook();
  };

  const handleSyncPage = async (bookId: string, page: number) => {
    if (!session) return;
    const res = await api.syncBookPage(session, bookId, page);
    avisarTrilha(bookId, res.trilhaNovas);
    const updated = res.book;
    setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...updated } : b));
    // A folha de registro guarda uma foto do livro: sem isto ela seguia
    // contando "da p. 0" depois de a pessoa dizer onde está.
    setLogBook(lb => (lb && lb.id === bookId ? { ...lb, ...updated } : lb));
    if (view.kind === 'book' && view.book.id === bookId) {
      setView({ kind: 'book', book: { ...view.book, ...updated } });
    }
    // Mesma logica do handleSaveLog: atualiza clube ativo se o livro pertence a ele.
    const fromClubId = (updated as any).fromClubId || books.find(b => b.id === bookId)?.fromClubId;
    if (fromClubId && activeClub?.id === fromClubId) {
      api.getClub(session, fromClubId).then(setActiveClub).catch(() => {});
    }
  };

  // Corrigir a posição inicial de um livro de CLUBE pelo kebab do card no Home.
  // Garante que o espelho pessoal exista (em group-read o membro pode ainda não
  // ter começado a ler → cria via startReadingClubBook, SEM abrir o log) e então
  // sincroniza a posição via handleSyncPage (não conta como leitura de hoje).
  const handleClubSyncPage = async (clubId: string, page: number) => {
    if (!session) return;
    const c: any = clubs.find((x: any) => x.id === clubId);
    let mirror: any = null;
    if (c?.mode === 'shared-habit') {
      // Avulso: o card mostra o livro ATIVO do membro (club.myBook). Sincroniza ESSE
      // — não um livro qualquer da fila com o mesmo from_club_id (pode haver vários).
      const myBookId = c?.myBook?.id;
      mirror = myBookId ? (books.find(b => b.id === myBookId) || null) : null;
    } else {
      // group-read: espelho único por from_club_id; cria sob demanda se ainda não
      // existe (membro entrou no meio do livro), SEM logar leitura.
      mirror = books.find(b => (b as any).fromClubId === clubId) || null;
      if (!mirror) {
        const r = await api.startReadingClubBook(session, clubId);
        const personalBook = r.book;
        setBooks(prev => prev.find(b => b.id === personalBook.id)
          ? prev.map(b => b.id === personalBook.id ? { ...b, ...personalBook } : b)
          : [personalBook, ...prev]);
        mirror = personalBook;
      }
    }
    if (!mirror) throw new Error('Não foi possível localizar seu livro neste clube.');
    await handleSyncPage(mirror.id, page);
  };

  // ───────────────────────────────────────────────
  // Insights
  // ───────────────────────────────────────────────
  const loadInsights = useCallback(async () => {
    if (!session) return;
    setInsightsLoading(true); setInsightsError(null);
    try {
      const r = await api.getInsights(session);
      setInsights(r);
    } catch (e: any) {
      setInsightsError(e.message || 'Erro ao gerar insights');
    } finally {
      setInsightsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (view.kind === 'report' && !insights && !insightsLoading) loadInsights();
  }, [view, insights, insightsLoading, loadInsights]);

  const applyGoalToBook = async (bookId: string, pages: number) => {
    if (!session) return;
    try {
      await api.updateBook(session, bookId, { dailyGoal: pages });
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, dailyGoal: pages } : b));
    } catch (e: any) {
      window.alert('Erro ao aplicar meta: ' + (e.message || e));
    }
  };

  const applyAllGoals = async (goals: { bookId: string; pages: number }[]) => {
    if (!session) return;
    try {
      await Promise.all(goals.map(g => api.updateBook(session, g.bookId, { dailyGoal: g.pages })));
      const byId = new Map(goals.map(g => [g.bookId, g.pages]));
      setBooks(prev => prev.map(b => byId.has(b.id) ? { ...b, dailyGoal: byId.get(b.id)! } : b));
    } catch (e: any) {
      window.alert('Erro ao aplicar metas: ' + (e.message || e));
    }
  };

  // ───────────────────────────────────────────────
  // Profile actions
  // ───────────────────────────────────────────────
  const changePalette = async (pal: 'midnight' | 'ember' | 'vellum') => {
    if (!session) return;
    try {
      const p = await api.patchProfile(session, { palette: pal });
      setProfile(p);
    } catch (e) { console.error(e); }
  };

  const finishTutorial = async () => {
    setTutorialOpen(false);
    if (session) {
      try { await api.markTutorialSeen(session, true); } catch {}
      setProfile(p => p ? { ...p, tutorial_seen: true } : p);
    }
  };

  // ───────────────────────────────────────────────
  // Derived state
  // ───────────────────────────────────────────────
  // Onda 4: unidade de VISÃO do card de metas. O agregado é filtrado por unidade
  // (nunca soma páginas + capítulos + posições). Default 'pages' = idêntico a hoje.
  const [goalViewUnit, setGoalViewUnit] = useState<'pages' | 'chapters' | 'positions'>('pages');
  const unitsPresent = useMemo(() => {
    const set = new Set<string>();
    books.filter(b => b.status === 'reading').forEach(b => set.add(((b as any).progressUnit) || 'pages'));
    return (['pages', 'chapters', 'positions'] as const).filter(uu => set.has(uu)) as string[];
  }, [books]);
  useEffect(() => {
    if (unitsPresent.length && !unitsPresent.includes(goalViewUnit)) setGoalViewUnit(unitsPresent[0] as any);
  }, [unitsPresent, goalViewUnit]);

  const dayPagesRead = useMemo(() => {
    return books
      .filter(b => (((b as any).progressUnit) || 'pages') === goalViewUnit)
      .reduce((s, b) => s + (b.sessions || []).filter(x => x.date === today).reduce((t, x) => t + (x.pages || 0), 0), 0);
  }, [books, today, goalViewUnit]);
  const dayTotalGoal = useMemo(() => {
    const sum = books
      .filter(b => b.status === 'reading' && (((b as any).progressUnit) || 'pages') === goalViewUnit)
      .reduce((s, b) => s + (b.dailyGoal || 0), 0);
    // Fallback por unidade (páginas=20, capítulos=2, posições=1000) em vez de
    // forçar 20 — evita "de 20 cap" quando o usuário nunca definiu meta.
    return sum > 0 ? sum : unitMeta(goalViewUnit).defaultGoal;
  }, [books, goalViewUnit]);
  // Bug #9 (parte 2): dayDone deriva apenas de pagesRead vs goal real-time.
  // Cache dailyDoneFor era usado pra prevenir replay de modal — moved pra checagem
  // separada onde o modal é disparado.
  const dayDone = dayTotalGoal > 0 && dayPagesRead >= dayTotalGoal;

  const totals = analytics?.totals || { books: 0, finishedBooks: 0, pages: 0, hours: 0, readingBooks: 0 };
  const homeTotals = { books: totals.finishedBooks, pages: totals.pages, hours: totals.hours };

  // O app é grátis (14/10/2026): não há plano do app para vender, e nenhuma
  // tela abre paywall. Os pontos que chamavam isto ficaram sem gatilho (o
  // servidor não devolve mais plan_limit); se algum disparar, leva para
  // "Plano e cobrança", que mostra o acesso de quem paga e o app grátis de quem
  // não paga. O que se paga é assinar um Mentor — na página do Mentor.
  const openPaywall = (_suggested?: any, _label?: string) => {
    setView({ kind: 'subscription' });
  };

  // ───────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────

  // Páginas legais — públicas, sem auth, escape total do app-shell.
  // ANTES de qualquer outro render pra que /termos e /privacidade sejam
  // acessíveis a quem ainda não criou conta (Apple/Google review e signup).
  if (isTermsRoute) return <LegalPage doc="terms"/>;
  if (isPrivacyRoute) return <LegalPage doc="privacy"/>;

  if (authLoading) {
    return <div className="app-shell"><div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', marginTop: 200 }}>Carregando...</div></div>;
  }

  if (resetToken) {
    return (
      <div className="app-shell">
        <ResetPasswordScreen
          token={resetToken}
          onDone={() => {
            setResetToken(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
          }}
        />
      </div>
    );
  }

  // Landing de convite (/c/:token) — funciona logado e deslogado.
  if (inviteToken) {
    return (
      <div className="app-shell">
        <InviteLandingScreen
          token={inviteToken}
          session={session}
          onLoginRequest={(term) => {
            try { localStorage.setItem('leitura.pendingInviteToken', inviteToken); } catch {}
            try { localStorage.setItem('leitura.inheritedTerm', term); } catch {}
            setAuthInitialView('login');
            setInviteToken(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
          }}
          onSignupRequest={(term) => {
            try { localStorage.setItem('leitura.pendingInviteToken', inviteToken); } catch {}
            try { localStorage.setItem('leitura.inheritedTerm', term); } catch {}
            // "Criar conta e entrar" abria o LOGIN: quem é novo tinha de achar o
            // "Criar conta" pequeno embaixo (pente fino, QA-02).
            setAuthInitialView('register');
            setInviteToken(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
          }}
          onAccepted={(clubId) => {
            setInviteToken(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
            loadClubs();
            setView({ kind: 'club-inner', clubId });
          }}
        />
      </div>
    );
  }

  // Landing do convite (/r/:codigo). Só para quem ainda não tem conta: quem
  // já está logado não precisa ser convencido, e o vínculo com quem indicou
  // acontece sozinho no efeito de indicação.
  if (conviteCode && !session) {
    const seguir = (view: 'login' | 'register') => {
      setAuthInitialView(view);
      setConviteCode(null);
      // Tira o /r/CODE da barra de endereço para o refresh não trazer a
      // landing de volta. O código já está no localStorage.
      try { window.history.replaceState({}, '', '/'); } catch {}
    };
    return (
      <div className="app-shell">
        <ConviteLandingScreen
          code={conviteCode}
          onCriarConta={() => seguir('register')}
          onEntrar={() => seguir('login')}
        />
      </div>
    );
  }

  // Landing de perfil (/@username) — funciona logada (abre o perfil no app) e
  // deslogada (preview público + CTA entrar + "Abrir no app").
  if (profileHandle) {
    return (
      <div className="app-shell">
        <ProfileLandingScreen
          handle={profileHandle}
          session={session}
          onLoginRequest={() => {
            try { localStorage.setItem('leitura.pendingProfileHandle', profileHandle); } catch {}
            setProfileHandle(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
          }}
          onSignupRequest={() => {
            try { localStorage.setItem('leitura.pendingProfileHandle', profileHandle); } catch {}
            setAuthInitialView('register');
            setProfileHandle(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
          }}
          onOpenProfile={(uid) => {
            setProfileHandle(null);
            try { window.history.replaceState({}, '', '/'); } catch {}
            openProfile(uid);
          }}
        />
      </div>
    );
  }

  if (!session) {
    // Círculo com a chave ainda chegando (ou desligado, a caminho da Home): nada
    // do Círculo, nem o cadastro "do Círculo de…".
    if (telaDoCirculo && circuloLigado !== true) return <div className="app-shell" />;
    // Círculo (P4): a página do Mentor abre sem conta, em modo vitrine.
    if (view.kind === 'mentor' && !mentorPediuConta && !pendingVerify) {
      return (
        <div className="app-shell">
          <MentorPagina
            session={null}
            slug={(view as any).slug}
            onBack={() => {}}
            onVitrineCarregou={(nome) => {
              // Só ABRIR o link já conta: o 1º login ou cadastro neste aparelho
              // (pelo topo, pelo "/" ou pelo app aberto neste link) volta aqui
              // uma vez. Não troca um pedido já guardado para este Mentor
              // (Assinar, clube, caderno), que leva o passo junto.
              const slug = (view as any).slug;
              if (lerMentorPendente()?.slug === slug) return;
              const p: MentorPendente = { slug, acao: 'pagina', t: Date.now(), ...(nome ? { nome } : {}) };
              try { localStorage.setItem(MENTOR_PENDENTE_KEY, JSON.stringify(p)); } catch {}
            }}
            onEntrar={(acao, modo, nome) => {
              const p: MentorPendente = { slug: (view as any).slug, acao, t: Date.now(), ...(nome ? { nome } : {}) };
              try { localStorage.setItem(MENTOR_PENDENTE_KEY, JSON.stringify(p)); } catch {}
              setAuthError(null);
              setAuthInitialView(modo);
              setMentorPediuConta(true);
            }}
          />
        </div>
      );
    }
    if (pendingVerify) {
      return (
        <div className="app-shell">
          <VerifyEmailScreen
            email={pendingVerify.email}
            senha={pendingVerify.password}
            onSessao={(sess: any) => {
              // Mesmo caminho do login normal: persiste e entra.
              setPendingVerify(null);
              if (sess) { saveStoredSession(sess, true); setSession(sess); }
              else { setAuthInitialView('login'); }
            }}
            onResend={() => handleResendVerification(pendingVerify.email)}
            onBack={() => { setAuthInitialView('login'); setPendingVerify(null); setAuthError(null); }}
            onUseAnotherEmail={() => { setVerifyEmail(''); setAuthInitialView('register'); setPendingVerify(null); setAuthError(null); }}
            hasInvite={(() => { try { return !!localStorage.getItem('leitura.pendingInviteToken'); } catch { return false; } })()}
          />
        </div>
      );
    }
    // O app só para assinantes: a página de assinar vem antes do cadastro, na
    // 1ª vez neste aparelho (fora da vitrine do Círculo, que tem o fluxo dela).
    const paginaAssinar = !mostrarAuth && !jaEntrouNoAparelho && view.kind !== 'mentor';
    if (paginaAssinar && oferta === undefined) return <div className="app-shell" />;
    if (paginaAssinar && oferta?.modelo === 'assinatura') {
      return (
        <div className="app-shell">
          <PaginaAssinar
            garantiaDias={oferta.garantia_dias ?? 7}
            onAssinar={() => {
              assinarAgoraRef.current = true;
              setAuthError(null); setAuthInitialView('register'); setMostrarAuth(true);
            }}
            onLogin={() => { setAuthError(null); setAuthInitialView('login'); setMostrarAuth(true); }}
          />
        </div>
      );
    }
    return (
      <div className="app-shell">
        {mostrarAuth && !jaEntrouNoAparelho && view.kind !== 'mentor' && oferta?.modelo === 'assinatura' && (
          <button type="button" className="cm-auth-voltar" data-voltar-assinar=""
            onClick={() => { assinarAgoraRef.current = false; setMostrarAuth(false); }}>
            ← Voltar
          </button>
        )}
        {view.kind === 'mentor' && mentorPediuConta && (
          // Veio da vitrine do Mentor: dá para desistir e voltar à página.
          <button type="button" className="cm-auth-voltar" data-voltar-mentor=""
            onClick={() => { try { localStorage.removeItem(MENTOR_PENDENTE_KEY); } catch {} setMentorPediuConta(false); }}>
            ← Voltar ao Círculo
          </button>
        )}
        <AuthFlow
          onLogin={handleLogin}
          onRegister={handleRegister}
          onForgot={handleForgot}
          initialEmail={verifyEmail}
          initialView={authInitialView}
          errorMessage={authError}
          loading={authBusy}
          circulo={view.kind === 'mentor' && mentorPediuConta ? (() => {
            const p = lerMentorPendente();
            return p?.nome ? { mentorNome: p.nome, assinar: p.acao === 'assinar' } : null;
          })() : null}
        />
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // PAYWALL OBRIGATÓRIO — não existe mais plano free. Assim que a conta existe
  // e o profile carregou, se o plano não é pago o app inteiro dá lugar ao
  // paywall (tela cheia). /planos passa direto (checkout web dedicado). Único
  // jeito de sair sem pagar = "Sair da conta".
  // O checkout do paywall, num lugar só: o obrigatório (fim do teste) e o que
  // a barra do teste abre (durante o teste) cobram pelo MESMO caminho.
  const checkoutDoPaywall = async (plan: any, period: any, method: any) => {
    // SÓ LOJAS na interface nova (decisão do Wesley, 31/10/2026): o site não
    // vende o Pro — nenhum caminho dela na web chega ao checkout do Mercado Pago.
    if (interfaceNovaRef.current && !isNativeApp()) {
      console.warn('[pro] checkout pelo site desligado na interface nova — assinatura só nas lojas');
      return null;
    }
    try {
      if (method === 'pix' && period === 'year') {
        const pixR: any = await api.startCheckoutPix(session, { planId: plan, period: 'year' });
        setPixCheckoutPayload(pixR);
        return pixR;
      }
      const r: any = await api.startCheckout(session, { planId: plan, period, paymentMethod: method });
      const redirectUrl = r?.init_point || r?.invoiceUrl;
      if (redirectUrl) window.location.href = redirectUrl;
      return r;
    } catch (e: any) {
      window.alert(e?.message || 'Erro ao iniciar o pagamento');
      throw e;
    }
  };

  // O PAYWALL DURO (01/11/2026): na interface nova, o teste grátis acabou sem
  // o Pro — o app inteiro dá lugar ao BloqueioPro (assinar, restaurar, sair,
  // excluir a conta, suporte). Os dados ficam; assinando, tudo volta.
  if (session && interfaceNova && acessoApp?.estado === 'bloqueado') {
    // Veio do "Assinar" da página de assinar: a compra abre sozinha, uma vez.
    // O botão da loja abre a compra uma vez por montagem; depois o pedido some.
    const assinarAgora = assinarAgoraRef.current;
    if (assinarAgora) setTimeout(() => { assinarAgoraRef.current = false; }, 0);
    return (
      <div className="app-shell">
        <BloqueioPro
          session={session}
          email={(profile as any)?.email}
          onConferir={conferirAcesso}
          onSair={() => handleLogout()}
          onExcluir={handleDeleteAccount}
          motivo={acessoApp?.motivo}
          garantiaDias={oferta?.garantia_dias}
          autoAssinar={assinarAgora}
        />
      </div>
    );
  }

  if (profile && !isPro && !isPlansRoute && voltaDoMp) {
    return (
      <div className="app-shell">
        <ConfirmandoPagamento
          estado={voltaDoMp}
          onConferirDeNovo={() => setVoltaDoMp('conferindo')}
          onVerPlanos={() => setVoltaDoMp(null)}
        />
      </div>
    );
  }

  if (profile && !isPro && !isPlansRoute) {
    return (
      <div className="app-shell">
        <PaywallScreen
          mandatory
          session={session}
          currentPlan={(profile?.plan as any) || 'free'}
          preferredTerm={(profile?.preferred_term === 'grupo' || profile?.preferred_term === 'clube') ? profile.preferred_term : undefined}
          onLogout={handleLogout}
          onClose={() => {}}
          onCheckout={checkoutDoPaywall}
        />
        {/* PIX overlay também aqui — o gate retorna antes do overlay global */}
        {pixCheckoutPayload && (
          <PixCheckout
            payload={pixCheckoutPayload}
            session={session}
            onPaid={() => { setPixCheckoutPayload(null); loadAllData(); }}
            onClose={() => setPixCheckoutPayload(null)}
          />
        )}
      </div>
    );
  }

  // Main screen routing
  let screen: React.ReactNode;
  if (telaDoCirculo && circuloLigado !== true) {
    // A chave do Círculo ainda chegando, ou desligado (o efeito leva à Home).
    screen = <div />;
  } else if (view.kind === 'book') {
    const currentBook = books.find(b => b.id === view.book.id) || view.book;
    screen = (
      <BookDetailScreen
        book={currentBook}
        onLivroSumiu={() => livroSumiu(currentBook.id)}
        onBack={closeBook}
        onLogRead={() => startLog(currentBook)}
        onOpenStudy={() => openBookStudy(currentBook)}
        mentorNovas={mentorNovas[currentBook.id] || 0}
        onStatusChange={handleStatusChange}
        onDelete={() => handleDeleteBook(currentBook.id)}
        onUpdateGoal={applyGoalToBook}
        onSyncPage={handleSyncPage}
        onEditDetails={async (bookId, patch) => {
          if (!session) return;
          const updated = await api.updateBook(session, bookId, patch);
          setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...updated } : b));
          if (view.kind === 'book' && view.book.id === bookId) {
            setView({ kind: 'book', book: { ...view.book, ...updated } });
          }
        }}
        onSearchCover={async (bookId) => {
          if (!session) return;
          const res = await api.searchCover(session, bookId);
          setBooks(prev => prev.map(b => b.id === bookId ? { ...b, coverUrl: res.coverUrl } : b));
          if (view.kind === 'book' && view.book.id === bookId) {
            setView({ kind: 'book', book: { ...view.book, coverUrl: res.coverUrl } });
          }
        }}
        onUploadCover={async (bookId, file) => {
          if (!session) return;
          const res = await api.uploadCover(session, bookId, file);
          setBooks(prev => prev.map(b => b.id === bookId ? { ...b, coverUrl: res.coverUrl } : b));
          if (view.kind === 'book' && view.book.id === bookId) {
            setView({ kind: 'book', book: { ...view.book, coverUrl: res.coverUrl } });
          }
        }}
        session={session}
        myName={profile?.display_name || profile?.username}
        myAvatarUrl={(profile as any)?.avatar_url || null}
        onOpenProfile={openProfile}
        onBlockUser={async (uid) => { if (session) await api.blockUser(session, uid); }}
        onSaveReview={async (bookId, rating, review) => {
          if (!session) return;
          const updated = await api.updateBook(session, bookId, { rating, review } as any);
          setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...updated } : b));
          if (view.kind === 'book' && view.book.id === bookId) {
            setView({ kind: 'book', book: { ...view.book, ...updated } });
          }
        }}
      />
    );
  } else if (view.kind === 'cadernos') {
    // A casa das notas. A visao global antiga nao sumiu — virou o atalho
    // "Todas as notas" aqui dentro, e /estudos continua respondendo para
    // quem tiver o link salvo.
    // Na interface nova, a trilha vem primeiro (CadernosNova); a tela de
    // sempre continua dentro dela, no atalho "Cadernos temáticos e todas as notas".
    const abrirNota = (bookId: string, page: number | null) => {
      const b = books.find(x => x.id === bookId);
      if (b) openBookStudy(b, page);
    };
    screen = interfaceNova ? (
      <CadernosNova
        session={session}
        books={books}
        travessias={travessias}
        mentorNovas={mentorNovas}
        acessoTrilhas={acessoTrilhas}
        onBack={goBack}
        onCaderno={(b, naTrilha) => openBookStudy(b, null, naTrilha)}
        onResponder={(b, pagina, contexto) => openBookStudy(b, pagina, false, contexto)}
        onAbrirNota={abrirNota}
        onTravessia={openTravessia}
        onExplorar={() => { setView({ kind: 'tab' }); setTab('explore'); }}
        onPro={() => setProAberto('vitrine')}
      />
    ) : (
      <CadernosScreen
        session={session}
        onBack={goBack}
        onAbrirLivro={abrirNota}
      />
    );
  } else if (view.kind === 'estudos') {
    // O acervo inteiro, de todos os livros. "Abrir no livro" reaproveita a
    // tela por livro, entao a nota e reencontrada exatamente onde nasceu.
    screen = (
      <EstudosGlobalScreen
        session={session}
        onBack={goBack}
        onAbrirLivro={(bookId: string, page: number | null) => {
          const b = books.find(x => x.id === bookId);
          if (b) openBookStudy(b, page);
        }}
      />
    );
  } else if (view.kind === 'book-study') {
    const sb = books.find(b => b.id === (view as any).book.id) || (view as any).book;
    screen = (
      <BookStudyScreen
        key={sb.id}
        book={sb}
        session={session}
        onBack={closeBook}
        autoComposePage={studyCompose}
        autoComposeContexto={studyContexto}
        abrirNaTrilha={studyTrilha}
        onAbrirTravessia={travessias[sb.id] ? () => trocarFace(sb, 'travessia') : undefined}
        abas={interfaceNova && travessias[sb.id] ? <LivroAbas ativa="caderno" sobreImagem novas={travessias[sb.id].nao_vistos || 0} onTrocar={(f) => trocarFace(sb, f)} /> : undefined}
      />
    );
  } else if (view.kind === 'travessia') {
    const tb = books.find(b => b.id === (view as any).book.id) || (view as any).book;
    screen = (
      <TravessiaScreen
        key={tb.id}
        book={tb}
        session={session}
        onBack={closeBook}
        onRegistrar={() => startLog(tb)}
        onAbrirCaderno={(p, c) => (interfaceNova ? trocarFace(tb, 'caderno', p ?? null, c) : openBookStudy(tb, p ?? null, false, c))}
        abas={interfaceNova ? <LivroAbas ativa="travessia" onTrocar={(f) => trocarFace(tb, f)} /> : undefined}
        onPro={interfaceNova ? () => setProAberto('trilha') : undefined}
        abrirItem={(view as any).item}
      />
    );
  } else if (view.kind === 'notif') {
    screen = <NotifScreen
      onBack={goBack}
      config={notifConfig}
      onChange={(next) => { setNotifConfig(next); saveNotifConfig(next); }}
      pushStatus={pushStatus}
      onActivatePush={activatePush}
      onTestPush={testPush}
      onRefreshPushStatus={() => {
        // "Atualizar" clicado: limpa erro explicitamente + reabre status check.
        setPushStatus(prev => ({ ...prev, error: null }));
        return refreshPushStatus();
      }}
      isAdmin={profile?.role === 'admin'}
    />;
  } else if (view.kind === 'subscription') {
    screen = (
      <MySubscriptionScreen
        session={session}
        profile={profile}
        onBack={goBack}
        onUpgrade={() => {
          // No iOS native: abre o PaywallModal interno (que tem o botao IAP via
          // RevenueCat). Apple Guideline 3.1.1 exige checkout DENTRO do app via IAP.
          // No web/android: continua abrindo /planos no navegador externo
          // (checkout web via Mercado Pago).
          if (isNativeApp()) {
            setPaywallSuggested('reader');
            setPaywallOpen(true);
          } else {
            const url = window.location.origin + '/planos?suggest=reader';
            openExternalUrl(url);
          }
        }}
      />
    );
  } else if (view.kind === 'readings') {
    screen = (
      <MyReadingsScreen
        session={session}
        onBack={goBack}
        onChange={() => {
          // Edição/delete invalidam livros + stats — recarrega tudo
          // pra refletir current_page novo, achievements, streak, etc.
          loadAllData();
        }}
      />
    );
  } else if (view.kind === 'report') {
    if (!isPro) {
      screen = <LockedFeatureScreen
        title="Insights"
        subtitle="Relatório inteligente"
        requiredPlan="reader"
        currentPlan={(profile?.plan as any) || 'free'}
        onUpgrade={() => openPaywall('reader', 'Insights')}/>;
    } else {
      screen = (
        <ReportScreen
          analytics={analytics}
          insights={insights}
          loading={insightsLoading}
          error={insightsError}
          readingBooks={books.filter(b => b.status === 'reading')}
          onBack={goBack}
          onRefresh={() => { setInsights(null); loadInsights(); }}
          onApplyGoalForBook={applyGoalToBook}
          onApplyAllGoals={applyAllGoals}
        />
      );
    }
  } else if (view.kind === 'admin') {
    // Restrição: só admin acessa o console. Se chegou aqui sem ser admin
    // (ex: deep link), redireciona pro tab home com aviso.
    if (profile?.role !== 'admin') {
      screen = (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div className="serif" style={{ fontSize: 24, marginBottom: 10 }}>Restrito</div>
          <div style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 20 }}>
            Esta área é exclusiva da administração.
          </div>
          <button onClick={() => setView({ kind: 'tab' })} style={{
            padding: '10px 18px', borderRadius: 999,
            background: 'var(--ink)', color: 'var(--paper)', border: 'none',
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}>Voltar</button>
        </div>
      );
    } else {
      screen = <AdminConsoleScreen
        session={session}
        onBack={() => {
          // Se estiver no subdomínio admin., "voltar" leva pro app principal.
          // Senão (user veio de /admin no domínio normal), volta pra aba.
          if (isAdminSubdomain) {
            window.location.href = 'https://www.forjamente.com.br/';
          } else {
            setView({ kind: 'tab' });
          }
        }}
      />;
    }
  } else if (view.kind === 'library') {
    screen = (
      <LibraryScreen
        books={books}
        isPro={isPro}
        maxLibrary={limits.maxLibrary}
        maxReading={limits.maxReading}
        onOpenBook={openBook}
        onAdd={() => setAddOpen(true)}
        onOpenPaywall={openPaywall}
        onBack={goBack}
        onBookActions={(b: BookV2) => setBookActions(b)}
        shelves={shelves}
        // Editar estante e no perfil agora; daqui so o caminho pra la.
        onIrParaPerfil={() => { setView({ kind: 'tab' }); setTab('me'); }}
      />
    );
  } else if (view.kind === 'mentor') {
    // Círculo do Mentor — a página do Mentor (P1).
    screen = (
      <MentorPagina
        session={session}
        slug={(view as any).slug}
        onBack={goBack}
        onAbrirCadernos={() => setView({ kind: 'cadernos' } as any)}
        onAbrirClube={(clubId: string) => setView({ kind: 'club-inner', clubId })}
        onAbrirEstudoDoClube={(clubId: string, bookId: string | null) => setView({ kind: 'estudo', clubId, ...(bookId ? { bookId } : {}) } as any)}
        onAbrirClubes={() => { setView({ kind: 'tab' }); setTab('clubs'); }}
        onAbrirCadernoDoLivro={openBookStudyById}
        onAbrirEstudio={() => setView({ kind: 'mentorEstudio', slug: (view as any).slug } as any)}
        onAbrirEditor={() => setView({ kind: 'circuloEditor' } as any)}
        onAbrirSerieNoEditor={() => setView({ kind: 'circuloEditor', vista: 'serie' } as any)}
        abrirAoEntrar={mentorAoEntrar}
        onAbriuAoEntrar={limparMentorAoEntrar}
      />
    );
  } else if (view.kind === 'mentorEstudio') {
    // Círculo — "Meus episódios": o Mentor sobe e publica os episódios (P3).
    screen = (
      <MentorEstudio
        session={session}
        slug={(view as any).slug}
        onBack={goBack}
        onAbrirPagina={() => setView({ kind: 'mentor', slug: (view as any).slug } as any)}
      />
    );
  } else if (view.kind === 'circuloEditor') {
    // Círculo — o Editor do Mentor. Quem não é Mentor vai para o início, como
    // um endereço que não existe (o mesmo aviso do QA-15).
    screen = (
      <EditorDoMentor
        session={session}
        vistaInicial={(view as any).vista}
        onBack={goBack}
        onNaoEMentor={() => {
          setView({ kind: 'tab' } as any, { trocar: true });
          toast.info('Essa página não existe — você está no início.');
        }}
        onVerPagina={(slug) => setView({ kind: 'mentor', slug } as any)}
        onAbrirClube={(clubId) => setView({ kind: 'club-inner', clubId } as any)}
      />
    );
  } else if (view.kind === 'programa') {
    // Protocolo do Hábito — a área de estudo: por que o programa é desse jeito,
    // com a evidência de cada decisão e o que ela NÃO sustenta.
    screen = (
      <ProgramaScreen
        session={session}
        estado={habito}
        onEditarContrato={() => setEditarContratoAberto(true)}
        onVoltar={goBack}
        onMudou={() => { recarregarHabito(); }}
      />
    );
  } else if (view.kind === 'tutoriais') {
    screen = (
      <TutoriaisScreen
        profile={profile}
        // Volta de verdade (chega-se aqui por Ajustes): setView empilhava Ajustes
        // de novo, e o voltar do navegador trazia os Tutoriais de volta.
        onBack={goBack}
        onToggle={(key, enabled) => {
          try { api.setTutorialState(session, key, { enabled }); } catch {}
          setProfile((p: any) => p ? { ...p, tutorials_state: { ...(p.tutorials_state || {}), [key]: { ...((p.tutorials_state || {})[key] || {}), enabled } } } : p);
        }}
        onReplay={(key) => {
          if (key === 'onboarding') setOnboardingOpen(true);
          else if (key === 'explore') setExploreTourOpen(true);
          else if (key === 'clube') {
            try { Object.keys(localStorage).forEach(k => { if (k.startsWith('forjamente.welcomeSeen.')) localStorage.removeItem(k); }); } catch {}
            try { window.alert('Pronto! O tutorial do clube vai aparecer no próximo clube que você abrir.'); } catch {}
          }
        }}
      />
    );
  } else if (view.kind === 'settings') {
    screen = (
<ProfileScreen
            profile={profile}
            streak={streak}
            finishedBooks={totals.finishedBooks}
            onOpenPaywall={() => {
              // Free user: abrir Safari externo direto via magic link (Apple 3.1.1)
              // Paid user: abrir MySubscriptionScreen interna pra ver/gerenciar
              if (isPro) {
                setView({ kind: 'subscription' });
              } else {
                openPaywall();
              }
            }}
            onOpenReport={() => isPro ? setView({ kind: 'report' }) : openPaywall()}
            onOpenNotif={() => setView({ kind: 'notif' })}
            onOpenTutorial={() => setTutorialOpen(true)}
            onOpenTutoriais={() => setView({ kind: 'tutoriais' } as any)}
            onLogout={handleLogout}
            onChangePalette={changePalette}
            onOpenAdmin={profile?.role === 'admin' ? () => setView({ kind: 'admin' }) : undefined}
            onOpenLibrary={() => setView({ kind: 'library' })}
            onOpenReadings={() => setView({ kind: 'readings' })}
            onOpenStats={() => setTab('stats')}
            clubsTerm={clubsTerm}
            onChangeClubsTerm={changeClubsTerm}
            onDeleteAccount={profile?.role !== 'admin' ? handleDeleteAccount : undefined}
            hidePaywall={isNativeApp()}
          onBack={goBack}
          />
    );
  } else if (view.kind === 'user') {
    screen = (
      <ProfileSocialScreen
        session={session}
        userId={(view as any).userId}
        currentUserId={profile?.id}
        onBack={goBack}
        onOpenClub={(clubId: string) => setView({ kind: 'club-inner', clubId })}
        onGoToEstudo={(clubId: string) => setView({ kind: 'estudo', clubId })}
        onOpenProfile={openProfile}
        onUpgrade={() => openPaywall('reader', 'Perfil')}
        onOpenBook={openBookById}
        myBooks={books}
        myShelves={shelves}
        // Tudo o que a biblioteca do perfil precisa para editar estante. Ia
        // para os ajustes antes — a pessoa via as estantes num lugar e tinha
        // que sair do perfil para mexer nelas.
        estantes={estantesDoPerfil}
      
        />
    );
  } else if (view.kind === 'proposta') {
    // Clube Social: proposta viva / lançamento (co-criação). Tela autônoma.
    screen = (
      <ProposalPage
        session={session}
        clubId={(view as any).clubId}
        currentUserId={profile?.id}
        myName={profile?.display_name || profile?.username}
        myAvatarUrl={(profile as any)?.avatar_url || null}
        onBack={goBack}
        onOpenProfile={openProfile}
        onOpenClub={(clubId: string) => setView({ kind: 'club-inner', clubId })}
        onEditProposal={(clubId: string) => setView({ kind: 'club-inner', clubId })}
      />
    );
  } else if (view.kind === 'club-inner') {
    screen = (
      <ClubInnerScreen
        term={clubsTerm}
        club={activeClub}
        members={activeClub?.membersList || []}
        loading={activeClubLoading}
        loadError={activeClubError}
        currentUserId={profile?.id}
        // Mural do clube: fala direto com a API, então precisa da sessão e do
        // meu rosto pro composer.
        session={session}
        myName={profile?.display_name || profile?.username}
        myAvatarUrl={(profile as any)?.avatar_url || null}
        // Livro pessoal espelhado: usado pra mostrar "hoje X/meta Y" no card.
        personalMirror={(() => {
          const pid = activeClub?.book?.personalBookId;
          return pid ? books.find(b => b.id === pid) : null;
        })()}
        onBack={goBack}
        onJoin={async () => {
          const cid = (view as any).clubId || activeClub?.id;
          if (!session || !cid) return;
          try {
            await api.joinClub(session, cid);
            const c = await api.getClub(session, cid);
            setActiveClub(c);
            loadClubs();
          } catch (e: any) {
            const msg = (e?.message || '') + '';
            if (msg.includes('plan_limit') || msg.includes('plan_required')) openPaywall('reader', 'Entrar em mais grupos');
            else if (msg.includes('club_member_limit_reached')) toast.error('Esse clube atingiu o máximo de membros pro plano do líder.');
            else toast.error('Não foi possível entrar agora.');
          }
        }}
        onMenu={() => setClubMenuOpen(true)}
        onShare={() => { if (view.clubId || activeClub?.id) setClubShareTargetId(view.clubId || activeClub?.id || null); setClubShareOpen(true); }}
        onOpenEstudo={(o?: { escrever?: boolean }) => setView({ kind: 'estudo', clubId: view.clubId, bookId: activeClub?.book?.id, ...(o?.escrever ? { escrever: true } : {}) } as any)}
        onOpenEstudoForBook={(bookId: string) => setView({ kind: 'estudo', clubId: view.clubId, bookId })}
        onPokeMember={async (toUserId: string, name: string) => {
          if (!session || !view.clubId) return;
          try {
            await api.createPoke(session, view.clubId, {
              toUserId,
              type: 'general',
              message: `${name}, sentimos sua falta no estudo!`,
            });
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao cutucar');
            // RELANCA: quem chamou precisa saber que falhou.
            // Sem isto a promessa terminava RESOLVIDA, e o botao la em
            // inner.tsx marcava "cutucado" mesmo depois do alerta de erro —
            // travado para o resto da visita, sem como tentar de novo. A tela
            // contradizia o proprio aviso que tinha acabado de dar.
            throw e;
          }
        }}
        onRemoveMember={async (toUserId: string, name: string) => {
          if (!session || !view.clubId) return;
          try {
            await api.removeClubMember(session, view.clubId, toUserId);
            // Re-fetch do clube ativo pra atualizar lista de membros
            const c = await api.getClub(session, view.clubId);
            setActiveClub(c);
            loadClubs();
          } catch (e: any) {
            window.alert(e?.message || `Erro ao remover ${name}`);
          }
        }}
        onOpenCurrentBook={async () => {
          // Tap no card "Livro atual" abre BookDetail do espelho pessoal.
          // Cria o espelho se nao existir (mesma logica do "Continuar leitura").
          if (!session || !view.clubId) return;
          try {
            const r = await api.startReadingClubBook(session, view.clubId);
            const personalBook = r.book;
            setBooks(prev => {
              const idx = prev.findIndex(b => b.id === personalBook.id);
              if (idx >= 0) {
                const next = [...prev];
                const existingSessions = prev[idx].sessions || [];
                const incomingSessions = personalBook.sessions || [];
                next[idx] = {
                  ...prev[idx], ...personalBook,
                  sessions: incomingSessions.length > 0 ? incomingSessions : existingSessions,
                };
                return next;
              }
              return [personalBook, ...prev];
            });
            setView({ kind: 'book', book: personalBook });
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao abrir livro');
          }
        }}
        onAddBook={() => { setAddClubBookFor(view.clubId); setAddClubBookOpen(true); }}
        onOpenSeriesBook={async (book: any) => {
          if (!session || !book?.id || !view.clubId) return;
          try {
            // Espelha o livro especifico (cria se nao existir) e abre o
            // BookDetailScreen padrao — mesma pagina que aparece quando user
            // toca em qualquer livro pessoal. La ele edita capa, paginas, etc.
            const r = await api.startReadingClubBook(session, view.clubId, book.id);
            const personalBook = r.book;
            setBooks(prev => {
              const idx = prev.findIndex(b => b.id === personalBook.id);
              if (idx >= 0) {
                const next = [...prev];
                const existingSessions = prev[idx].sessions || [];
                const incomingSessions = personalBook.sessions || [];
                next[idx] = {
                  ...prev[idx], ...personalBook,
                  sessions: incomingSessions.length > 0 ? incomingSessions : existingSessions,
                };
                return next;
              }
              return [personalBook, ...prev];
            });
            setView({ kind: 'book', book: personalBook });
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao abrir livro');
          }
        }}
        onFinishCurrentBook={async (bookId: string) => {
          if (!session || !view.clubId) return;
          try {
            if (activeClub?.mode === 'shared-habit') {
              await api.finishMemberBook(session, view.clubId, bookId);
            } else {
              await api.finishClubBook(session, view.clubId, bookId);
            }
            const c = await api.getClub(session, view.clubId);
            setActiveClub(c);
            loadClubs();
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao avançar livro do clube');
          }
        }}
        onReorderMemberBooks={async (ids: string[]) => {
          if (!session || !view.clubId) return;
          try {
            await api.reorderMemberBooks(session, view.clubId, ids);
            const c = await api.getClub(session, view.clubId, clubViewingUserId);
            setActiveClub(c);
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao reordenar fila');
          }
        }}
        onChangeBanner={async (file: any) => {
          if (!session || !view.clubId) return;
          try {
            const { url } = await api.uploadClubCover(session, file);
            await api.patchClub(session, view.clubId, { customBannerUrl: url });
            const c = await api.getClub(session, view.clubId, clubViewingUserId);
            setActiveClub(c);
            loadClubs();
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao trocar o banner');
          }
        }}
        onOpenProfile={openProfile}
        viewingUserId={clubViewingUserId}
        onChangeViewingUser={async (userId: string | null) => {
          // F5: re-fetch o clube com o viewingUserId pra trazer mySeries do
          // membro selecionado (ou da propria fila quando userId=null).
          if (!session || !view.clubId) return;
          setClubViewingUserId(userId);
          try {
            const c = await api.getClub(session, view.clubId, userId);
            setActiveClub(c);
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao trocar visao');
          }
        }}
        onContinueReading={async () => {
          if (!session) return;
          // shared-habit: livro já é pessoal — abre direto pelo id local.
          if (activeClub?.mode === 'shared-habit') {
            const myBookId = activeClub?.myBook?.id;
            const local = myBookId ? books.find(b => b.id === myBookId) : null;
            if (local) setView({ kind: 'book', book: local });
            return;
          }
          try {
            const r = await api.startReadingClubBook(session, view.clubId);
            const personalBook = r.book;
            // Merge cuidadoso: nunca sobrescrever sessions locais com [] vazio.
            setBooks(prev => {
              const idx = prev.findIndex(b => b.id === personalBook.id);
              if (idx >= 0) {
                const next = [...prev];
                const existingSessions = prev[idx].sessions || [];
                const incomingSessions = personalBook.sessions || [];
                next[idx] = {
                  ...prev[idx], ...personalBook,
                  sessions: incomingSessions.length > 0 ? incomingSessions : existingSessions,
                };
                return next;
              }
              return [personalBook, ...prev];
            });
            setView({ kind: 'book', book: personalBook });
          } catch (e: any) {
            window.alert(e?.message || 'Erro ao abrir livro do clube');
          }
        }}
      />
    );
  } else if (view.kind === 'club-explore') {
    screen = (
      <ClubsExploreScreen
        term={clubsTerm}
        session={session}
        onBack={goBack}
        onOpenClub={(clubId: string) => setView({ kind: 'club-inner', clubId })}
        onOpenPaywall={() => openPaywall('reader', 'Explorar grupos públicos')}
      />
    );
  } else if (view.kind === 'club-create') {
    const isPropose = (view as any).propose === true;
    const ccOnSubmit = async (data: any) => { const created = await handleCreateClub(data); return created ? { id: created.id } : undefined; };
    const ccOnCreated = (data: any, clubId: string | null) => {
      // Proposta → vai pra página de proposta viva; criação direta → club-empty.
      if (data?.propose && clubId) setView({ kind: 'proposta', clubId }, { trocar: true });
      else setView({ kind: 'club-empty', clubData: data, clubId }, { trocar: true });
    };
    const ccOnUploadCover = async (file: File) => { if (!session) throw new Error('Sessão expirada'); const r = await api.uploadClubCover(session, file); return r.url; };
    // Criar (e propor) clube é só para Mentor. `is_mentor` vem de /api/profile;
    // se não veio (perfil em cache antigo), deixa seguir — o servidor decide.
    // PROPOR usa a tela única (design); CRIAR normal continua no wizard.
    screen = (profile as any)?.is_mentor === false ? (
      <SoMentorScreen term={clubsTerm} onBack={goBack} onExplore={() => setView({ kind: 'club-explore' }, { trocar: true })} />
    ) : isPropose ? (
      <ProporClubeForm term={clubsTerm} session={session} currentUserId={profile?.id} library={books}
        onBack={goBack} onSubmit={ccOnSubmit} onCreated={ccOnCreated} onUploadCover={ccOnUploadCover} />
    ) : (
      <ClubCreateFlow term={clubsTerm} library={books} onBack={goBack} onSubmit={ccOnSubmit} onCreated={ccOnCreated} onUploadCover={ccOnUploadCover} />
    );
  } else if (view.kind === 'club-empty') {
    screen = (
      <ClubEmptyScreen
        term={clubsTerm}
        data={view.clubData}
        clubId={view.clubId}
        onBack={goBack}
        onInvite={() => {
          // Bug #2 fix: garantir clubId no modal share via state dedicado
          const cid = (view as any).clubId;
          fetch('/api/debug-log', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tag: 'club-invite-click', data: `view.kind=${view.kind} view.clubId=${cid} clubData=${JSON.stringify((view as any).clubData)?.slice(0,100)}` })
          }).catch(()=>{});
          if (cid) setClubShareTargetId(cid);
          setClubShareOpen(true);
        }}
        onSetMilestone={undefined}
        onEnterClub={() => view.clubId && setView({ kind: 'club-inner', clubId: view.clubId }, { trocar: true })}
      />
    );
  } else if (view.kind === 'club-analytics') {
    screen = (
      <ClubAnalyticsScreen
        term={clubsTerm}
        session={session}
        clubId={view.clubId}
        clubName={activeClub?.name}
        onBack={goBack}
        onOpenProfile={openProfile}
      />
    );
  } else if (view.kind === 'club-question-box') {
    const qbClubId = view.clubId;
    const qbPostId = (view as any).postId;
    screen = (
      <ClubQuestionBoxAdminScreen
        term={clubsTerm}
        initialPhase={view.initialPhase || 'active'}
        session={session}
        clubId={qbClubId}
        postId={qbPostId}
        onSelectQbox={(pid: string) => setView({ kind: 'club-question-box', clubId: qbClubId, initialPhase: 'active', postId: pid })}
        onBack={goBack}
        onCreateBox={async ({ prompt, duration, anonymous }) => {
          if (!session) throw new Error('Não autenticado');
          await api.createPost(session, qbClubId, {
            kind: 'question-box',
            content: { prompt, closesIn: duration, anonymous },
          });
          // Recarrega feed pra que ao voltar ao Estudo o card já esteja lá
          try {
            const ps = await api.listPosts(session, qbClubId, { filter: studyFilter, limit: 30 });
            setStudyPosts(ps.posts || []);
          } catch (e) {
            console.error('[question-box/refresh]', e);
          }
        }}
        onAnswered={async () => {
          // Quando admin responde uma pergunta, recarrega o feed pra mostrar o
          // novo leader-answer post na próxima volta pro Estudo, e atualiza o
          // count de pendentes (badge no menu do clube).
          if (!session) return;
          try {
            const ps = await api.listPosts(session, qbClubId, { filter: studyFilter, limit: 30 });
            setStudyPosts(ps.posts || []);
          } catch (e) { console.error('[qbox/answered/refresh]', e); }
          try {
            const qbs = await api.listQuestionBoxes(session, qbClubId);
            const list: any[] = qbs.qboxes || [];
            const total = list.reduce((acc, qb) => acc + (qb.openQuestions || 0), 0);
            setPendingQboxCount(total);
          } catch (e) { /* silencioso */ }
        }}
      />
    );
  } else if (view.kind === 'estudo') {
    const clubId = view.clubId;
    const isClubAdmin = activeClub?.yourRole === 'owner' || activeClub?.yourRole === 'admin';
    // canPost: viewer comenta apenas se for membro (yourRole presente). Em clube
    // 'open' não-membros conseguem ler comentários, mas o backend bloqueia POST.
    const canPostComments = !!activeClub?.yourRole;
    // viewerIsModerator: pode editar/excluir conteúdo de QUALQUER membro
    // (regra Wesley: admin/owner/moderator gerenciam; membro comum só o próprio).
    const viewerIsModerator = activeClub?.yourRole === 'owner' || activeClub?.yourRole === 'admin' || activeClub?.yourRole === 'moderator';
    const viewerId = profile?.id;
    // Estudo 2.0 — ROLLOUT GERAL (todos os usuários) após correção dos blockers
    // (gating de não-membro, feedback de erro, deep-link de marco). Kill-switch por
    // device: localStorage.estudo2='0' volta pra v1. Rollback global = reverter commit.
    const _e2flag = (typeof window !== 'undefined') ? window.localStorage.getItem('estudo2') : null;
    const useEstudo2 = _e2flag !== '0';
    // O Estudo com layout de Caderno. Ele busca os proprios dados (a paginacao
    // por pagina do livro tem cursor composto e nao cabe no estado do App), por
    // isso entra antes e nao usa nada do bloco abaixo.
    // `localStorage.estudoCaderno='0'` volta pro Estudo 2.0 sem deploy.
    if (usandoCaderno()) {
      screen = (
        <EstudoCaderno
          session={session}
          clubId={clubId}
          clubName={activeClub?.name}
          clubBook={activeClub?.book || activeClub?.myBook || null}
          yourPage={activeClub?.book?.yourPage || 0}
          viewerId={viewerId}
          canPost={canPostComments}
          viewerIsModerator={viewerIsModerator}
          onBack={goBack}
          onOpenProfile={openProfile}
          onReportPost={(post: any) => {
            const kind = post?.kind || 'post';
            const author = post?.author?.name || 'membro';
            setReportTarget({ kind: 'post', id: post.id, label: `${kind} de ${author}` });
          }}
          onOpenQboxAdmin={(qboxPostId: string) => {
            setView({ kind: 'club-question-box', clubId, initialPhase: 'active', postId: qboxPostId });
          }}
          headerExtra={<PokesBell unread={pokesUnread} onOpen={() => setPokesOpen(true)}/>}
          storiesTray={<StoriesTray/>}
          // "O clube CERTO já chegou", não "não está carregando".
          //
          // `!activeClubLoading` não servia: efeito de filho roda ANTES do
          // efeito do pai, então na primeira renderização o loading ainda era
          // false e o Caderno buscava sem o livro. Comparar os ids é o único
          // sinal que não depende de ordem de execução.
          clubePronto={activeClub?.id === clubId}
          abrirEscrita={!!(view as any).escrever}
        />
      );
    } else if (useEstudo2) {
      const reloadStudyPosts = async () => {
        try { const ps = await api.listPosts(session, clubId, { filter: studyFilter, limit: 30, bookId: activeClub?.book?.id }); setStudyPosts(ps.posts || []); } catch {}
      };
      const reloadStudyMs = async () => {
        try { const r = await api.listMilestones(session, clubId, { bookId: activeClub?.book?.id }); setStudyMilestones(r.milestones || []); } catch {}
      };
      screen = (
        <EstudoScreen2
          session={session}
          onOpenProfile={openProfile}
          clubId={clubId}
          clubName={activeClub?.name}
          clubBook={activeClub?.book || activeClub?.myBook || null}
          isAdmin={isClubAdmin}
          canPost={canPostComments}
          viewerId={viewerId}
          viewerIsModerator={viewerIsModerator}
          milestones={studyMilestones}
          posts={studyPosts}
          loading={studyLoading}
          yourPage={activeClub?.book?.yourPage || 0}
          filter={studyFilter}
          onChangeFilter={setStudyFilter}
          onBack={goBack}
          onRefetch={reloadStudyPosts}
          onReloadMilestones={reloadStudyMs}
          onCommentAdded={(postId: string, delta: number) => setStudyPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: Math.max(0, (p.comments || 0) + delta) } : p))}
          initialMilestoneId={expandMilestoneOnEstudo}
          onAfterInitialMilestone={() => setExpandMilestoneOnEstudo(null)}
          headerExtra={<PokesBell unread={pokesUnread} onOpen={() => setPokesOpen(true)}/>}
          storiesTray={<StoriesTray/>}
        />
      );
    } else {
    screen = (
      <EstudoScreen
        term={clubsTerm}
        clubId={clubId}
        clubName={activeClub?.name}
        isAdmin={isClubAdmin}
        session={session}
        canPost={canPostComments}
        viewerId={viewerId}
        viewerIsModerator={viewerIsModerator}
        milestones={studyMilestones}
        posts={studyPosts}
        loading={studyLoading}
        yourPage={activeClub?.book?.yourPage || 0}
        filter={studyFilter}
        onChangeFilter={setStudyFilter}
        onBack={goBack}
        onMenu={() => setClubMenuOpen(true)}
        onReact={handleReactPost}
        onEditPost={handleEditPost}
        onDeletePost={handleDeletePost}
        onReportPost={(post: any) => {
          const kind = post?.kind || 'post';
          const author = post?.author?.name || 'membro';
          setReportTarget({ kind: 'post', id: post.id, label: `${kind} de ${author}` });
        }}
        onReplyToQuestion={(q: any) => {
          setComposeReplyTo({
            postId: q.id,
            questionText: q.text || q.content?.text || '',
            authorName: q.author?.name || 'Membro',
          });
          setComposeOpen(true);
        }}
        onOpenQboxAdmin={(qboxPostId: string) => {
          setView({ kind: 'club-question-box', clubId, initialPhase: 'active', postId: qboxPostId });
        }}
        headerExtra={
          <PokesBell unread={pokesUnread} onOpen={() => setPokesOpen(true)}/>
        }
        storiesTray={<StoriesTray/>}
        // FAB abre direto o ComposeSheet (que tem o picker de tipo no topo).
        // question-box / quiz / insight-final ainda virão pelo menu "..." do clube.
        onCompose={() => { setComposeReplyTo(null); setComposeOpen(true); }}
        onOpenInsight={(insight) => setView({ kind: 'insight-detail', insight, clubId })}
        onCommentAdded={(postId, delta) => {
          setStudyPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: (p.comments || 0) + delta } : p));
        }}
        initialExpandedMilestoneId={expandMilestoneOnEstudo}
        onAfterInitialExpand={() => setExpandMilestoneOnEstudo(null)}
      />
    );
    } // fim do else (EstudoScreen v1 fallback)
    screen = (
      <StoriesProvider session={session} clubId={clubId} isElevated={isClubAdmin} onOpenProfile={openProfile as any}
        viewer={{ id: profile?.id, name: profile?.display_name, username: (profile as any)?.username, avatarUrl: (profile as any)?.avatar_url }}>
        {screen}
        <StoriesOverlays/>
      </StoriesProvider>
    );
  } else if (view.kind === 'insight-detail') {
    const insightClubId = view.clubId;
    // Marcos encerrados COM Insight Final pra navegação cronológica
    const closedWithInsight = studyMilestones.filter((m: any) => m.state === 'closed' && m.insightFinal?.id);
    screen = (
      <InsightFinalDetailScreen
        insight={view.insight}
        session={session}
        clubId={insightClubId}
        closedMilestones={closedWithInsight}
        onOpenInsight={(insight: any) => setView({ kind: 'insight-detail', insight, clubId: insightClubId, postId: insight?.id })}
        onOpenMilestoneFeed={(milestoneId: string) => {
          // Navega pro estudo e foca o feed daquele marco específico
          setView({ kind: 'estudo', clubId: insightClubId });
          // Marca pra expandir o ClosedMilestone alvo
          setExpandMilestoneOnEstudo(milestoneId);
        }}
        onBack={goBack}
      />
    );
  } else {
    switch (tab) {
      case 'home':
        screen = interfaceNova ? (
          <HomeNova
            session={session}
            books={books}
            streak={streak}
            travessias={travessias}
            carregando={primeiraCarga}
            onRegistrar={(b) => startLog(b)}
            onTravessia={openTravessia}
            onAbrirItem={abrirItemDaTravessia}
            onAbrirLivro={openBook}
            onExplorar={() => setTab('explore')}
            onAdicionarLivro={() => setAddOpen(true)}
            onCaderno={(b) => openBookStudy(b, null, !!travessias[b.id])}
            pro={souPro}
            onPro={() => setProAberto('vitrine')}
            aviso={acessoApp?.estado === 'teste' ? <AvisoTeste acesso={acessoApp} onAssinar={() => setProAberto('vitrine')} onde="home" /> : undefined}
          />
        ) : (
          <HomeScreen
            carregando={primeiraCarga}
            nomeProvisorio={nomeDoCadastro}
            habito={habito}
            onAbrirLivroDoProtocolo={abrirSessaoDoProtocolo}
            onVerLivro={openBookById}
            onComecarProtocolo={() => setPreparoOpen(true)}
            onVerPrograma={() => setView({ kind: 'programa' } as any)}
            onEditarContrato={() => setEditarContratoAberto(true)}
            onAbrirCirculo={circuloLigado ? (slug) => setView({ kind: 'mentor', slug } as any) : undefined}
            onAbrirMeuCirculo={circuloLigado ? () => setView({ kind: 'circuloEditor' } as any) : undefined}
            onMarcoVisto={async (chave) => {
              try { await habitoApi.marcarMarcoVisto(session, chave); } catch {}
              await recarregarHabito();
            }}
            onTrocarLivro={() => setTrocarAberto(true)}
            ofereceTroca={ofereceTrocaDeLivro}
            session={session}
            profile={profile}
            onOpenProfile={openProfile}
            books={books}
            streak={streak}
            totals={homeTotals}
            dayPagesRead={dayPagesRead}
            dayTotalGoal={dayTotalGoal}
            goalViewUnit={goalViewUnit}
            onGoalViewUnit={setGoalViewUnit}
            unitsPresent={unitsPresent}
            dayDone={dayDone}
            dailyDoneFor={dailyDoneFor}
            today={today}
            isPro={isPro}
            maxReading={limits.maxReading}
            dailyQuote={dailyQuote}
            // Passa só os clubes que já têm livro (compartilhado em group-read
            // OU pessoal do membro em shared-habit). Sem esse OR, clubes avulsos
            // somem da Home porque c.book continua null.
            clubsWithBooks={clubs.filter((c: any) =>
              (c.book && c.book.title) || (c.myBook && c.myBook.title)
            )}
            clubsTerm={clubsTerm}
            onOpenClub={(clubId) => setView({ kind: 'club-inner', clubId })}
            onClubBookRead={async (clubId) => {
              if (!session) return;
              // shared-habit: livro já é pessoal — abre LogReadingSheet direto.
              const c: any = clubs.find((x: any) => x.id === clubId);
              if (c?.mode === 'shared-habit') {
                const myBookId = c?.myBook?.id;
                const local = myBookId ? books.find(b => b.id === myBookId) : null;
                if (local) startLog(local);
                return;
              }
              try {
                const r = await api.startReadingClubBook(session, clubId);
                const personalBook = r.book;
                // Merge cuidadoso: preserva sessions locais se backend retornar array vazio.
                setBooks(prev => {
                  const idx = prev.findIndex(b => b.id === personalBook.id);
                  if (idx >= 0) {
                    const next = [...prev];
                    const existingSessions = prev[idx].sessions || [];
                    const incomingSessions = personalBook.sessions || [];
                    next[idx] = {
                      ...prev[idx], ...personalBook,
                      sessions: incomingSessions.length > 0 ? incomingSessions : existingSessions,
                    };
                    return next;
                  }
                  return [personalBook, ...prev];
                });
                startLog(personalBook);
              } catch (e: any) {
                window.alert(e?.message || 'Erro ao abrir livro do clube');
              }
            }}
            onOpenBook={openBook}
            onRead={startLog}
            onStudy={(b: any) => openBookStudy(b)}
            mentorNovas={mentorNovas}
            travessias={travessias}
            onTravessia={openTravessia}
            onBookActions={(b: any) => setBookActions(b)}
            onClubActions={(club: any, mirror: any) => setClubActions({ club, mirror })}
            onExploreClubs={() => setTab('explore')}
            onOpenReport={() => isPro ? setView({ kind: 'report' }) : openPaywall()}
            onOpenPaywall={openPaywall}
            onOpenNotif={handleOpenNotif}
            onOpenSupport={() => setSupportOpen(true)}
            onOpenLibrary={() => setView({ kind: 'library' })}
            onAddBook={() => setAddOpen(true)}
            onActivatePush={() => activatePush()}
            pushSubscribed={pushStatus.subscribed}
            hasAnyReading={(totals?.pages ?? 0) > 0}
            notifUnreadCount={notifUnread}
          />
        );
        break;
      case 'clubs':
        screen = (
          <ClubsHubScreen
            term={clubsTerm}
            clubs={clubs}
            pendingInvites={pendingInvites}
            exploreClubs={exploreClubs}
            loading={clubsLoading}
            proposals={myProposals}
            onOpenClub={(clubId) => setView({ kind: 'club-inner', clubId })}
            onOpenProposta={(clubId) => setView({ kind: 'proposta', clubId })}
            onCreate={() => setView({ kind: 'club-create' })}
            onPropose={() => setView({ kind: 'club-create', propose: true })}
            onAcceptInvite={(token) => handleAcceptInvite(token)}
            onRejectInvite={(token) => handleRejectInvite(token)}
            onExploreAll={() => setView({ kind: 'club-explore' })}
          />
        );
        break;
      case 'explore':
        screen = interfaceNova ? (
          <ExplorarNova
            session={session}
            books={books}
            onEstanteMudou={() => api.getLibrary(session).then(setBooks).catch(() => {})}
            onComecar={comecarTravessiaDaFicha}
            onAbrirLivro={openBook}
            acessoTrilhas={acessoTrilhas}
            pro={souPro}
            onPro={() => setProAberto('vitrine')}
          />
        ) : (
          <ExploreScreen
            session={session}
            onBack={() => setTab('home')}
            onOpenClub={(clubId: string) => setView({ kind: 'club-inner', clubId })}
            onOpenProfile={openProfile}
            onUpgrade={() => openPaywall('reader', 'Explorar')}
            onAcervoMudou={() => { api.getLibrary(session).then(setBooks).catch(() => {}); }}
            minhaEstante={books}
            onOpenBook={(b: any) => openBook(b)}
          />
        );
        break;
      case 'stats':
        screen = isPro
          ? <StatsScreen streak={streak} heatmap={heatmap} achievements={achievements} analytics={analytics} onOpenReport={() => setView({ kind: 'report' })}/>
          : <LockedFeatureScreen
              title="Ritmo"
              subtitle="Estatísticas e conquistas"
              requiredPlan="reader"
              currentPlan={(profile?.plan as any) || 'free'}
              onUpgrade={() => openPaywall('reader', 'Ritmo')}/>;
        break;
      case 'me':
        screen = (interfaceNova && !perfilSocialAberto) ? (
          <PerfilNovo
            session={session}
            profile={profile}
            books={books}
            streak={streak}
            travessias={travessias}
            notifNaoLidas={notifUnread}
            onAbrirLivro={openBook}
            onTravessia={openTravessia}
            onExplorar={() => setTab('explore')}
            pro={souPro}
            onPro={() => setProAberto('vitrine')}
            aviso={acessoApp?.estado === 'teste' ? <AvisoTeste acesso={acessoApp} onAssinar={() => setProAberto('vitrine')} onde="perfil" /> : undefined}
            onCadernos={() => setView({ kind: 'cadernos' } as any)}
            onAbrirPessoa={openProfile}
            onPerfilMudou={refreshProfile}
            menu={{
              social: () => setPerfilSocialAberto(true),
              ajustes: () => setView({ kind: 'settings' } as any),
              cadernos: () => setView({ kind: 'cadernos' } as any),
              ritmo: () => setTab('stats'),
              protocolo: () => setView({ kind: 'programa' } as any),
              clubes: () => setTab('clubs'),
              biblioteca: () => setView({ kind: 'library' } as any),
              notificacoes: () => handleOpenNotif(),
              suporte: () => setSupportOpen(true),
              convidar: convidarAmigos,
              circulo: circuloLigado ? (slug: string) => setView({ kind: 'mentor', slug } as any) : undefined,
              meuCirculo: circuloLigado ? () => setView({ kind: 'circuloEditor' } as any) : undefined,
            }}
          />
        ) : (
          <ProfileSocialScreen
            onBack={interfaceNova ? () => setPerfilSocialAberto(false) : undefined}
            session={session}
            currentUserId={profile?.id}
            onOpenSettings={() => setView({ kind: 'settings' } as any)}
            onOpenClub={(clubId: string) => setView({ kind: 'club-inner', clubId })}
            onGoToEstudo={(clubId: string) => setView({ kind: 'estudo', clubId })}
            onOpenProfile={openProfile}
            onUpgrade={() => openPaywall('reader', 'Perfil')}
            onAddBook={() => { setTab('home'); setAddOpen(true); }}
            onExploreClubs={() => setTab('explore')}
            onProfileUpdated={refreshProfile}
            onOpenBook={openBookById}
            myBooks={books}
            myShelves={shelves}
            estantes={estantesDoPerfil}
            onAbrirEstudos={() => setView({ kind: 'cadernos' })}
            souMentor={circuloLigado === true && (profile as any)?.is_mentor === true}
            onAbrirMeuCirculo={() => setView({ kind: 'circuloEditor' } as any)}
            onVerMinhaPagina={(slug: string) => setView({ kind: 'mentor', slug } as any)}
          />
        );
        break;
    }
  }

  // TabBar fica visível em qualquer view de aba normal e nas telas internas de clube
  // (hub, club-inner, estudo) — escondida no fluxo de criação, empty state pós-criação
  // e na caixa de perguntas (foco no líder).
  const isClubFlowFullscreen = ['club-create', 'club-empty', 'club-question-box', 'club-explore', 'admin'].includes(view.kind);
  const showTabBar = (view.kind === 'tab') || (view.kind === 'club-inner') || (view.kind === 'estudo') || (view.kind === 'proposta');

  // Mapear o tab "atual" pra destaque na nav quando estiver dentro de telas de clube.
  // Qualquer subpágina de clube destaca a aba "Clube/Grupo".
  const isClubScope = ['club-inner', 'estudo', 'club-create', 'club-empty', 'proposta',
    'club-analytics', 'club-question-box', 'insight-detail', 'club-explore'].includes(view.kind);
  const currentTabHighlight: string = isClubScope ? 'clubs' : tab;

  // (legal routes já tratadas no topo do render)

  // Página /planos é standalone — escapa do app-shell. Renderizamos AQUI no
  // final pra que `session` (caso logado) possa ser passada pro checkout.
  if (isPlansRoute) {
    // O app é grátis desde 14/10/2026: /planos não vende mais plano do app
    // (pagaria por nada — e o SKU reader agora é a assinatura de um Mentor).
    // Links antigos (e-mails, magic link do iOS) caem neste aviso.
    return <PlanosAposentados logado={!!session} />;
  }

  // FP4 — valores dos providers de menção (nav + dados de autocomplete). Objetos
  // planos: o custo de re-render dos chips é desprezível (spans baratos).
  const mentionNav = {
    openProfile, openBook: openBookById, openClub: (id: string) => setView({ kind: 'club-inner', clubId: id }),
    // user @username plano → resolve handle→id (reusa o resolver público do deep-link) → abre perfil
    openProfileByHandle: async (handle: string) => { try { const r = await api.getProfilePreview(handle); if (r && r.id) openProfile(r.id); } catch { /* handle inexistente: noop */ } },
  };
  // Dentro de um clube, os membros dele também podem ser @mencionados — antes
  // só apareciam os mútuos (quem se segue), e no clube quase ninguém se segue.
  const clubeDaTela = (view as any).clubId && activeClub?.id === (view as any).clubId ? activeClub : null;
  // (Cálculo direto, sem useMemo: aqui já passou dos returns antecipados.)
  const pessoasParaMencao = (() => {
    const membros = ((clubeDaTela?.membersList || []) as any[])
      .filter((m: any) => m?.userId && m.username && m.userId !== profile?.id)
      .map((m: any) => ({ id: m.userId, name: m.name, username: m.username, avatarUrl: null }));
    if (!membros.length) return mentionPeople;
    const ja = new Set((mentionPeople || []).map((p: any) => p.id));
    return [...membros.filter((m) => !ja.has(m.id)), ...(mentionPeople || [])];
  })();
  const mentionData = { people: pessoasParaMencao, books: (books || []).map((b: any) => ({ id: b.id, title: b.title, coverUrl: b.coverUrl })), clubs: (clubs || []).map((c: any) => ({ id: c.id, name: c.name })) };

  return (
    <MentionNavProvider value={mentionNav}>
    <MentionDataProvider value={mentionData}>
    <div className="app-shell">
      {/* A barra do teste de 7 dias saiu em 14/10/2026: o app é grátis. */}
      {screen}
      {showTabBar && !isClubFlowFullscreen && (
        <TabBar
          current={currentTabHighlight}
          onChange={(t) => {
            // setTab já navega pra URL da aba e o sync de URL seta view={kind:'tab'}.
            // NÃO chamar setView({kind:'tab'}) junto: ele usaria o `tab` ANTIGO do
            // closure e dispararia uma 2ª navegação que sobrescreve a correta —
            // era a causa do "menu volta pro Clube antes de ir pra aba" (2 cliques).
            setTab(t as Tab);
          }}
          onAdd={() => {
            // Botao + e dinamico baseado na view atual:
            // - club-inner → abre o Estudo do livro atual
            // - estudo     → abre o ComposeSheet (compartilhar insight)
            // - tab clubs  → criar novo clube
            // - demais     → adicionar livro avulso
            if (view.kind === 'estudo') {
              setComposeReplyTo(null);
              setComposeOpen(true);
              return;
            }
            if (view.kind === 'club-inner') {
              setView({ kind: 'estudo', clubId: view.clubId, bookId: activeClub?.book?.id });
              return;
            }
            if (currentTabHighlight === 'clubs') {
              setView({ kind: 'club-create' });
              return;
            }
            setAddOpen(true); // biblioteca ilimitada — o cap é só nas 2 leituras 'lendo'
          }}
          clubsTerm={clubsTerm}
          urls={{
            home: '/',
            clubs: '/grupos',
            explore: '/explorar',
            me: '/eu',
          }}
          // Pulso no botão central pra user novo (sem livros). Sai assim que
          // adicionar primeiro livro — chama atenção sem ser invasivo.
          pulseAdd={books.length === 0 && currentTabHighlight === 'home'}
        />
      )}

      {/* Action sheet do clube (menu "...") */}
      {clubMenuOpen && (view.kind === 'club-inner' || view.kind === 'estudo') && (
        <div className="modal-layer on" style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
          <ActionSheet
            term={clubsTerm}
            isAdmin={activeClub?.yourRole === 'owner' || activeClub?.yourRole === 'admin'}
            isOwner={activeClub?.yourRole === 'owner'}
            hasBook={!!activeClub?.book}
            pendingQuestions={pendingQboxCount}
            activeMilestonesCount={studyMilestones.filter((m: any) => m.state === 'active').length}
            onClose={() => setClubMenuOpen(false)}
            onShare={() => { setClubMenuOpen(false); if (view.clubId || activeClub?.id) setClubShareTargetId(view.clubId || activeClub?.id || null); setClubShareOpen(true); }}
            onAnalytics={() => {
              setClubMenuOpen(false);
              const clubId = (view as any).clubId;
              setView({ kind: 'club-analytics', clubId });
            }}
            onQuestionBox={() => {
              setClubMenuOpen(false);
              const clubId = (view as any).clubId;
              setView({ kind: 'club-question-box', clubId });
            }}
            onEditBook={() => { setClubMenuOpen(false); setEditClubBookOpen(true); }}
            onMilestones={() => { setClubMenuOpen(false); setMilestonesSheetOpen(true); }}
            onEditClub={() => { setClubMenuOpen(false); setEditClubOpen(true); }}
            onLeave={() => {
              setClubMenuOpen(false);
              const clubId = (view as any).clubId;
              if (clubId && activeClub) handleLeaveClub(clubId, activeClub.name);
            }}
            onDelete={() => {
              setClubMenuOpen(false);
              const clubId = (view as any).clubId;
              if (clubId && activeClub) handleDeleteClub(clubId, activeClub.name);
            }}
            onReport={() => {
              setClubMenuOpen(false);
              const clubId = (view as any).clubId;
              if (clubId && activeClub) {
                setReportTarget({ kind: 'club', id: clubId, label: `Clube: ${activeClub.name}` });
              }
            }}
          />
        </div>
      )}

      {/* Compose do Estudo: o botao + central do TabBar dispara setComposeOpen.
          O FAB flutuante "Compartilhar" foi removido. */}
      {view.kind === 'estudo' && (
        <ComposeSheet
          open={composeOpen}
          onClose={() => { setComposeOpen(false); setComposeReplyTo(null); }}
          term={clubsTerm}
          isAdmin={activeClub?.yourRole === 'owner' || activeClub?.yourRole === 'admin'}
          allowMemberQuiz={!!activeClub?.allowMemberQuiz}
          replyTo={composeReplyTo}
          onPickQuestionBox={() => {
            // Caixa de perguntas tem tela dedicada full-screen — redireciona pra lá
            setComposeOpen(false);
            setComposeReplyTo(null);
            setView({ kind: 'club-question-box', clubId: view.clubId, initialPhase: 'create' });
          }}
          activeMilestoneLabel={
            (() => {
              const active = studyMilestones.find((m: any) => m.state === 'active');
              return active?.label || undefined;
            })()
          }
          defaultPage={
            // Sugere a página atual do livro pessoal espelhado
            (() => {
              const personalId = activeClub?.book?.personalBookId;
              if (!personalId) return undefined;
              const b = books.find(x => x.id === personalId);
              return b?.currentPage || b?.read || undefined;
            })()
          }
          onSubmit={async (payload) => {
            if (!session) return;
            const r = await api.createPost(session, view.clubId, payload);
            // Recarrega feed + marcos (se foi insight-final, marco fechou)
            const [ps] = await Promise.all([
              api.listPosts(session, view.clubId, { filter: studyFilter, limit: 30 }),
              reloadMilestones(),
            ]);
            setStudyPosts(ps.posts || []);
            setComposeReplyTo(null);
          }}
        />
      )}

      {/* Sheet de marcos do clube (admin) */}
      {/* Inbox de cutucadas (pokes) recebidas. Disponível em qualquer tela. */}
      <PokesSheet
        open={pokesOpen}
        onClose={() => setPokesOpen(false)}
        session={session}
        onAfterRead={() => setPokesUnread(0)}
        onOpenClub={(clubId: string) => setView({ kind: 'club-inner', clubId })}
      />

      {/* Modal de Suporte — abre via icone na home, envia ticket pro admin */}
      <SupportSheet
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        session={session}
      />

      {/* Modal de Denuncia UGC (Apple Guideline 1.2). Acionado via menu do
          clube ou long-press num post. */}
      <ReportSheet
        open={!!reportTarget}
        targetLabel={reportTarget?.label || ''}
        onClose={() => setReportTarget(null)}
        onSubmit={async (reason) => {
          if (!session || !reportTarget) return;
          if (reportTarget.kind === 'club') {
            await api.reportClub(session, reportTarget.id, reason);
          } else {
            await api.reportPost(session, reportTarget.id, reason);
          }
        }}
      />

      <MilestonesSheet
        open={milestonesSheetOpen}
        onClose={() => setMilestonesSheetOpen(false)}
        term={clubsTerm}
        bookTotalPages={activeClub?.book?.totalPages}
        milestones={studyMilestones}
        loading={studyLoading && studyMilestones.length === 0}
        onCreate={async (payload) => {
          if (!session || !activeClub?.id) return;
          await api.createMilestone(session, activeClub.id, payload);
          await reloadMilestones();
        }}
      />

      {/* Sheet de editar dados do clube (admin) — nome, descrição, privacy, regras */}
      <EditClubSheet
        open={editClubOpen}
        onClose={() => setEditClubOpen(false)}
        session={session}
        clubId={activeClub?.id || (view as any).clubId || null}
        initialClub={activeClub}
        term={clubsTerm}
        onSaved={async () => {
          if (!session || !activeClub?.id) return;
          // Recarrega o clube ativo + lista
          const c = await api.getClub(session, activeClub.id);
          setActiveClub(c);
          loadClubs();
        }}
      />

      {/* Sheet de editar livro do clube (admin). Usa editClubBookTarget quando
          o admin tap num card especifico do coverflow; senao cai no livro atual. */}
      <EditClubBookSheet
        open={editClubBookOpen}
        onClose={() => { setEditClubBookOpen(false); setEditClubBookTarget(null); }}
        term={clubsTerm}
        initial={(editClubBookTarget || activeClub?.book) ? {
          title: (editClubBookTarget || activeClub.book).title,
          author: (editClubBookTarget || activeClub.book).author,
          totalPages: (editClubBookTarget || activeClub.book).totalPages,
          startPage: (editClubBookTarget || activeClub.book).startPage || 1,
          coverUrl: (editClubBookTarget || activeClub.book).coverUrl || null,
          coverHue: (editClubBookTarget || activeClub.book).coverHue,
          buyUrl: (editClubBookTarget || activeClub.book).buyUrl || null,
        } : null}
        onSave={async (patch) => {
          if (!session || !activeClub?.id) return;
          if (editClubBookTarget?.id) {
            // Edita o livro especifico (qualquer um da fila)
            await api.patchClubBookById(session, activeClub.id, editClubBookTarget.id, patch);
          } else {
            // Edita o livro atual (compat com fluxo antigo)
            await api.patchClubBook(session, activeClub.id, patch);
          }
          // Recarrega o clube ativo
          const c = await api.getClub(session, activeClub.id);
          setActiveClub(c);
          loadClubs();
          setEditClubBookTarget(null);
        }}
      />

      {/* Share modal do clube */}
      {clubShareOpen && (() => {
        const resolvedClubId = clubShareTargetId || (view as any).clubId || activeClub?.id;
        fetch('/api/debug-log', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tag: 'club-share-render', data: `target=${clubShareTargetId} viewClubId=${(view as any).clubId} activeId=${activeClub?.id} resolved=${resolvedClubId}` })
        }).catch(()=>{});
        return (
          <div className="modal-layer on" style={{ position: 'fixed', inset: 0, zIndex: 90 }}>
            <ClubShareScreen
              term={clubsTerm}
              session={session}
              clubId={resolvedClubId}
              clubName={activeClub?.name}
              clubBookTitle={activeClub?.book?.title}
              onClose={() => { setClubShareOpen(false); setClubShareTargetId(null); }}
            />
          </div>
        );
      })()}

      {bookActions && (
        <BookActionsSheet
          book={bookActions}
          onClose={() => setBookActions(null)}
          onUpdateGoal={applyGoalToBook}
          onSyncPage={handleSyncPage}
          onEditDetails={async (id: string, patch: any) => {
            if (!session) return;
            const updated = await api.updateBook(session, id, patch);
            setBooks(prev => prev.map(b => b.id === id ? { ...b, ...updated } : b));
          }}
          onDelete={async (id: string) => {
            if (!session) return;
            // Só sai da lista depois do sucesso (pente fino, QA-21) — o mesmo
            // padrão de handleDeleteBook. A falha sobe para a folha, que a
            // mostra; antes o livro sumia e voltava sozinho ao recarregar.
            try { await api.deleteBook(session, id); }
            catch (e: any) { throw new Error(e?.message && !/fetch/i.test(e.message) ? e.message : 'Não consegui remover o livro. Confira a conexão e tente de novo.'); }
            setBooks(prev => prev.filter(b => b.id !== id));
          }}
          onOpenBook={() => { const b = bookActions; setBookActions(null); openBook(b); }}
          shelves={shelves as any}
          onCreateShelf={async (name: string) => { if (!session) return undefined; const s = await api.createShelf(session, name); setShelves(prev => [...prev, s]); return s; }}
          onAddToShelf={async (bookId: string, shelfId: string) => { if (!session) return; await api.addBookToShelf(session, shelfId, bookId); setBooks(prev => prev.map(b => b.id === bookId ? { ...b, shelfIds: Array.from(new Set([...(b.shelfIds || []), shelfId])) } : b)); setShelves(prev => prev.map(s => s.id === shelfId ? { ...s, count: (s.count || 0) + 1 } : s)); }}
          onRemoveFromShelf={async (bookId: string, shelfId: string) => { if (!session) return; await api.removeBookFromShelf(session, shelfId, bookId); setBooks(prev => prev.map(b => b.id === bookId ? { ...b, shelfIds: (b.shelfIds || []).filter(x => x !== shelfId) } : b)); setShelves(prev => prev.map(s => s.id === shelfId ? { ...s, count: Math.max(0, (s.count || 0) - 1) } : s)); }}
        />
      )}

      {clubActions && (
        <ClubBookActionsSheet
          club={clubActions.club}
          mirror={clubActions.mirror}
          term={clubsTerm}
          onClose={() => setClubActions(null)}
          onSyncInitial={(page: number) => handleClubSyncPage(clubActions.club.id, page)}
          onLeave={() => handleLeaveClub(clubActions.club.id, clubActions.club.name)}
          onDelete={() => handleDeleteClub(clubActions.club.id, clubActions.club.name)}
        />
      )}

      <AddBookSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAddBook}
        onLookupISBN={(isbn) => api.lookupISBN(session, isbn)}
        onSearchBooks={(q) => api.searchBooks(session, q)}
        onUpgrade={() => openPaywall('reader', 'Mais livros ao mesmo tempo')}
        shelves={shelves}
      />

      {/* Sheet espelhado para adicionar/trocar o livro do clube.
          - group-read: admin define livro compartilhado via setClubBook.
          - shared-habit: cada membro cria seu livro pessoal vinculado ao clube
            (from_club_id). Em ambos os modos o visual é o mesmo (AddBookSheet). */}
      <AddBookSheet
        open={addClubBookOpen}
        showStatus={false}
        onClose={() => { setAddClubBookOpen(false); setAddClubBookFor(null); }}
        onAdd={async (b) => {
          if (!session || !addClubBookFor) return;
          const isHabit = activeClub?.mode === 'shared-habit';
          if (isHabit) {
            // F4: Avulso agora cria entry em club_member_books + books num so
            // request (endpoint /api/clubs/:id/member-books). Mantem comportamento
            // pra UX (livro aparece na biblioteca pessoal) ja que o backend
            // cria o  com from_club_id e status='lendo' se for o
            // primeiro, 'proximo' se for adicional na fila.
            const r = await api.addMemberBook(session, addClubBookFor, {
              title: b.title,
              author: b.author,
              totalPages: b.totalPages,
              dailyGoal: b.dailyGoal,
              coverUrl: b.coverSourceUrl || null,
              progressUnit: (b as any).progressUnit || 'pages',
            });
            if (r?.memberBook?.book) {
              // Anexa em books pra UI ja ver o livro novo. Mapeia colunas do
              // banco pro shape BookV2 do frontend.
              const bk = r.memberBook.book;
              // O servidor usou o livro que a pessoa já tinha na estante: ele
              // só ganhou o vínculo com o clube, com a página e o histórico dele.
              if (r.livroProprio) {
                setBooks(prev => prev.map(x => x.id === bk.id ? { ...x, fromClubId: bk.from_club_id, status: ({ lendo: 'reading', proximo: 'wishlist', parado: 'paused' } as any)[bk.status] || x.status } as any : x));
                toast.info(`Usei o "${bk.title}" que já estava na sua estante, na ${bk.current_page ? `p. ${bk.current_page}` : 'página em que você está'}.`);
              } else
              setBooks(prev => [{
                id: bk.id,
                title: bk.title,
                author: bk.author,
                totalPages: bk.total_pages,
                currentPage: bk.current_page || 0,
                dailyGoal: bk.daily_goal,
                coverUrl: bk.cover_url,
                coverHue: bk.cover_hue,
                coverStyle: bk.cover_style,
                status: bk.status,
                fromClubId: bk.from_club_id,
                startedAt: bk.started_at,
                targetDate: bk.target_date,
                progressUnit: bk.progress_unit || 'pages',
                sessions: [],
              } as any, ...prev]);
            }
          } else {
            await api.setClubBook(session, addClubBookFor, {
              title: b.title,
              author: b.author,
              totalPages: b.totalPages,
              coverUrl: b.coverSourceUrl || null,
            });
          }
          // Recarrega o clube ativo se estivermos olhando ele
          if (view.kind === 'club-inner' && view.clubId === addClubBookFor) {
            try {
              const c = await api.getClub(session, view.clubId);
              setActiveClub(c);
            } catch {}
          }
          // E atualiza a lista (pra refletir no Hub e no Home)
          loadClubs();
        }}
        onLookupISBN={(isbn) => api.lookupISBN(session, isbn)}
        onSearchBooks={(q) => api.searchBooks(session, q)}
      />
      {/* QA-20: uma sessão de foco ficou em andamento quando o app fechou. */}
      {sessaoFocoPendente && !logOpen && (() => {
        const livro = books.find((b) => b.id === sessaoFocoPendente.bookId);
        if (!livro) return null;
        const min = Math.max(1, Math.round(segundosLidos(sessaoFocoPendente, Date.now()) / 60));
        const desde = new Date(sessaoFocoPendente.iniciadaEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        const fechar = () => setSessaoFocoPendente(null);
        return (
          <div role="dialog" aria-label="Leitura em andamento" data-foco-pendente style={{
            position: 'fixed', left: 12, right: 12, bottom: 'calc(92px + env(safe-area-inset-bottom, 0px))', zIndex: 9000,
            maxWidth: 440, margin: '0 auto', background: 'var(--paper-2)', border: '1px solid var(--ember)',
            borderRadius: 18, padding: '14px 16px', boxShadow: '0 16px 40px rgba(0,0,0,.45)',
          }}>
            <div style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.45 }}>
              Você tinha uma sessão de <strong>{min} min</strong> em <em>{livro.title}</em> (desde {desde}). Registrar?
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => { fechar(); setRegistrarSessaoSalva(true); startLog(livro, 'foco'); }} style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: 'none', background: 'var(--ember)', color: '#1c1208', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>Registrar</button>
              <button onClick={() => { fechar(); setRegistrarSessaoSalva(false); startLog(livro, 'foco'); }} style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--line-strong)', background: 'transparent', color: 'var(--ink)', fontSize: 13.5, cursor: 'pointer' }}>Continuar</button>
              <button onClick={() => { fechar(); descartarSessaoFoco(); }} style={{ padding: '10px 12px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 13.5, cursor: 'pointer' }}>Descartar</button>
            </div>
          </div>
        );
      })()}

      <LogReadingSheet
        open={logOpen}
        book={logBook}
        registrarSessaoSalva={registrarSessaoSalva}
        onClose={() => { setLogOpen(false); setRegistrarSessaoSalva(false); }}
        onSave={handleSaveLog}
        onSaveSegment={handleSaveSegment}
        onOpenReadings={() => setView({ kind: 'readings' })}
        onSyncPage={handleSyncPage}
        onOpenStudy={(bookId: string, page: number) => { const b = books.find(x => x.id === bookId) || logBook; if (b) openBookStudy(b, page); }}
        trilhaNovas={avisoTrilha && logBook && avisoTrilha.bookId === logBook.id ? avisoTrilha.n : 0}
        onOpenTrilha={(bookId: string) => { setAvisoTrilha(null); const b = books.find(x => x.id === bookId) || logBook; if (b) abrirTrilha(b); }}
        travessia={!!(logBook && travessias[logBook.id])}
        aposSalvarNoMapa={interfaceNova ? (bookId: string) => { const b = books.find(x => x.id === bookId) || logBook; if (b) openTravessia(b); } : undefined}
        onDispensarTrilha={() => setAvisoTrilha(null)}
        modoInicial={logModo}
        pausado={pausaFolha || !!achievement || !!celebracao}
      />

      <AchievementModal
        open={!!achievement}
        payload={achievement}
        onClose={() => setAchievement(null)}
      />

      <TutorialOverlay
        open={tutorialOpen}
        onFinish={finishTutorial}
        onSkip={finishTutorial}
        onRequestTab={(t) => setTab(t as Tab)}
      />

      {/* A entrada curta da conta nova na interface nova: no lugar do preparo
          e do tutorial antigo. Vista uma vez, fica marcada no perfil. */}
      {entradaAberta && (
        <EntradaCurta
          nome={(nomeDoCadastro || profile?.display_name || '').trim().split(' ')[0] || undefined}
          session={session}
          onComecar={async (slug) => {
            await comecarTravessiaDaFicha(slug, true);
            fecharEntrada();
          }}
          onExplorar={() => { setTab('explore'); fecharEntrada(); }}
        />
      )}

      {/* Protocolo do Hábito — Estágios 0 e 1. É a primeira tela de quem acabou
          de criar a conta, e ela tem precedência sobre o tutorial de perfil. */}
      {preparoOpen && habito && (
        <PreparoDoHabito
          session={session}
          estado={habito}
          meusLivros={books}
          nome={(profile?.display_name || '').trim().split(' ')[0] || undefined}
          onConcluir={async (opts) => {
            setPreparoOpen(false);
            const h = await recarregarHabito();
            // Quem dispensou volta para o app de sempre — e o tutorial de perfil
            // antigo deixa de ficar pendente, porque para essa pessoa ele nunca
            // foi adiado por causa do protocolo.
            if (opts?.dispensou) { await loadAllData(); return; }
            // Terminou o preparo: a chave nasce do lado do protocolo.
            try { await habitoApi.trocarModo(session, 'protocolo'); } catch {}
            await loadAllData();
            if (opts?.lerAgora && h?.livro?.id) setLivroPendente(h.livro.id);
          }}
        />
      )}

      {/* O diagnóstico faz a pergunta certa; aqui é onde ela pode ser respondida. */}
      <EditarContrato
        aberto={editarContratoAberto}
        session={session}
        estado={habito}
        onFechar={() => setEditarContratoAberto(false)}
        onSalvo={async () => { await recarregarHabito(); }}
      />

      {/* Trocar de livro é curadoria, não desistência — e não toca na sequência. */}
      {habito?.inscricao && (
        <TrocarLivro
          open={trocarAberto}
          session={session}
          interesse={habito.inscricao.interesse}
          livroAtual={habito.livro ? { id: habito.livro.id, titulo: habito.livro.titulo } : null}
          meusLivros={books}
          onAdicionarLivro={() => setAddOpen(true)}
          onFechar={() => {
            setTrocarAberto(false);
            // Recusou uma vez: a oferta não volta para este livro.
            try { if (habito.livro) localStorage.setItem(`leitura.trocaOferecida.${habito.livro.id}`, '1'); } catch {}
          }}
          onTrocado={async () => { await recarregarHabito(); await loadAllData(); }}
        />
      )}

      {/* A formatura: números → veredito honesto → o que vem agora → e só então
          o selo. O próximo objetivo vem ANTES do troféu porque depois de um
          marco entregue a atividade cai. */}
      {/* A guarda de RETORNO estava aqui e a Formatura foi esquecida: como ela
          tem zIndex maior, quem voltava de um sumiço depois do dia 67 recebia o
          questionário por cima de "Que bom que você voltou" — violando o
          invariante 5, que diz que o retorno tem prioridade sobre tudo. */}
      {habito?.podeFormar && !formaturaAdiada && !preparoOpen && !sessaoAberta && !celebracao
        && habito.estado !== 'RETORNO' && (
        <Formatura
          session={session}
          estado={habito}
          onAdiar={() => {
            // Adiar três vezes é uma resposta. A partir daí a formatura para de
            // se abrir sozinha e passa a esperar na área do Programa — senão ela
            // volta a cada 72 h, para sempre, dizendo "você chegou ao dia 67"
            // para quem já está no dia 200.
            const n = Number(localStorage.getItem('leitura.formaturaAdiou') || 0) + 1;
            try { localStorage.setItem('leitura.formaturaAdiou', String(n)); } catch {}
            adiarPor('leitura.formaturaAdiada', n >= 3 ? 24 * 365 : 72);
            setFormaturaAdiada(true);
          }}
          onConcluir={async () => { setFormaturaAdiada(true); await recarregarHabito(); }}
          onEditarPlano={() => setEditarContratoAberto(true)}
          onEscolherLivro={() => setTrocarAberto(true)}
        />
      )}

      {/* Medição periódica (dias 21 e 45): dias seguidos medem o uso do app,
          estas quatro perguntas medem o hábito. */}
      {/* A guarda de estado faltava: `medicao` é montado ANTES do return de
          LEITOR/DISPENSADO, então quem passava do dia 21 sem responder e trocava
          para o modo Leitor (ou dispensava o programa) levava o questionário em
          tela cheia mesmo tendo saído do protocolo. */}
      {!habito?.podeFormar && habito?.medicao?.pedir && !medicaoAdiada
        && !preparoOpen && !sessaoAberta && !celebracao
        && !['RETORNO', 'LEITOR', 'DISPENSADO', 'EXPLORANDO'].includes(habito.estado) && (
        <MedicaoPeriodica
          session={session}
          onAdiar={() => { adiarPor('leitura.srbaiAdiado', 48); setMedicaoAdiada(true); }}
          onPronto={async () => { setMedicaoAdiada(true); await recarregarHabito(); }}
        />
      )}

      {/* O dia seguinte à falha é o momento mais importante do programa: a
          percepção de violação precisa ser desarmada antes de se instalar. */}
      {habito?.estado === 'RETORNO' && habito.lapso && !retornoAdiado
        && !preparoOpen && !sessaoAberta && !celebracao && (
        <TelaDeRetorno
          estado={habito}
          onAbrirLivro={() => {
            const id = habito.lapso!.id;
            habitoApi.marcarLapsoVisto(session, id, 'compaixao').catch(() => {});
            setSessaoAberta(true);
          }}
          onDepois={() => {
            const id = habito.lapso!.id;
            habitoApi.marcarLapsoVisto(session, id, 'compaixao').catch(() => {});
            setRetornoAdiado(true);
          }}
        />
      )}

      {/* A sessão sem decisões — o livro já aberto na página certa. */}
      {sessaoAberta && habito?.livro && (
        <SessaoSemDecisoes
          livro={habito.livro}
          minimoPaginas={habito.inscricao?.minimoPaginas || 1}
          onFechar={() => setSessaoAberta(false)}
          onSalvar={async (pagina, minutos, inicio) => {
            // De onde a leitura de HOJE partiu. Quando a pessoa ajusta a pagina
            // de inicio, o ponto de partida passa a ser ele — nao o paginaAtual
            // antigo, que ainda vale 0 neste instante porque o sync so roda
            // abaixo. Sem isto a celebracao comemora o prefacio inteiro: quem
            // comecou na 13 e leu ate a 14 via "+14 paginas" em vez de "+1".
            const antes = inicio != null ? inicio : habito.livro!.paginaAtual;
            const eraDia = habito.dia;
            // A pagina de inicio entra ANTES da leitura. Se entrasse depois, o
            // registro somaria a partir de 0 e o total de paginas lidas sairia
            // inflado pelo prefacio inteiro.
            if (inicio != null) {
              try { await handleSyncPage(habito.livro!.id, inicio); }
              catch (e) { toast.error('Não consegui ajustar a página inicial.'); return; }
            }
            try { await handleSaveLog(habito.livro!.id, pagina, minutos, { semCelebracao: true }); } catch (e) { toast.error('Não consegui salvar. Tente de novo.'); return; }
            setSessaoAberta(false);
            const h = await recarregarHabito();
            // Se esta leitura fechou um lapso, a celebração é a do RETORNO —
            // maior que a de qualquer dia perfeito.
            const ret = h?.retorno || null;
            if (ret) {
              try { await habitoApi.marcarLapsoVisto(session, ret.id, 'celebracao'); } catch {}
              setRetornoAdiado(false);
            }
            setCelebracao({
              dia: h?.dia ?? eraDia ?? null,
              paginas: Math.max(1, pagina - antes),
              primeiroDia: (h?.dia ?? 0) === 1 && !ret,
              // Superar a própria marca é o único momento de vitória que o app
              // já calculava e nunca dizia em voz alta.
              recorde: !!h && h.sequencia.atual > 1 && h.sequencia.atual >= h.sequencia.melhor,
              retorno: ret ? { dias: ret.dias, total: h?.retornos?.total } : null,
            });
          }}
        />
      )}

      {/* Os dez segundos depois de fechar o livro valem tanto quanto os trinta
          segundos antes de abrir. */}
      {celebracao && (
        <CelebracaoDoDia
          dia={celebracao.dia}
          paginas={celebracao.paginas}
          primeiroDia={celebracao.primeiroDia}
          retorno={celebracao.retorno}
          recorde={celebracao.recorde}
          perfil={habito?.inscricao?.perfil ?? null}
          sequencia={habito?.sequencia || { atual: 0, melhor: 0, diasLidos: 0 }}
          onFechar={() => setCelebracao(null)}
        />
      )}

      <OnboardingWizard
        open={onboardingOpen}
        session={session}
        apiBase={API_BASE}
        profile={profile}
        onAddBook={() => setAddOpen(true)}
        temLivro={books.length > 0}
        onFinish={() => {
          setOnboardingOpen(false);
          tutorialJaMostradoRef.current = true;
          try { api.markTutorialSeen(session, true); } catch {}
          try { api.setTutorialState(session, 'onboarding', { seen: true, enabled: false }); } catch {}
          setProfile((p: any) => p ? { ...p, tutorial_seen: true, tutorials_state: { ...(p.tutorials_state || {}), onboarding: { seen: true, enabled: false } } } : p);
        }}
      />

      <ExploreTour
        open={exploreTourOpen}
        onClose={() => {
          setExploreTourOpen(false);
          try { api.setTutorialState(session, 'explore', { seen: true, enabled: false }); } catch {}
          setProfile((p: any) => p ? { ...p, tutorials_state: { ...(p.tutorials_state || {}), explore: { seen: true, enabled: false } } } : p);
        }}
      />

      {/* Paywall agora é modal slim — abre /planos em nova aba pra fugir das
         taxas de IAP da Apple. Pagamento sempre via navegador externo. */}
      <PaywallModal
        open={paywallOpen}
        onClose={() => { setPaywallOpen(false); setPaywallSuggested(undefined); setPaywallFeatureLabel(undefined); }}
        session={session}
        currentPlan={(profile?.plan as any) || 'free'}
        suggestedPlan={paywallSuggested}
        featureLabel={paywallFeatureLabel}
      />
      {interfaceNova && proAberto === 'vitrine' && (
        <ProVitrine
          session={session}
          pro={souPro}
          trilhas={acessoTrilhas}
          diasDeTeste={acessoApp?.estado === 'teste' ? acessoApp.dias_restantes : null}
          testeEmEspera={acessoApp?.motivo === 'espera'}
          onFechar={() => setProAberto(null)}
          onAssinar={() => setProAberto('checkout')}
          onVerTemporada={() => { setProAberto(null); setView({ kind: 'tab' }); setTab('explore'); }}
        />
      )}
      {interfaceNova && (proAberto === 'checkout' || proAberto === 'trilha') && (
        <PaywallScreen
          variante="trilhas"
          session={session}
          currentPlan={(profile?.plan as any) || 'free'}
          preferredTerm={(profile?.preferred_term === 'grupo' || profile?.preferred_term === 'clube') ? profile.preferred_term : undefined}
          onClose={() => setProAberto((p) => (p === 'trilha' ? null : 'vitrine'))}
          onCheckout={isNativeApp() ? checkoutDoPaywall : undefined}
          origemDoClique="pro"
        />
      )}
      {planosDoTeste && (
        <PaywallScreen
          session={session}
          currentPlan={(profile?.plan as any) || 'free'}
          preferredTerm={(profile?.preferred_term === 'grupo' || profile?.preferred_term === 'clube') ? profile.preferred_term : undefined}
          onClose={() => setPlanosDoTeste(false)}
          onCheckout={checkoutDoPaywall}
          origemDoClique="barra"
        />
      )}
      <UpgradeCelebration open={upgradeCelebOpen} onClose={() => setUpgradeCelebOpen(false)}/>

      {/* Prompt push após primeira meta batida — 1x por user, dispensável. */}
      {pushPromptOpen && (
        <div
          onClick={() => setPushPromptOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,16,14,0.55)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 850, backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 480,
              background: 'var(--paper)', borderRadius: '22px 22px 0 0',
              padding: '28px 22px 32px',
              animation: 'push-prompt-up 250ms ease-out',
            }}
          >
            <div style={{
              width: 44, height: 4, borderRadius: 2,
              background: 'var(--line-strong)', margin: '0 auto 18px',
            }}/>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--ember-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--ember)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <div className="serif" style={{ fontSize: 26, lineHeight: 1.1, textAlign: 'center' }}>
              Que tal um <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>lembrete</span>?
            </div>
            <div style={{
              fontSize: 14, color: 'var(--ink-2)', textAlign: 'center',
              marginTop: 10, lineHeight: 1.5, padding: '0 8px',
            }}>
              Você bateu a meta hoje. Ativa as notificações pra eu lembrar você
              no horário certo todo dia — assim a chama nunca apaga.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button onClick={() => setPushPromptOpen(false)} style={{
                flex: 1, padding: '14px', borderRadius: 14,
                background: 'var(--paper-3)', color: 'var(--ink-2)', fontSize: 14,
              }}>Agora não</button>
              <button onClick={async () => {
                setPushPromptOpen(false);
                await activatePush();
              }} style={{
                flex: 1, padding: '14px', borderRadius: 14,
                background: 'var(--ember)', color: 'var(--paper)', fontSize: 14, fontWeight: 500,
              }}>Ativar lembretes</button>
            </div>
            <style>{`@keyframes push-prompt-up { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
          </div>
        </div>
      )}

      {/* PIX 1-shot anual — overlay custom. Webhook ativa plano automático. */}
      {pixCheckoutPayload && (
        <PixCheckout
          payload={pixCheckoutPayload}
          session={session}
          onPaid={() => {
            setPixCheckoutPayload(null);
            setUpgradeCelebOpen(true);
            // Refetch profile/limits — plano já foi atualizado pelo webhook.
            loadAllData();
          }}
          onClose={() => setPixCheckoutPayload(null)}
        />
      )}

      {/* Popover de notificações — anchored ao sino do header (qualquer view) */}
      <NotifPopover
        open={notifOpen}
        items={notifItems}
        loading={notifLoading}
        onClose={() => setNotifOpen(false)}
        onItemClick={handleNotifItemClick}
        onOpenProfile={(uid: string) => { setNotifOpen(false); openProfile(uid); }}
        onOpenSettings={() => { setNotifOpen(false); setView({ kind: 'notif' }); }}
      />

      {/* Com a folha de registro aberta, quem convida é ela (ConviteTrilha). */}
      {avisoTrilha && !logOpen && !((view.kind === 'book-study' || view.kind === 'travessia') && (view as any).book?.id === avisoTrilha.bookId) && (
        <AvisoTrilha
          n={avisoTrilha.n}
          onFechar={() => setAvisoTrilha(null)}
          onAbrir={() => {
            const b = books.find(x => x.id === avisoTrilha.bookId);
            setAvisoTrilha(null);
            if (b) abrirTrilha(b);
          }}
        />
      )}

      <Toaster/>
    </div>
    </MentionDataProvider>
    </MentionNavProvider>
  );
}

export default App;

// ─────────────────────────────────────────────────────────────
// CodigoConfirmado — o desfecho do cadastro.
//
// O pedido explícito foi: o destaque NÃO pode ser o login, tem que ser o
// download. Faz sentido — o app nativo é onde o lembrete existe, e lembrete
// é o que sustenta o hábito. Quem fica só no navegador tem chance menor de
// chegar ao fim dos 7 dias.
//
// No Android o destaque inverte: como não há app público (teste fechado),
// "continuar no navegador" É a forma de usar, e fingir o contrário só
// geraria uma frustração no melhor momento do funil.
// ─────────────────────────────────────────────────────────────
/** A espera depois de pagar no Mercado Pago (ver `voltaDoMp`). */
function ConfirmandoPagamento({ estado, onConferirDeNovo, onVerPlanos }: {
  estado: 'conferindo' | 'demorou';
  onConferirDeNovo: () => void;
  onVerPlanos: () => void;
}) {
  const demorou = estado === 'demorou';
  return (
    <div data-volta-mp={estado} style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '40px 28px', textAlign: 'center', background: 'var(--paper)',
    }}>
      {!demorou && (
        <div aria-hidden="true" style={{
          width: 34, height: 34, borderRadius: '50%', marginBottom: 22,
          border: '3px solid var(--line)', borderTopColor: 'var(--ember)',
          animation: 'spin 0.9s linear infinite',
        }} />
      )}
      <style>{'@keyframes spin { to { transform: rotate(360deg) } }'}</style>
      <div className="serif" style={{ fontSize: 26, lineHeight: 1.2, color: 'var(--ink)', maxWidth: 340 }}>
        {demorou ? 'O Mercado Pago ainda não confirmou' : 'Confirmando o seu pagamento'}
      </div>
      <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.55, color: 'var(--ink-3)', maxWidth: 340 }}>
        {demorou
          ? 'Costuma levar menos de um minuto, mas às vezes demora mais. Se você pagou, não pague de novo: confira daqui a pouco. Se precisar, fale com contato@forjamente.com.br.'
          : 'Estamos esperando o aviso do Mercado Pago. Isso leva só alguns segundos — o app abre sozinho.'}
      </p>
      {demorou && (
        <>
          <button onClick={onConferirDeNovo} style={{
            marginTop: 26, padding: '14px 26px', borderRadius: 999, border: 'none',
            background: 'var(--ember)', color: 'var(--paper)', fontSize: 15, fontWeight: 600,
            fontFamily: 'inherit', cursor: 'pointer',
          }}>Conferir de novo</button>
          <button onClick={onVerPlanos} style={{
            marginTop: 12, padding: '10px', border: 'none', background: 'transparent',
            color: 'var(--ink-3)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
          }}>Ver os planos</button>
        </>
      )}
    </div>
  );
}

function CodigoConfirmado({ temSessao, onEntrar }: { temSessao: boolean; onEntrar: () => void }) {
  const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
  const ehAndroid = /android/i.test(ua);
  const ehIOS = /iphone|ipad|ipod/i.test(ua);
  const APPLE = 'https://apps.apple.com/br/app/forjamente/id6767300513';

  const Primario: React.FC<{ children: React.ReactNode; onClick?: () => void; href?: string }> =
    ({ children, onClick, href }) => {
      const st: React.CSSProperties = {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
        width: '100%', padding: '17px 16px', borderRadius: 16,
        background: 'var(--ember)', color: 'var(--paper)',
        fontSize: 16, fontWeight: 700, textDecoration: 'none',
        boxShadow: '0 12px 28px -10px rgba(199,83,26,0.55)',
      };
      return href
        ? <a href={href} style={st}>{children}</a>
        : <button onClick={onClick} style={st}>{children}</button>;
    };

  const Secundario: React.FC<{ children: React.ReactNode; onClick?: () => void; href?: string }> =
    ({ children, onClick, href }) => {
      const st: React.CSSProperties = {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100%', padding: '14px 16px', borderRadius: 999, marginTop: 11,
        background: 'transparent', border: '1px solid var(--line-strong)',
        color: 'var(--ink-2)', fontSize: 14.5, fontWeight: 600, textDecoration: 'none',
      };
      return href
        ? <a href={href} style={st}>{children}</a>
        : <button onClick={onClick} style={st}>{children}</button>;
    };

  return (
    <div style={{ animation: 'e2FadeUp .35s ease both' }}>
      <div style={{
        width: 66, height: 66, borderRadius: '50%', background: 'var(--ember-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
      }}>
        <Icon name="check" size={30} color="var(--ember)" stroke={2.6}/>
      </div>

      <div className="serif" style={{ fontSize: 32, lineHeight: 1.05, textAlign: 'center' }}>
        E-mail <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>confirmado</span>.
      </div>
      <div style={{ fontSize: 15, color: 'var(--ink-2)', textAlign: 'center', marginTop: 13, lineHeight: 1.55 }}>
        {ehAndroid
          ? 'Sua conta está aberta, com o app inteiro liberado.'
          : 'Baixe o app para receber seus lembretes — é o que sustenta o hábito.'}
      </div>

      <div style={{ marginTop: 26 }}>
        {ehAndroid ? (
          <>
            <Primario onClick={onEntrar}>
              {temSessao ? 'Começar agora' : 'Entrar na minha conta'}
            </Primario>
            <div style={{
              marginTop: 14, padding: '12px 14px', borderRadius: 12,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5, textAlign: 'center',
            }}>
              O app de Android está em testes. Por enquanto você usa pelo
              navegador — funciona igual.
            </div>
          </>
        ) : (
          <>
            <Primario href={APPLE}>
              <svg width="17" height="19" viewBox="0 0 24 28" fill="currentColor" aria-hidden="true">
                <path d="M17 14.6c0-3 2.4-4.3 2.5-4.4-1.4-2-3.5-2.3-4.2-2.3-1.8-.2-3.5 1-4.4 1s-2.3-1-3.8-1C5 8 3 9.6 1.9 11.8c-1.9 3.3-.5 8.2 1.4 10.9.9 1.3 2 2.8 3.4 2.7 1.4-.1 1.9-.9 3.5-.9s2.1.9 3.5.9c1.5 0 2.4-1.3 3.3-2.6.7-1 1.3-2.1 1.6-3.2-.1 0-3.1-1.2-3.1-4.9z"/>
                <path d="M14.5 6.1c.8-1 1.3-2.3 1.2-3.6-1.1.1-2.5.8-3.3 1.8-.7.8-1.4 2.1-1.2 3.3 1.2.1 2.5-.6 3.3-1.5z"/>
              </svg>
              Baixar na App Store
            </Primario>
            <Secundario onClick={onEntrar}>
              {temSessao ? 'Continuar no navegador' : 'Entrar pelo navegador'}
            </Secundario>
            {!ehIOS && (
              <div style={{
                fontSize: 12, color: 'var(--ink-4)', textAlign: 'center',
                marginTop: 13, lineHeight: 1.5,
              }}>Android em breve — por enquanto, pelo navegador.</div>
            )}
          </>
        )}
      </div>

      <div style={{
        fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center',
        marginTop: 22, lineHeight: 1.55,
      }}>Use o mesmo e-mail e senha que você acabou de criar.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// VerifyEmailScreen — bloqueia acesso até confirmar o email.
// Aparece pós-signup ou se login retorna 403 com needsVerification.
// ─────────────────────────────────────────────────────────────
// VerifyEmailScreen — bloqueia acesso ate confirmar o email (UX melhorada).
function VerifyEmailScreen({ email, senha, onSessao, onResend, onBack, onUseAnotherEmail, hasInvite }: {
  email: string;
  senha?: string;
  onSessao?: (sessao: any) => void;
  onResend: () => Promise<void>;
  onBack: () => void;
  onUseAnotherEmail?: () => void;
  hasInvite?: boolean;
}) {
  const [codigo, setCodigo] = useState('');
  const [conferindo, setConferindo] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  const [sessaoPronta, setSessaoPronta] = useState<any>(null);

  const confirmarCodigo = async (valor?: string) => {
    const c = String(valor ?? codigo).replace(/\D/g, '');
    if (c.length !== 6 || conferindo) return;
    setConferindo(true); setErrMsg(null);
    try {
      const r = await fetch(`${API_BASE}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: c, password: senha || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErrMsg(d?.error || 'Código inválido.');
        setCodigo('');
        return;
      }
      // Indicação, segunda tentativa. O cadastro já vinculou pelo `refCode`,
      // mas esta tela é o último ponto em que ainda existe navegador COM o
      // código e sessão ao mesmo tempo — depois dela a pessoa vai pro app.
      // Vale para conta criada antes desta correção e pra código que só
      // apareceu depois. O backend é idempotente: repetir não custa nada.
      try {
        const pend = localStorage.getItem('forjamente.pendingReferralCode');
        if (pend && d?.session) {
          await api.attachReferral(d.session, pend).catch(() => {});
          localStorage.removeItem('forjamente.pendingReferralCode');
        }
      } catch {}

      setSessaoPronta(d?.session || null);
      setConfirmado(true);
    } catch (e: any) {
      setErrMsg(e?.message || 'Não consegui confirmar. Tente de novo.');
    } finally {
      setConferindo(false);
    }
  };
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (busy || cooldown > 0) return;
    setBusy(true); setErrMsg(null);
    try {
      await onResend();
      toast.success('Reenviamos. Se não chegar em 1 minuto, olhe no spam.');
      setCooldown(45);
    } catch (e: any) {
      setErrMsg(e?.message || 'Erro ao reenviar.');
    } finally {
      setBusy(false);
    }
  };

  const Step = ({ n, children }: { n: number; children: React.ReactNode }) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--ember-soft)', color: 'var(--ember)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{n}</div>
      <div style={{ flex: 1, fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.45 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--ember-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--ember)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
        </div>
        {/* ── B) DESFECHO: confirmado ─────────────────────────────── */}
        {confirmado ? (
          <CodigoConfirmado
            temSessao={!!sessaoPronta}
            onEntrar={() => { if (onSessao) onSessao(sessaoPronta); }}
          />
        ) : (
        <>
        <div className="serif" style={{ fontSize: 32, lineHeight: 1.05, textAlign: 'center' }}>
          Digite o <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>código</span>.
        </div>
        <div style={{ fontSize: 14.5, color: 'var(--ink-2)', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
          Enviamos 6 dígitos para<br/><strong style={{ color: 'var(--ink)' }}>{email}</strong>
        </div>

        {/* inputMode numérico: no celular abre o teclado de números direto.
            O paste de 6 dígitos também funciona — muita gente copia do e-mail. */}
        <input
          value={codigo}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCodigo(v);
            setErrMsg(null);
            if (v.length === 6) confirmarCodigo(v);   // confirma sozinho ao completar
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          autoFocus
          style={{
            width: '100%', marginTop: 24, padding: '18px 16px', borderRadius: 16,
            background: 'var(--paper-2)', border: '1px solid var(--line-strong)',
            color: 'var(--ink)', textAlign: 'center',
            fontFamily: 'Geist Mono, ui-monospace, monospace',
            fontSize: 32, letterSpacing: '0.28em', outline: 'none',
          }}
        />

        <button
          onClick={() => confirmarCodigo()}
          disabled={codigo.length !== 6 || conferindo}
          style={{
            width: '100%', marginTop: 14, padding: '15px 16px',
            background: 'var(--ember)', color: 'var(--paper)',
            borderRadius: 14, fontSize: 15, fontWeight: 700,
            opacity: codigo.length === 6 && !conferindo ? 1 : 0.45,
            boxShadow: '0 10px 24px -8px rgba(199,83,26,0.5)',
          }}>{conferindo ? 'Conferindo…' : 'Confirmar'}</button>

        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
          Não chegou? Olhe no <strong style={{ color: 'var(--ink-2)' }}>spam</strong> ou nas promoções.
          O código vale por 30 minutos.
        </div>
        </>
        )}

        {errMsg && (
          <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 10, background: 'rgba(192,58,42,0.08)', color: '#C03A2A', fontSize: 13, lineHeight: 1.4, textAlign: 'center' }}>{errMsg}</div>
        )}

        <button onClick={onBack} style={{
          width: '100%', marginTop: 22, padding: '15px 16px',
          background: 'var(--ember)', color: 'var(--paper)',
          borderRadius: 14, fontSize: 15, fontWeight: 600,
          boxShadow: '0 10px 24px -8px rgba(199,83,26,0.5)',
        }}>Já confirmei — fazer login</button>

        <button onClick={handleResend} disabled={busy || cooldown > 0} style={{
          width: '100%', marginTop: 10, padding: '13px 16px',
          background: 'transparent', color: cooldown > 0 ? 'var(--ink-3)' : 'var(--ink-2)', border: '1px solid var(--line)',
          borderRadius: 14, fontSize: 14, fontWeight: 500,
          opacity: busy ? 0.6 : 1, cursor: (busy || cooldown > 0) ? 'default' : 'pointer',
        }}>{busy ? 'Reenviando…' : cooldown > 0 ? `Reenviar email (${cooldown}s)` : 'Não recebeu? Reenviar email'}</button>

        <button onClick={onUseAnotherEmail || onBack} style={{
          width: '100%', marginTop: 10, padding: '10px',
          background: 'transparent', color: 'var(--ink-3)', border: 'none',
          fontSize: 13,
        }}>Usar outro email</button>
      </div>
    </div>
  );
}
